import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM, { flushSync } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import {
  useDocumentTypeTranslator,
  resolvePaymentDocumentDisplayType,
  resolveCreditNoteDisplayLabel,
  resolveCreditNoteDisplayColor,
  resolveArchiveDocumentTypeLabel,
  isSupplierCreditNoteDocument,
  isClientCreditNoteDocument,
  CREDIT_NOTE_LABEL_SUPPLIER,
  CREDIT_NOTE_LABEL_CLIENT,
  formatPaymentVoucherReason,
  resolvePartnerReferenceCode,
  buildPaymentVoucherPatchFromPurchaseOrder,
  buildPaymentReceiptPatchFromInvoice,
  buildPaymentReceiptPatchFromClientCreditNote,
  summarizePaymentDocumentTitleCore,
} from '../../shared/utils/documentTypeUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Filter as FilterIcon, 
  Download as ArrowDownTrayIcon, 
  Upload,
  Eye as EyeIcon, 
  Edit as EditIcon, 
  Trash2 as TrashIcon,
  Clock as ClockIcon,
  CheckCircle as CheckCircleIcon,
  AlertTriangle as ExclamationTriangleIcon,
  BanknoteIcon,
  Calendar as CalendarIcon,
  FolderOpen as FolderOpenIcon,
  User as UserIcon,
  X,
  FileOutput,
  ClipboardList,
  FileSpreadsheet,
  Truck,
  Send,
  CheckSquare,
  Printer,
  Copy,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  RefreshCw,
  RefreshCcw,
  Package,
  PackagePlus,
  CreditCard,
  Receipt,
  Undo2,
  FileSearch,
  ShoppingCart,
  ArrowLeft,
  ArrowRight,
  Banknote,
  DollarSign,
  FileCheck,
  FileX,
  PlusCircle,
  ArrowRightLeft,
  Download,
  FilePlus,
  Settings,
  Eye,
  Save,
} from 'lucide-react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandVatRate, getBrandVatMultiplier } from '../../shared/utils/brandCurrency';
import { downloadDocumentPDF, printMergedInvoicePagesHTML } from '../../shared/utils/pdfGenerator';
import { triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { useDocuments } from '../../core/contexts/DocumentsContext';
import { useSettings } from '../../core/contexts/SettingsContext';
import { printListPdfWithInvoiceTemplate } from './utils/templateListPdfExport';
import { useClients } from '../../core/contexts/ClientsContext';
import { useAppData } from '../../core/contexts/AppDataContext';
import TemplateWrapper from './invoice/templates/TemplateWrapper';
import ArchivesToolbarFiltersRow from './ArchivesToolbarFiltersRow';
import {
  reorderStatusesLikeDocumentCreation,
  mapArchiveDbStatusToUiStatus,
} from './config/facturationArchiveStatusOrder';
import { resetDocuments } from '../../shared/utils/resetDocuments';
import { compareDocumentsNewestFirst } from '../../shared/utils/archiveDocumentOrdering';
import {
  NOTIFICATION_TOAST_ACTION_BUTTON_CLASS,
  NOTIFICATION_TOAST_ACTIONS_FOOTER_CLASS,
} from '../../shared/components/AppNotificationToast';
import {
  generateDocumentTitle,
  resolveArchiveDocumentTitle,
  ARCHIVE_TITLE_PREVIEW_MAX_LEN,
  applySupplementaryDefaultsForArchiveConversion,
  applyArchiveConversionDatesAndSignatures,
  applyConversionTaxProfile,
  resolveShowTvaFromArchive,
  pickArchiveNum,
  isInvoicePaymentConditionSlug,
  mapQuotePaymentTermsToInvoicePaymentConditions,
  resolveInvoicePaymentConditionsFromArchive,
} from './invoice/DocumentCreation';
import { normalizeReceptionLineItem } from '../../shared/utils/documentTypeUtils';
// Tables-mobile.css - DELETED
import { resolveFacturationPageSize } from './utils/facturationTablePageSize';
import { useFacturationConfig } from './useFacturationConfig';
import { buildFacturationConversionPrefixMap } from './facturationConfigDefaults';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
} from '../../products/saudi/components/QawaemTableSelection';
import QawaemPageTitleWithFilters from '../../products/saudi/components/QawaemPageTitleWithFilters';
import QawaemTableCardsViewToggle from '../../products/saudi/components/QawaemTableCardsViewToggle';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import useFacturationModalBodyLock from './utils/useFacturationModalBodyLock';
import {
  ARCHIVE_MODULE_FACTURATION,
  ARCHIVE_MODULE_INVENTAIRE,
  ARCHIVE_MODULE_LOGISTIQUE,
  documentBelongsToArchiveModule,
  filterConversionsForModule,
  getArchiveModuleForDocType,
  getArchivesRouteForModule,
  applyArchiveModuleToDocumentPayload,
  isInventoryLogisticsDocType,
  normalizeInventoryDocTypeForUi,
  resolveConversionMatrixKey,
} from './utils/archiveModuleScope';
import {
  filterDocumentsForSection,
  buildSectionCreateDocumentUrl,
} from './config/facturationSectionArchivesConfig';

const archivesFacturationI18n = (key, opts) =>
  i18n.t(`modules.facturation.pages.archivesFacturation.${key}`, opts);

const paymentLinePurchaseOrderDesc = (ref) =>
  ref
    ? archivesFacturationI18n('payment_line_purchase_order_ref', { ref })
    : archivesFacturationI18n('payment_line_purchase_order');

const paymentLineCreditNoteSupplierDesc = (ref) =>
  ref
    ? archivesFacturationI18n('payment_line_credit_note_supplier_ref', { ref })
    : archivesFacturationI18n('payment_line_credit_note_supplier');

const paymentLineCreditNoteClientDesc = (ref) =>
  ref
    ? archivesFacturationI18n('payment_line_credit_note_client_ref', { ref })
    : archivesFacturationI18n('payment_line_credit_note_client');

/** Clé canonique pour mapper type base / UI ↔ type PDF TemplateWrapper */
function canonicalArchiveTypeKey(type) {
  return String(type ?? '')
    .trim()
    .replace(/-/g, '_')
    .toLowerCase();
}

function resolveBulkSupplierPartnerKey(doc) {
  if (!doc) return '';
  const id = String(
    doc.supplierId || doc.supplier_id || doc.clientId || doc.client_id || '',
  ).trim();
  if (id) return `id:${id.toLowerCase()}`;
  const num = String(doc.partnerNumber || doc.partner_number || '').trim().toLowerCase();
  if (num) return `num:${num}`;
  const name = String(
    doc.supplierName || doc.supplier_name || doc.client || '',
  )
    .trim()
    .toLowerCase();
  if (name) return `name:${name}`;
  return '';
}

function isBulkBvMergeSourceDoc(doc) {
  const key = canonicalArchiveTypeKey(doc?.type);
  if (key === 'purchase_order') return true;
  if (key === 'credit_note' && isSupplierCreditNoteDocument(doc)) return true;
  return false;
}

function isBulkRpMergeSourceDoc(doc) {
  const key = canonicalArchiveTypeKey(doc?.type);
  if (key === 'invoice') return true;
  if (key === 'credit_note' && isClientCreditNoteDocument(doc)) return true;
  return false;
}

function resolveBulkClientPartnerKey(doc) {
  if (!doc) return '';
  const id = String(doc.clientId || doc.client_id || '').trim();
  if (id) return `id:${id.toLowerCase()}`;
  const num = String(doc.partnerNumber || doc.partner_number || '').trim().toLowerCase();
  if (num) return `num:${num}`;
  const name = String(doc.clientName || doc.client_name || doc.client || '')
    .trim()
    .toLowerCase();
  if (name) return `name:${name}`;
  return '';
}

function getCreditNoteConversions(sourceDoc, labelForType) {
  const label = (target) =>
    typeof labelForType === 'function' ? labelForType(target) : target;
  if (isSupplierCreditNoteDocument(sourceDoc)) {
    return [
      { target: 'payment_voucher', label: label('payment_voucher'), icon: CreditCard, color: 'text-green-400' },
    ];
  }
  return [
    { target: 'payment_receipt', label: label('payment_receipt'), icon: Receipt, color: 'text-indigo-400' },
  ];
}

function conversionTargetDiffersFromDocType(target, docType) {
  const t = canonicalArchiveTypeKey(target);
  const d = canonicalArchiveTypeKey(docType);
  return t !== d;
}

function getBulkCommonConversions(documents, getAvailableConversions) {
  if (!Array.isArray(documents) || documents.length === 0) return [];

  const targetSets = documents.map((doc) => {
    const avail = getAvailableConversions(doc?.type, doc) || [];
    return new Set(
      avail
        .filter((c) => conversionTargetDiffersFromDocType(c.target, doc?.type))
        .map((c) => canonicalArchiveTypeKey(c.target)),
    );
  });

  const commonTargets = [...targetSets[0]].filter((t) =>
    targetSets.every((set) => set.has(t)),
  );

  const firstConv = getAvailableConversions(documents[0]?.type, documents[0]) || [];
  const seen = new Set();
  return firstConv.filter((c) => {
    const key = canonicalArchiveTypeKey(c.target);
    if (!commonTargets.includes(key)) return false;
    if (seen.has(key)) return false;
    seen.add(key);
    return documents.every((doc) => conversionTargetDiffersFromDocType(c.target, doc?.type));
  });
}

function evaluateBulkConversionSelection(documents, getAvailableConversions) {
  if (!Array.isArray(documents) || documents.length < 2) {
    return {
      ok: false,
      reasonKey: 'bulk_select_min_two',
      documents: documents || [],
      commonConversions: [],
      mergeEligible: false,
    };
  }

  const resolved = documents.filter(Boolean);
  if (resolved.length < 2) {
    return {
      ok: false,
      reasonKey: 'bulk_select_min_two',
      documents: resolved,
      commonConversions: [],
      mergeEligible: false,
    };
  }

  const typeKeys = resolved.map((d) => canonicalArchiveTypeKey(d.type));
  const allSameType = typeKeys.every((k) => k === typeKeys[0]);
  const allMergeSources = resolved.every(isBulkBvMergeSourceDoc);
  const allRpMergeSources = resolved.every(isBulkRpMergeSourceDoc);

  if (!allSameType && !allMergeSources && !allRpMergeSources) {
    return {
      ok: false,
      reasonKey: 'bulk_select_same_type',
      documents: resolved,
      commonConversions: [],
      mergeEligible: false,
    };
  }

  const commonConversions = getBulkCommonConversions(resolved, getAvailableConversions);
  if (commonConversions.length === 0) {
    return {
      ok: false,
      reasonKey: 'bulk_no_common_conversion',
      documents: resolved,
      commonConversions: [],
      mergeEligible: false,
    };
  }

  const partnerKeys = resolved.map(resolveBulkSupplierPartnerKey);
  const clientPartnerKeys = resolved.map(resolveBulkClientPartnerKey);
  const mergeEligible =
    resolved.length >= 2 &&
    allMergeSources &&
    partnerKeys.every((k) => k && k === partnerKeys[0]);
  const mergeRpEligible =
    resolved.length >= 2 &&
    allRpMergeSources &&
    clientPartnerKeys.every((k) => k && k === clientPartnerKeys[0]);

  return {
    ok: true,
    reasonKey: '',
    documents: resolved,
    commonConversions,
    mergeEligible,
    mergeRpEligible,
  };
}

function normalizeTypeForDb(type) {
  const typeMap = {
    'quote-request': 'quote_request',
    'purchase-order': 'purchase_order',
    'delivery-note': 'delivery',
    delivery_note: 'delivery',
    'shipping-slip': 'shipping',
    shipping_slip: 'shipping',
    'reception-note': 'reception_note',
    'receipt-note': 'reception_note',
    receipt_note: 'reception_note',
    'return-slip': 'return_slip',
    'client-return-slip': 'client-return-slip',
    'material-exit': 'material_exit',
    'credit-note': 'credit_note',
    'payment-slip': 'payment_voucher',
    payment_slip: 'payment_voucher',
    'payment-voucher': 'payment_voucher',
    'payment-receipt': 'payment_receipt',
    payment_receipt: 'payment_receipt',
    'exit-note': 'exit_note',
    'exit-slip': 'exit_slip',
    exit_slip: 'exit_slip',
    'outbound-slip': 'exit_slip',
    outbound_slip: 'exit_slip',
    'transfer-note': 'transfer_note',
    'transfer-slip': 'transfer_slip',
    transfer_slip: 'transfer_slip',
    'stock-entry': 'stock_entry',
    'proforma-request': 'proforma_request',
    client_return_slip: 'client_return_slip',
    'client-return': 'client-return-slip',
  };
  return typeMap[type] || type;
}

const ARCHIVE_DB_TYPE_TO_PDF_TEMPLATE_TYPE = {
  invoice: 'invoice',
  proforma: 'proforma',
  quote: 'quote',
  quote_request: 'quote-request',
  proforma_request: 'proforma-request',
  purchase_order: 'purchase-order',
  delivery: 'delivery-note',
  delivery_note: 'delivery-note',
  material_exit: 'material-exit',
  shipping: 'shipping-slip',
  shipping_slip: 'shipping-slip',
  return_slip: 'return-slip',
  reception_note: 'reception-note',
  receipt_note: 'reception-note',
  credit_note: 'credit-note',
  payment_voucher: 'payment-voucher',
  payment_slip: 'payment-voucher',
  payment_receipt: 'payment-receipt',
  client_return_slip: 'client-return-slip',
  exit_slip: 'exit-note',
  exit_note: 'exit-note',
  outbound_slip: 'exit-note',
  transfer_slip: 'transfer-note',
  transfer_note: 'transfer-note',
  stock_entry: 'stock-entry',
};

/**
 * type stocké en archives (souvent underscore) → id document pour PDF / TemplateWrapper.
 */
function convertArchiveTypeForPdf(storedType) {
  if (storedType == null || storedType === '') return '';
  const key = canonicalArchiveTypeKey(storedType);
  if (ARCHIVE_DB_TYPE_TO_PDF_TEMPLATE_TYPE[key]) {
    return ARCHIVE_DB_TYPE_TO_PDF_TEMPLATE_TYPE[key];
  }
  const t = String(storedType).trim();
  if (!t) return '';
  return t.replace(/_/g, '-');
}

/** Demande de devis / proforma : budget = saisie manuelle maxBudget uniquement (declared_value en base). */
function applyArchiveQuoteBudgetFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  const typeKey = canonicalArchiveTypeKey(document.type);
  if (typeKey !== 'quote_request' && typeKey !== 'proforma_request') return;

  const raw =
    docToDisplay.maxBudget ??
    document.max_budget ??
    document.maxBudget ??
    document.declared_value ??
    document.declaredValue;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) {
    docToDisplay.maxBudget = n;
    docToDisplay.approximateBudget = n;
    docToDisplay.declaredValue = n;
    return;
  }

  docToDisplay.maxBudget = '';
  docToDisplay.approximateBudget = '';
  docToDisplay.declaredValue = '';
}

function parseArchiveMoneyValue(val) {
  if (val === undefined || val === null || val === '') return undefined;
  if (typeof val === 'number') return Number.isFinite(val) ? val : undefined;
  const sanitized = String(val).replace(/[^0-9,.-]/g, '').replace(',', '.');
  const n = Number(sanitized);
  return Number.isFinite(n) ? n : undefined;
}

function isQuoteRequestArchiveType(type) {
  const key = canonicalArchiveTypeKey(type);
  return key === 'quote_request' || key === 'proforma_request';
}

/** Budget maximum estimé saisi manuellement (DD / DPR) — pas les prix catalogue des lignes. */
function resolveQuoteRequestEstimatedBudget(doc) {
  if (!doc || typeof doc !== 'object') return undefined;
  const raw =
    doc.maxBudget ??
    doc.declaredValue ??
    doc.declared_value ??
    doc.approximateBudget ??
    doc.approximate_budget;
  const n = parseArchiveMoneyValue(raw);
  return n != null && n > 0 ? n : undefined;
}

/** Montant affiché / agrégé en Archives. DD/DPR → budget estimé uniquement. */
function resolveArchiveDocumentAmount(doc) {
  if (!doc || typeof doc !== 'object') return 0;
  if (isQuoteRequestArchiveType(doc.type)) {
    return resolveQuoteRequestEstimatedBudget(doc) ?? 0;
  }

  let amount = parseArchiveMoneyValue(doc.amount);
  if (amount === undefined) amount = parseArchiveMoneyValue(doc.totalTTC);
  if (amount === undefined) amount = parseArchiveMoneyValue(doc.total);
  if (amount === undefined) amount = parseArchiveMoneyValue(doc.subtotalHT);

  if (amount === undefined && Array.isArray(doc.items) && doc.items.length > 0) {
    amount = doc.items.reduce((sum, item) => {
      const qty = Number(item?.quantity) || 0;
      const unitPrice =
        parseArchiveMoneyValue(item?.price) ??
        parseArchiveMoneyValue(item?.unitPrice) ??
        parseArchiveMoneyValue(item?.value) ??
        0;
      return sum + qty * unitPrice;
    }, 0);
  }

  return amount ?? 0;
}

function formatArchiveListAmount(doc, formatBrandMoney) {
  if (isQuoteRequestArchiveType(doc?.type)) {
    const budget = resolveQuoteRequestEstimatedBudget(doc);
    return budget != null
      ? formatBrandMoney(budget)
      : i18n.t('modules.facturation.pages.archivesFacturation.not_budgeted');
  }
  return formatBrandMoney(resolveArchiveDocumentAmount(doc));
}

function isArchiveClientLabelPlaceholder(value) {
  const s = String(value ?? '').trim();
  return !s || s === '---' || s === '\u2014';
}

function looksLikeArchivePartnerId(value) {
  const s = String(value ?? '').trim();
  if (!s) return false;
  if (/^[0-9a-f-]{36}$/i.test(s)) return true;
  return /^MOCK-(CLI|CLIENT|SUPPLIER)/i.test(s);
}

/** Resolve partner display name for archives list rows (DB may store id or placeholder in `client`). */
function resolveArchiveListClientLabel(document, findClientData) {
  const raw = String(
    document.clientName ||
      document.client_name ||
      document.client ||
      document.supplierName ||
      document.supplier_name ||
      '',
  ).trim();
  if (!isArchiveClientLabelPlaceholder(raw) && !looksLikeArchivePartnerId(raw)) return raw;

  const partner = typeof findClientData === 'function' ? findClientData(document) : null;
  if (!partner) return '\u2014';

  const resolved = String(
    partner.name || partner.company_name || partner.companyName || '',
  ).trim();
  return resolved || '\u2014';
}

function pickFirstIsoDate(source, keys) {
  if (!source || typeof source !== 'object') return '';
  for (const k of keys) {
    const v = source[k];
    if (v != null && String(v).trim()) return String(v).trim().slice(0, 10);
  }
  return '';
}

/**
 * Après conversion : aligner le champ lu par le PDF (objectif / complémentaires) sur le nouveau type.
 */
/** DD/DPR → BC : le budget max appartient à la demande, pas au bon de commande. */
function stripRequestBudgetFromConvertedPurchaseOrder(newDoc, sourceDoc, normalizedTargetType) {
  if (!newDoc || normalizedTargetType !== 'purchase_order') return;
  const srcKey = canonicalArchiveTypeKey(sourceDoc?.type);
  if (srcKey !== 'quote_request' && srcKey !== 'proforma_request') return;

  const budgetKeys = [
    'maxBudget',
    'max_budget',
    'estimatedBudget',
    'estimated_budget',
    'approximateBudget',
    'approximate_budget',
    'declaredValue',
    'declared_value',
    'orderBudget',
    'order_budget',
  ];
  budgetKeys.forEach((k) => {
    newDoc[k] = null;
  });
}

/** BC → Bon de réception : refs, lignes, fournisseur, pas de bloc fiscal. */
function applyPurchaseOrderToReceptionConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  const srcKey = canonicalArchiveTypeKey(sourceDoc.type);
  if (srcKey !== 'purchase_order') return;

  const bcRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
  if (bcRef) {
    newDoc.purchaseOrderReference = bcRef;
    newDoc.purchase_order_reference = bcRef;
    newDoc.receptionOrder = bcRef;
    newDoc.reception_order = bcRef;
    newDoc.receptionOrderRef = bcRef;
  }

  const supplier =
    sourceDoc.supplier ||
    sourceDoc.supplierName ||
    sourceDoc.supplier_name ||
    sourceDoc.client ||
    '';
  if (supplier && String(supplier).trim()) {
    const name =
      sourceDoc.supplierName ||
      sourceDoc.supplier_name ||
      String(supplier).trim();
    newDoc.supplier = String(supplier).trim();
    newDoc.supplierName = name;
    if (!newDoc.client) newDoc.client = String(supplier).trim();
  }

  const deliveryLoc =
    sourceDoc.deliveryLocation ||
    sourceDoc.delivery_location ||
    sourceDoc.deliveryAddress ||
    sourceDoc.delivery_address ||
    sourceDoc.deliveryPlace ||
    sourceDoc.delivery_place ||
    sourceDoc.lieu_livraison ||
    sourceDoc.lieuLivraison ||
    sourceDoc.executionLocation ||
    sourceDoc.execution_location ||
    sourceDoc.receiverAddress ||
    sourceDoc.receiver_address ||
    sourceDoc.receptionAddress ||
    sourceDoc.reception_address ||
    '';
  if (deliveryLoc && String(deliveryLoc).trim()) {
    const addr = String(deliveryLoc).trim();
    newDoc.receiverAddress = addr;
    newDoc.receiver_address = addr;
    newDoc.receptionAddress = addr;
    newDoc.reception_address = addr;
  }

  if (Array.isArray(sourceDoc.items) && sourceDoc.items.length > 0) {
    newDoc.items = sourceDoc.items.map((item) => normalizeReceptionLineItem(item));
  }

  const plannedReception =
    pickFirstIsoDate(sourceDoc, [
      'deliveryDate',
      'delivery_date',
      'orderDeliveryDate',
      'order_delivery_date',
      'expectedDeliveryDate',
      'expected_delivery_date',
    ]) || '';
  if (plannedReception) {
    newDoc.expectedReceptionDate = plannedReception;
    newDoc.expected_reception_date = plannedReception;
    newDoc.expectedDeliveryDate = plannedReception;
    newDoc.expected_delivery_date = plannedReception;
  }
}

/** BC/DD → BV : retirer les champs achat/devis qui ne concernent pas le bon de versement. */
function stripIrrelevantFieldsFromConvertedPaymentVoucher(newDoc) {
  if (!newDoc) return;
  const clearKeys = [
    'documentReference',
    'document_reference',
    'quoteRequestRef',
    'quote_request_ref',
    'proformaRequestRef',
    'proforma_request_ref',
    'projectReference',
    'project_reference',
    'projectType',
    'project_type',
    'requiredDocuments',
    'required_documents',
    'desiredDeliveryTime',
    'desired_delivery_time',
    'estimatedDeliveryTime',
    'estimated_delivery_time',
    'deliveryTimeframe',
    'deliveryAddress',
    'deliveryLocation',
    'deliveryPlace',
    'deliveryMode',
    'delivery_mode',
    'buyerContact',
    'buyer_contact',
    'terms',
    'conditions',
    'specifications',
  ];
  clearKeys.forEach((k) => {
    newDoc[k] = '';
  });
  if (!String(newDoc.supplierCreditNoteRef || '').trim()) {
    newDoc.originalDocumentReference = '';
    newDoc.original_document_reference = '';
  }

  newDoc.creditNoteApplicationDate = '';
  newDoc.credit_note_application_date = '';
  newDoc.orderDate = '';
  newDoc.order_date = '';
  if (newDoc.creditNote && typeof newDoc.creditNote === 'object') {
    newDoc.creditNote = { ...newDoc.creditNote, applicationDate: '' };
  }
}

/** Reprend fournisseur + N° partenaire pour un BV converti. */
function applyPaymentVoucherPartnerFromSource(newDoc, sourceDoc, suppliersList) {
  newDoc.isSupplier = true;
  newDoc.is_supplier = true;

  const supplierId = String(
    sourceDoc.supplierId ||
      sourceDoc.supplier_id ||
      sourceDoc.clientId ||
      sourceDoc.client_id ||
      '',
  ).trim();

  const supplierName = String(
    sourceDoc.supplierName ||
      sourceDoc.supplier_name ||
      sourceDoc.client ||
      '',
  ).trim();

  if (supplierId) {
    newDoc.clientId = supplierId;
    newDoc.supplierId = supplierId;
    newDoc.client_id = supplierId;
    newDoc.supplier_id = supplierId;
  }

  if (supplierName) {
    newDoc.supplierName = supplierName;
    newDoc.supplier_name = supplierName;
    newDoc.client = supplierName;
  }

  const existingPartnerNum = String(
    sourceDoc.partnerNumber || sourceDoc.partner_number || '',
  ).trim();
  if (existingPartnerNum) {
    newDoc.partnerNumber = existingPartnerNum;
    newDoc.partner_number = existingPartnerNum;
    return;
  }

  if (supplierId && Array.isArray(suppliersList)) {
    const partner = suppliersList.find((s) => String(s.id) === String(supplierId));
    if (partner) {
      const num = resolvePartnerReferenceCode(partner, { isSupplier: true });
      if (num) {
        newDoc.partnerNumber = num;
        newDoc.partner_number = num;
      }
    }
  }
}

/** BC → Bon de versement : fournisseur, ref BC, montant, lignes de paiement. */
function applyPurchaseOrderToPaymentVoucherConversion(newDoc, sourceDoc, suppliersList) {
  if (!newDoc || !sourceDoc) return;
  if (canonicalArchiveTypeKey(sourceDoc.type) !== 'purchase_order') return;

  const patch = buildPaymentVoucherPatchFromPurchaseOrder(sourceDoc);
  const bcRef =
    patch.purchaseOrderRef || (sourceDoc.number ? String(sourceDoc.number).trim() : '');
  const lines =
    Array.isArray(patch.paymentLines) && patch.paymentLines.length
      ? patch.paymentLines
      : [];
  const total = Number(lines[0]?.amount) || 0;

  if (bcRef) {
    newDoc.orderReference = bcRef;
    newDoc.order_reference = bcRef;
    newDoc.purchaseOrderReference = bcRef;
    newDoc.purchaseOrderRef = bcRef;
    newDoc.purchase_order_reference = bcRef;
  }

  applyPaymentVoucherPartnerFromSource(newDoc, sourceDoc, suppliersList);

  if (total > 0) {
    newDoc.amount = total;
    newDoc.paymentAmount = total;
    newDoc.payment_amount = total;
  }

  newDoc.paymentLines =
    lines.length > 0
      ? lines
      : [{
          lineType: 'purchase_order',
          reference: bcRef,
          description: paymentLinePurchaseOrderDesc(bcRef),
          amount: total || 0,
          relatedInvoice: '',
        }];
  newDoc.payments = newDoc.paymentLines;
  newDoc.items = [];
}

/** Avoir sur achat → Bon de versement : refs, fournisseur, montant, lignes. */
function applyCreditNoteToPaymentVoucherConversion(newDoc, sourceDoc, suppliersList) {
  if (!newDoc || !sourceDoc) return;
  const srcKey = canonicalArchiveTypeKey(sourceDoc.type);
  if (srcKey !== 'credit_note') return;
  if (!isSupplierCreditNoteDocument(sourceDoc)) return;

  const cnRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
  if (cnRef) {
    newDoc.supplierCreditNoteRef = cnRef;
    newDoc.originalDocumentReference = cnRef;
    newDoc.original_document_reference = cnRef;
  }

  applyPaymentVoucherPartnerFromSource(newDoc, sourceDoc, suppliersList);

  const invRef = String(
    sourceDoc.originalInvoiceNumber ||
      sourceDoc.original_invoice_number ||
      (sourceDoc.originalInvoice && sourceDoc.originalInvoice.number) ||
      '',
  ).trim();
  if (invRef) {
    newDoc.relatedInvoices = invRef;
    newDoc.invoicesPaid = invRef;
  }

  const bcRef = String(
    sourceDoc.returnOriginalPurchaseOrder ||
      sourceDoc.return_original_purchase_order ||
      sourceDoc.purchaseOrderReference ||
      sourceDoc.purchase_order_reference ||
      '',
  ).trim();
  if (bcRef) {
    newDoc.purchaseOrderRef = bcRef;
    newDoc.purchaseOrderReference = bcRef;
    newDoc.purchase_order_reference = bcRef;
  }

  let total =
    Number(
      sourceDoc.creditNoteAmount ||
        sourceDoc.credit_note_amount ||
        sourceDoc.paymentAmount ||
        sourceDoc.amount,
    ) || 0;
  if (!total && Array.isArray(sourceDoc.items) && sourceDoc.items.length > 0) {
    total = sourceDoc.items.reduce(
      (sum, item) =>
        sum +
        (Number(item.total) ||
          Number(item.quantity) * Number(item.price || item.unitPrice) ||
          0),
      0,
    );
  }
  if (total > 0) {
    newDoc.amount = total;
    newDoc.paymentAmount = total;
    newDoc.payment_amount = total;
  }

  const desc = paymentLineCreditNoteSupplierDesc(cnRef);
  newDoc.paymentLines = [{
    lineType: 'credit_note',
    reference: cnRef,
    description: desc,
    amount: total || 0,
    relatedInvoice: invRef,
  }];
  newDoc.payments = newDoc.paymentLines;

  newDoc.items = [];
}

/** BRC → Avoir sur vente : même type credit_note, partenaire client. */
function applyClientReturnSlipToCreditConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  const srcKey = canonicalArchiveTypeKey(sourceDoc.type);
  if (srcKey !== 'client_return_slip') return;

  newDoc.creditNoteParty = 'client';
  newDoc.credit_note_party = 'client';
  newDoc.isSupplier = false;
  newDoc.is_supplier = false;

  const clientName = String(
    sourceDoc.clientName || sourceDoc.client_name || sourceDoc.client || '',
  ).trim();
  if (clientName) {
    newDoc.client = clientName;
    newDoc.clientName = clientName;
  }
  if (sourceDoc.clientId || sourceDoc.client_id) {
    newDoc.clientId = sourceDoc.clientId || sourceDoc.client_id;
    newDoc.client_id = newDoc.clientId;
  }

  const invRef =
    sourceDoc.returnOriginalInvoice ||
    sourceDoc.return_original_invoice ||
    sourceDoc.returnOriginalInvoiceRef ||
    '';
  if (invRef && String(invRef).trim()) {
    newDoc.originalInvoiceNumber = String(invRef).trim();
    newDoc.original_invoice_number = newDoc.originalInvoiceNumber;
  }

  const invDate =
    sourceDoc.returnOriginalInvoiceDate ||
    sourceDoc.return_original_invoice_date ||
    '';
  if (invDate && String(invDate).trim()) {
    const iso = String(invDate).trim().slice(0, 10);
    newDoc.originalInvoiceDate = iso;
    newDoc.original_invoice_date = iso;
    newDoc.originalInvoice = {
      ...(newDoc.originalInvoice && typeof newDoc.originalInvoice === 'object'
        ? newDoc.originalInvoice
        : {}),
      number: newDoc.originalInvoiceNumber || invRef || '',
      date: iso,
    };
  }

  const returnReason =
    sourceDoc.clientReturnMotif ||
    sourceDoc.clientReturnReason ||
    sourceDoc.returnReason ||
    sourceDoc.return_reason ||
    sourceDoc.motif ||
    '';
  if (returnReason && String(returnReason).trim()) {
    newDoc.creditNoteReason = String(returnReason).trim();
    newDoc.credit_note_reason = newDoc.creditNoteReason;
  }

  newDoc.priority = '';
  newDoc.creditNoteType = '';
  newDoc.credit_type = '';
  newDoc.creditType = '';
  if (newDoc.creditNote && typeof newDoc.creditNote === 'object') {
    newDoc.creditNote = { ...newDoc.creditNote, type: '' };
  }

  if (Array.isArray(sourceDoc.items) && sourceDoc.items.length > 0) {
    newDoc.items = sourceDoc.items.map((item) => ({
      ...item,
      price: item.price ?? item.unitPrice ?? 0,
      unitPrice: item.unitPrice ?? item.price ?? 0,
    }));
    const subtotal = newDoc.items.reduce(
      (sum, item) =>
        sum +
        (Number(item.total) ||
          Number(item.quantity) * Number(item.price || item.unitPrice) ||
          0),
      0,
    );
    if (subtotal > 0) {
      newDoc.creditNoteAmount = subtotal;
      newDoc.credit_note_amount = subtotal;
      newDoc.amount = subtotal;
    }
  }
}

/** Facture → Avoir sur vente : partenaire client, refs facture, montant. */
function applyInvoiceToCreditConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  if (canonicalArchiveTypeKey(sourceDoc.type) !== 'invoice') return;

  newDoc.creditNoteParty = 'client';
  newDoc.credit_note_party = 'client';
  newDoc.isSupplier = false;
  newDoc.is_supplier = false;

  const clientName = String(
    sourceDoc.clientName || sourceDoc.client_name || sourceDoc.client || '',
  ).trim();
  if (clientName) {
    newDoc.client = clientName;
    newDoc.clientName = clientName;
  }
  if (sourceDoc.clientId || sourceDoc.client_id) {
    newDoc.clientId = sourceDoc.clientId || sourceDoc.client_id;
    newDoc.client_id = newDoc.clientId;
  }

  const invRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
  if (invRef) {
    newDoc.originalInvoiceNumber = invRef;
    newDoc.original_invoice_number = invRef;
    newDoc.originalInvoice = {
      ...(newDoc.originalInvoice && typeof newDoc.originalInvoice === 'object'
        ? newDoc.originalInvoice
        : {}),
      number: invRef,
      date: String(sourceDoc.date || '').slice(0, 10),
    };
  }

  let total = Number(sourceDoc.amount ?? sourceDoc.total) || 0;
  if (!total && Array.isArray(sourceDoc.items) && sourceDoc.items.length > 0) {
    total = sourceDoc.items.reduce(
      (sum, item) =>
        sum +
        (Number(item.total) ||
          Number(item.quantity) * Number(item.price || item.unitPrice) ||
          0),
      0,
    );
  }
  if (total > 0) {
    newDoc.creditNoteAmount = total;
    newDoc.credit_note_amount = total;
    newDoc.amount = total;
    newDoc.originalInvoiceAmount = total;
    newDoc.original_invoice_amount = total;
    newDoc.items = Array.isArray(sourceDoc.items)
      ? sourceDoc.items.map((item) => ({ ...item }))
      : [];
  }

  const invDate = pickDocStringField(
    sourceDoc,
    'date',
    'dueDate',
    'due_date',
  );
  if (invDate) {
    const iso = invDate.slice(0, 10);
    newDoc.originalInvoiceDate = iso;
    newDoc.original_invoice_date = iso;
    newDoc.originalInvoice = {
      ...(newDoc.originalInvoice && typeof newDoc.originalInvoice === 'object'
        ? newDoc.originalInvoice
        : {}),
      number: invRef || newDoc.originalInvoiceNumber || '',
      date: iso,
      amount: total > 0 ? total : newDoc.originalInvoice?.amount,
    };
  }

  newDoc.priority = '';
  newDoc.creditNoteType = '';
}

/** Reprend client + N° partenaire pour un RP converti. */
function applyPaymentReceiptPartnerFromSource(newDoc, sourceDoc, clientsList) {
  newDoc.isSupplier = false;
  newDoc.is_supplier = false;

  const clientId = String(
    sourceDoc.clientId || sourceDoc.client_id || '',
  ).trim();

  const clientName = String(
    sourceDoc.clientName || sourceDoc.client_name || sourceDoc.client || '',
  ).trim();

  if (clientId) {
    newDoc.clientId = clientId;
    newDoc.client_id = clientId;
  }

  if (clientName) {
    newDoc.clientName = clientName;
    newDoc.client_name = clientName;
    newDoc.client = clientName;
  }

  const existingPartnerNum = String(
    sourceDoc.partnerNumber || sourceDoc.partner_number || '',
  ).trim();
  if (existingPartnerNum) {
    newDoc.partnerNumber = existingPartnerNum;
    newDoc.partner_number = existingPartnerNum;
    return;
  }

  if (clientId && Array.isArray(clientsList)) {
    const partner = clientsList.find((c) => String(c.id) === String(clientId));
    if (partner) {
      const num = resolvePartnerReferenceCode(partner, { isSupplier: false });
      if (num) {
        newDoc.partnerNumber = num;
        newDoc.partner_number = num;
      }
    }
  }
}

function stripDeliveryConversionIrrelevantFields(newDoc) {
  if (!newDoc) return;
  [
    'dueDate',
    'due_date',
    'paymentConditions',
    'payment_conditions',
    'conditions',
    'paymentTerms',
    'payment_terms',
    'desiredPaymentTerms',
    'desired_payment_terms',
    'paymentMethod',
    'payment_method',
    'invoiceType',
    'customerOrderReference',
    'customer_order_reference',
    'deliveryReference',
    'delivery_reference',
    'deliveryNoteReference',
    'delivery_note_reference',
    'projectNumber',
    'project_number',
    'projectReference',
    'project_reference',
    'validUntil',
    'valid_until',
    'acceptanceDeadline',
    'acceptance_deadline',
    'priority',
    'paymentAmount',
    'payment_amount',
    'paymentInvoiceNumbers',
    'invoicesPaid',
    'invoices_paid',
    'clientCreditNoteRef',
    'originalInvoiceNumber',
    'original_invoice_number',
  ].forEach((k) => {
    newDoc[k] = '';
  });
}

/** Facture → Bon de livraison : client, lignes, refs traceabilité ; retire champs paiement/facture. */
function applyInvoiceToDeliveryConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  if (canonicalArchiveTypeKey(sourceDoc.type) !== 'invoice') return;

  const clientName = pickDocStringField(sourceDoc, 'clientName', 'client_name', 'client');
  if (clientName) {
    newDoc.client = clientName;
    newDoc.clientName = clientName;
  }
  if (sourceDoc.clientId || sourceDoc.client_id) {
    newDoc.clientId = sourceDoc.clientId || sourceDoc.client_id;
    newDoc.client_id = newDoc.clientId;
  }
  newDoc.isSupplier = false;
  newDoc.is_supplier = false;

  if (Array.isArray(sourceDoc.items) && sourceDoc.items.length > 0) {
    newDoc.items = sourceDoc.items.map((item) => ({ ...item }));
  }

  const invRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
  if (invRef) {
    newDoc.invoiceReference = invRef;
    newDoc.invoice_reference = invRef;
  }

  const bc = pickDocStringField(
    sourceDoc,
    'orderReference',
    'order_reference',
    'purchaseOrderReference',
    'purchase_order_reference',
  );
  if (bc) {
    newDoc.orderReference = bc;
    newDoc.purchaseOrderReference = bc;
    newDoc.purchase_order_reference = bc;
  }

  const addr = pickDocStringField(
    sourceDoc,
    'deliveryLocation',
    'delivery_location',
    'deliveryPlace',
    'delivery_place',
    'deliveryAddress',
    'delivery_address',
  );
  if (addr) {
    newDoc.deliveryAddress = addr;
    newDoc.delivery_address = addr;
    newDoc.deliveryLocation = addr;
    newDoc.delivery_location = addr;
  }

  const delDate = pickDocStringField(
    sourceDoc,
    'deliveryDate',
    'delivery_date',
    'expectedDeliveryDate',
    'expected_delivery_date',
  );
  if (delDate) {
    const iso = delDate.slice(0, 10);
    newDoc.deliveryDate = iso;
    newDoc.delivery_date = iso;
    newDoc.expectedDeliveryDate = iso;
    newDoc.expected_delivery_date = iso;
  }

  const sales = pickDocStringField(
    sourceDoc,
    'salesPerson',
    'sales_person',
    'commercial',
  );
  if (sales) {
    newDoc.salesPerson = sales;
    newDoc.commercial = sales;
  }

  stripDeliveryConversionIrrelevantFields(newDoc);

  newDoc.quoteReference = '';
  newDoc.quote_reference = '';
  newDoc.originalDocumentReference = '';
  newDoc.original_document_reference = '';

  const cor = pickDocStringField(
    sourceDoc,
    'customerOrderReference',
    'customer_order_reference',
    'originalDocumentReference',
    'original_document_reference',
  );
  if (cor) {
    newDoc.customerOrderReference = cor;
    newDoc.documentReference = cor;
    newDoc.document_reference = cor;
  }
}

function stripLogisticsConversionIrrelevantFields(newDoc) {
  if (!newDoc) return;
  [
    'dueDate',
    'due_date',
    'paymentConditions',
    'payment_conditions',
    'conditions',
    'paymentTerms',
    'payment_terms',
    'paymentMethod',
    'payment_method',
    'invoiceType',
    'paymentAmount',
    'payment_amount',
    'validUntil',
    'valid_until',
  ].forEach((k) => {
    newDoc[k] = '';
  });
}

/** Bon de sortie → Bon de transfert : entrepôts, refs, lignes. */
function applyExitToTransferConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  const srcKey = canonicalArchiveTypeKey(sourceDoc.type);
  if (srcKey !== 'exit_slip' && srcKey !== 'exit_note') return;

  if (Array.isArray(sourceDoc.items) && sourceDoc.items.length > 0) {
    newDoc.items = sourceDoc.items.map((item) => ({ ...item }));
  }

  const orig = pickDocStringField(
    sourceDoc,
    'exitNoteOriginWarehouse',
    'originWarehouseName',
    'origin_warehouse_name',
  );
  if (orig) {
    newDoc.transferNoteOriginWarehouse = orig;
    newDoc.originWarehouseName = orig;
  }

  const dest = pickDocStringField(
    sourceDoc,
    'exitNoteDestinationWarehouse',
    'destinationWarehouseName',
    'destination_warehouse_name',
  );
  if (dest) {
    newDoc.transferNoteDestinationWarehouse = dest;
    newDoc.destinationWarehouseName = dest;
  }

  const exitRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
  if (exitRef) {
    newDoc.transferNoteTransferRequestRef = exitRef;
    newDoc.originalDocumentReference = exitRef;
  }

  const docRef = pickDocStringField(
    sourceDoc,
    'exitNoteDocumentReference',
    'entryDocumentReference',
    'entry_document_reference',
  );
  if (docRef) newDoc.transferNoteDocumentReference = docRef;

  const mode = pickDocStringField(
    sourceDoc,
    'exitNoteTransportMode',
    'deliveryMode',
    'delivery_mode',
  );
  if (mode) {
    newDoc.transferNoteTransportMode = mode;
    newDoc.shipmentMode = mode;
  }

  const handler = pickDocStringField(
    sourceDoc,
    'exitNoteHandler',
    'transferResponsibleName',
    'transfer_responsible_name',
  );
  if (handler) {
    newDoc.transferNoteHandler = handler;
    newDoc.transferResponsibleName = handler;
  }

  const approved = pickDocStringField(
    sourceDoc,
    'exitNoteAuthorizedBy',
    'approvedByName',
    'approved_by_name',
  );
  if (approved) {
    newDoc.transferNoteApprovedBy = approved;
    newDoc.approvedByName = approved;
  }

  const expect = pickDocStringField(
    sourceDoc,
    'exitNoteExpectedDate',
    'expectedDeliveryDate',
    'expected_delivery_date',
    'date',
  );
  if (expect) {
    newDoc.transferNoteExpectedDate = expect.slice(0, 10);
    newDoc.expectedDeliveryDate = newDoc.transferNoteExpectedDate;
  }

  stripLogisticsConversionIrrelevantFields(newDoc);
  newDoc.priority = '';
}

/** Bon de transfert → Bon de réception (destination). */
function applyTransferToReceptionConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  const srcKey = canonicalArchiveTypeKey(sourceDoc.type);
  if (srcKey !== 'transfer_slip' && srcKey !== 'transfer_note') return;

  const transferRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
  if (transferRef) {
    newDoc.originalDocumentReference = transferRef;
    newDoc.deliveryNoteReference = transferRef;
    newDoc.receptionDeliveryNoteRef = transferRef;
  }

  const destWh = pickDocStringField(
    sourceDoc,
    'transferNoteDestinationWarehouse',
    'destinationWarehouseName',
    'destination_warehouse_name',
  );
  if (destWh) {
    newDoc.receptionWarehouse = destWh;
    newDoc.warehouseName = destWh;
    newDoc.receiverAddress = destWh;
    newDoc.receptionAddress = destWh;
  }

  if (Array.isArray(sourceDoc.items) && sourceDoc.items.length > 0) {
    newDoc.items = sourceDoc.items.map((item) => normalizeReceptionLineItem(item));
  }

  const expected = pickDocStringField(
    sourceDoc,
    'transferNoteExpectedDate',
    'expectedDeliveryDate',
    'expected_delivery_date',
    'date',
  );
  if (expected) {
    const iso = expected.slice(0, 10);
    newDoc.expectedReceptionDate = iso;
    newDoc.expected_reception_date = iso;
    newDoc.expectedDeliveryDate = iso;
    newDoc.expected_delivery_date = iso;
  }

  stripLogisticsConversionIrrelevantFields(newDoc);
  newDoc.priority = '';
  newDoc.carrier = '';
  newDoc.trackingNumber = '';
}

/** Bon de réception → Bon d'entrée : entrepôt, refs, lignes, fournisseur. */
function applyReceptionToStockEntryConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  const srcKey = canonicalArchiveTypeKey(sourceDoc.type);
  if (srcKey !== 'reception_note' && srcKey !== 'receipt_note') return;

  if (Array.isArray(sourceDoc.items) && sourceDoc.items.length > 0) {
    newDoc.items = sourceDoc.items.map((item) => ({ ...item }));
  }

  const receptionRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
  if (receptionRef) {
    newDoc.stockEntryReceptionNoteRef = receptionRef;
    newDoc.originalDocumentReference = receptionRef;
  }

  const wh = pickDocStringField(
    sourceDoc,
    'receptionWarehouse',
    'warehouseName',
    'warehouse_name',
    'receptionAddress',
    'reception_address',
  );
  if (wh) {
    newDoc.stockEntryWarehouse = wh;
    newDoc.warehouseName = wh;
  }

  const poRef = pickDocStringField(
    sourceDoc,
    'purchaseOrderReference',
    'purchase_order_reference',
    'receptionOrder',
    'reception_order',
    'receptionOrderRef',
  );
  if (poRef) {
    newDoc.stockEntryPurchaseOrderRef = poRef;
  }

  const supplier = pickDocStringField(
    sourceDoc,
    'supplierName',
    'supplier_name',
    'supplier',
    'clientName',
    'client_name',
    'client',
  );
  if (supplier) {
    newDoc.stockEntrySupplier = supplier;
    newDoc.supplierName = supplier;
  }

  const entryDate = pickFirstIsoDate(sourceDoc, [
    'date',
    'expectedReceptionDate',
    'expected_reception_date',
    'expectedDeliveryDate',
    'expected_delivery_date',
  ]);
  if (entryDate) {
    newDoc.stockEntryEntryDate = entryDate.slice(0, 10);
    newDoc.entryDate = newDoc.stockEntryEntryDate;
  }

  const receivedBy = pickDocStringField(
    sourceDoc,
    'receptionReceivedBy',
    'receivedByName',
    'received_by_name',
    'receivedBy',
    'received_by',
  );
  if (receivedBy) {
    newDoc.stockEntryReceivedBy = receivedBy;
    newDoc.receivedByName = receivedBy;
  }

  const controlledBy = pickDocStringField(
    sourceDoc,
    'receptionControlledBy',
    'controlledByName',
    'controlled_by_name',
  );
  if (controlledBy) {
    newDoc.stockEntryControlledBy = controlledBy;
    newDoc.controlledByName = controlledBy;
  }

  const condition = pickDocStringField(
    sourceDoc,
    'receptionCondition',
    'goodsCondition',
    'goods_condition',
  );
  if (condition) {
    newDoc.stockEntryCondition = condition;
    newDoc.goodsCondition = condition;
  }

  stripLogisticsConversionIrrelevantFields(newDoc);
  newDoc.priority = '';
}

/** Bon de transfert → Bon d'entrée (raccourci) : entrepôt destination, refs, lignes. */
function applyTransferToStockEntryConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  const srcKey = canonicalArchiveTypeKey(sourceDoc.type);
  if (srcKey !== 'transfer_slip' && srcKey !== 'transfer_note') return;

  if (Array.isArray(sourceDoc.items) && sourceDoc.items.length > 0) {
    newDoc.items = sourceDoc.items.map((item) => ({ ...item }));
  }

  const transferRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
  if (transferRef) {
    newDoc.stockEntryTransferNoteRef = transferRef;
    newDoc.originalDocumentReference = transferRef;
  }

  const destWh = pickDocStringField(
    sourceDoc,
    'transferNoteDestinationWarehouse',
    'destinationWarehouseName',
    'destination_warehouse_name',
  );
  if (destWh) {
    newDoc.stockEntryWarehouse = destWh;
    newDoc.warehouseName = destWh;
  }

  const originWh = pickDocStringField(
    sourceDoc,
    'transferNoteOriginWarehouse',
    'originWarehouseName',
    'origin_warehouse_name',
  );
  if (originWh) {
    newDoc.stockEntrySource = originWh;
    newDoc.entrySource = originWh;
  }

  const handler = pickDocStringField(
    sourceDoc,
    'transferNoteHandler',
    'transferResponsibleName',
    'transfer_responsible_name',
  );
  if (handler) {
    newDoc.stockEntryReceivedBy = handler;
    newDoc.receivedByName = handler;
  }

  const entryDate = pickFirstIsoDate(sourceDoc, [
    'date',
    'transferNoteExpectedDate',
    'expectedDeliveryDate',
    'expected_delivery_date',
  ]);
  if (entryDate) {
    newDoc.stockEntryEntryDate = entryDate.slice(0, 10);
    newDoc.entryDate = newDoc.stockEntryEntryDate;
  }

  stripLogisticsConversionIrrelevantFields(newDoc);
  newDoc.priority = '';
}

/** Bon de livraison → Bordereau d'envoi : client, refs, transport, colis. */
function applyDeliveryToShippingConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  if (canonicalArchiveTypeKey(sourceDoc.type) !== 'delivery') return;

  const clientName = pickDocStringField(sourceDoc, 'clientName', 'client_name', 'client');
  if (clientName) {
    newDoc.client = clientName;
    newDoc.clientName = clientName;
  }
  if (sourceDoc.clientId || sourceDoc.client_id) {
    newDoc.clientId = sourceDoc.clientId || sourceDoc.client_id;
    newDoc.client_id = newDoc.clientId;
  }
  newDoc.isSupplier = false;
  newDoc.is_supplier = false;

  if (Array.isArray(sourceDoc.items) && sourceDoc.items.length > 0) {
    newDoc.items = sourceDoc.items.map((item) => ({ ...item }));
  }

  const blRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
  if (blRef) {
    newDoc.shipmentDeliveryNoteRef = blRef;
    newDoc.deliveryNoteReference = blRef;
  }

  const clientOrderRef = pickDocStringField(
    sourceDoc,
    'customerOrderReference',
    'customer_order_reference',
    'documentReference',
    'document_reference',
  );
  const purchaseOrderRef = pickDocStringField(
    sourceDoc,
    'orderReference',
    'order_reference',
    'purchaseOrderReference',
    'purchase_order_reference',
  );
  const clientRef = clientOrderRef || purchaseOrderRef;
  if (clientRef) {
    newDoc.shipmentClientOrderRef = clientRef;
    newDoc.purchaseOrderReference = clientRef;
    if (clientOrderRef) {
      newDoc.customerOrderReference = clientOrderRef;
      newDoc.customer_order_reference = clientOrderRef;
    }
  }

  const inv = pickDocStringField(sourceDoc, 'invoiceReference', 'invoice_reference');
  if (inv) {
    newDoc.shipmentInvoiceRef = inv;
    newDoc.invoiceReference = inv;
  }

  const wh = pickDocStringField(
    sourceDoc,
    'departureWarehouse',
    'departure_warehouse',
    'originWarehouseName',
    'origin_warehouse_name',
  );
  if (wh) {
    newDoc.shipmentOriginWarehouse = wh;
    newDoc.originWarehouseName = wh;
  }

  const delLoc = pickDocStringField(
    sourceDoc,
    'deliveryLocation',
    'delivery_location',
    'deliveryAddress',
    'delivery_address',
    'lieu_livraison',
  );
  if (delLoc) {
    newDoc.deliveryLocation = delLoc;
    newDoc.deliveryAddress = delLoc;
    newDoc.lieu_livraison = delLoc;
  }

  const transp = pickDocStringField(
    sourceDoc,
    'carrier',
    'transporterName',
    'transporter_name',
    'deliveryCarrier',
  );
  if (transp) {
    newDoc.shipmentCarrier = transp;
    newDoc.carrier = transp;
  }

  const track = pickDocStringField(
    sourceDoc,
    'trackingNumber',
    'tracking_number',
    'shipmentTrackingNumber',
  );
  if (track) {
    newDoc.shipmentTrackingNumber = track;
    newDoc.trackingNumber = track;
  }

  const carrierTrack = resolveCarrierTrackingFromArchiveDoc(sourceDoc);
  assignCarrierTrackingFields(newDoc, carrierTrack);

  const mode = pickDocStringField(
    sourceDoc,
    'deliveryMethod',
    'delivery_method',
    'deliveryMode',
    'delivery_mode',
    'shipmentMethod',
    'shipment_mode',
  );
  if (mode) {
    newDoc.shipmentMethod = mode;
    newDoc.shipmentMode = mode;
    newDoc.deliveryMethod = mode;
    newDoc.delivery_method = mode;
  }

  const pkgRaw =
    sourceDoc.shipmentPackageCount ?? sourceDoc.packageCount ?? sourceDoc.package_count;
  const pkgNum = Number(pkgRaw);
  if (Number.isFinite(pkgNum) && pkgNum >= 0) {
    newDoc.shipmentPackageCount = pkgNum;
    newDoc.packageCount = pkgNum;
  }

  const docDate = pickFirstIsoDate(sourceDoc, ['date']);
  if (docDate) {
    newDoc.date = docDate;
  }

  const shipDate = pickFirstIsoDate(sourceDoc, [
    'deliveryDate',
    'delivery_date',
    'expectedDeliveryDate',
    'expected_delivery_date',
  ]);
  if (shipDate) {
    newDoc.shipmentDate = shipDate;
    newDoc.shipment_date = shipDate;
  }

  const preparer = pickDocStringField(
    sourceDoc,
    'issuedBy',
    'issued_by',
    'etablieParName',
    'etablie_par_name',
  );
  if (preparer) {
    newDoc.issuedBy = preparer;
    newDoc.issued_by = preparer;
  }

  const declaredRaw = pickDocStringField(
    sourceDoc,
    'shipmentDeclaredValue',
    'declaredValue',
    'declared_value',
  );
  const declaredNum = Number(String(declaredRaw).replace(/\s/g, '').replace(',', '.'));
  if (Number.isFinite(declaredNum) && declaredNum > 0) {
    newDoc.shipmentDeclaredValue = declaredNum;
    newDoc.declaredValue = declaredNum;
    newDoc.amount = declaredNum;
  } else {
    newDoc.shipmentDeclaredValue = '';
    newDoc.declaredValue = null;
    newDoc.amount = 0;
  }

  stripLogisticsConversionIrrelevantFields(newDoc);
  newDoc.priority = sourceDoc.priority ?? '';
}

/** Facture → Reçu de paiement : client, refs, montant, lignes. */
function applyInvoiceToPaymentReceiptConversion(newDoc, sourceDoc, clientsList) {
  if (!newDoc || !sourceDoc) return;
  if (canonicalArchiveTypeKey(sourceDoc.type) !== 'invoice') return;

  const patch = buildPaymentReceiptPatchFromInvoice(sourceDoc);
  const invRef = patch.paymentInvoiceNumbers || (sourceDoc.number ? String(sourceDoc.number).trim() : '');
  const lines =
    Array.isArray(patch.paymentLines) && patch.paymentLines.length ? patch.paymentLines : [];
  const total = Number(lines[0]?.amount) || 0;

  if (invRef) {
    newDoc.paymentInvoiceNumbers = invRef;
    newDoc.invoicesPaid = invRef;
    newDoc.invoices_paid = invRef;
  }

  applyPaymentReceiptPartnerFromSource(newDoc, sourceDoc, clientsList);

  if (total > 0) {
    newDoc.amount = total;
    newDoc.paymentAmount = total;
    newDoc.payment_amount = total;
  }

  newDoc.paymentLines =
    lines.length > 0
      ? lines
      : [{
          lineType: 'invoice',
          reference: invRef,
          description: invRef
            ? i18n.t('modules.facturation.pages.archivesFacturation.payment_line_invoice_collection_ref', { ref: invRef })
            : i18n.t('modules.facturation.pages.archivesFacturation.payment_line_invoice_collection'),
          amount: total || 0,
          relatedInvoice: invRef,
        }];
  newDoc.payments = newDoc.paymentLines;
  newDoc.items = [];
}

/** Avoir sur vente → Reçu de paiement : refs, client, montant, lignes. */
function applyCreditNoteToPaymentReceiptConversion(newDoc, sourceDoc, clientsList) {
  if (!newDoc || !sourceDoc) return;
  if (canonicalArchiveTypeKey(sourceDoc.type) !== 'credit_note') return;
  if (!isClientCreditNoteDocument(sourceDoc)) return;

  const patch = buildPaymentReceiptPatchFromClientCreditNote(sourceDoc);
  const cnRef = patch.clientCreditNoteRef || (sourceDoc.number ? String(sourceDoc.number).trim() : '');
  const lines =
    Array.isArray(patch.paymentLines) && patch.paymentLines.length ? patch.paymentLines : [];
  const total = Number(lines[0]?.amount) || 0;

  if (cnRef) {
    newDoc.clientCreditNoteRef = cnRef;
    newDoc.originalDocumentReference = cnRef;
    newDoc.original_document_reference = cnRef;
  }

  const invRef = String(patch.paymentInvoiceNumbers || '').trim();
  if (invRef) {
    newDoc.paymentInvoiceNumbers = invRef;
    newDoc.invoicesPaid = invRef;
    newDoc.invoices_paid = invRef;
  }

  applyPaymentReceiptPartnerFromSource(newDoc, sourceDoc, clientsList);

  if (total > 0) {
    newDoc.amount = total;
    newDoc.paymentAmount = total;
    newDoc.payment_amount = total;
  }

  newDoc.paymentLines = lines.length > 0 ? lines : [{
    lineType: 'credit_note',
    reference: cnRef,
    description: paymentLineCreditNoteClientDesc(cnRef),
    amount: total || 0,
    relatedInvoice: invRef,
  }];
  newDoc.payments = newDoc.paymentLines;
  newDoc.items = [];
}

function buildCreditNotePaymentLineFromSource(sourceDoc) {
  const cnRef = sourceDoc?.number ? String(sourceDoc.number).trim() : '';
  const invRef = String(
    sourceDoc?.originalInvoiceNumber ||
      sourceDoc?.original_invoice_number ||
      (sourceDoc?.originalInvoice && sourceDoc.originalInvoice.number) ||
      '',
  ).trim();
  let total =
    Number(
      sourceDoc?.creditNoteAmount ||
        sourceDoc?.credit_note_amount ||
        sourceDoc?.paymentAmount ||
        sourceDoc?.amount,
    ) || 0;
  if (!total && Array.isArray(sourceDoc?.items) && sourceDoc.items.length > 0) {
    total = sourceDoc.items.reduce(
      (sum, item) =>
        sum +
        (Number(item.total) ||
          Number(item.quantity) * Number(item.price || item.unitPrice) ||
          0),
      0,
    );
  }
  const desc = paymentLineCreditNoteSupplierDesc(cnRef);
  return {
    lineType: 'credit_note',
    reference: cnRef,
    description: desc,
    amount: total || 0,
    relatedInvoice: invRef,
  };
}

function buildPaymentLinesFromMergeSource(sourceDoc) {
  const key = canonicalArchiveTypeKey(sourceDoc?.type);
  if (key === 'purchase_order') {
    const patch = buildPaymentVoucherPatchFromPurchaseOrder(sourceDoc);
    if (Array.isArray(patch.paymentLines) && patch.paymentLines.length) {
      return patch.paymentLines;
    }
    const bcRef =
      patch.purchaseOrderRef || (sourceDoc.number ? String(sourceDoc.number).trim() : '');
    return [
      {
        lineType: 'purchase_order',
        reference: bcRef,
        description: paymentLinePurchaseOrderDesc(bcRef),
        amount: 0,
        relatedInvoice: '',
      },
    ];
  }
  if (key === 'credit_note' && isSupplierCreditNoteDocument(sourceDoc)) {
    return [buildCreditNotePaymentLineFromSource(sourceDoc)];
  }
  return [];
}

function buildMergedPaymentVoucherFromSources(
  sourceDocs,
  suppliersList,
  buildPayload,
  { regenerateTitle } = {},
) {
  const sorted = [...sourceDocs].sort((a, b) => {
    const rank = (doc) => {
      const k = canonicalArchiveTypeKey(doc?.type);
      if (k === 'purchase_order') return 0;
      if (k === 'credit_note') return 1;
      return 9;
    };
    return rank(a) - rank(b);
  });

  const newDoc = buildPayload(sorted[0], 'payment_voucher');
  const allLines = sorted.flatMap((source) => buildPaymentLinesFromMergeSource(source));
  const total = allLines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);

  newDoc.paymentLines = allLines;
  newDoc.payments = allLines;
  newDoc.amount = total;
  newDoc.paymentAmount = total;
  newDoc.payment_amount = total;
  newDoc.items = [];

  applyPaymentVoucherPartnerFromSource(newDoc, sorted[0], suppliersList);
  newDoc.paymentReason = '';
  newDoc.motif = '';
  newDoc.priority = '';
  stripIrrelevantFieldsFromConvertedPaymentVoucher(newDoc);
  newDoc.mergedSourceDocumentIds = sorted.map((d) => d.id).filter(Boolean);

  if (typeof regenerateTitle === 'function') {
    regenerateTitle(newDoc);
  }

  return newDoc;
}

function buildPaymentLinesFromRpMergeSource(sourceDoc) {
  const key = canonicalArchiveTypeKey(sourceDoc?.type);
  if (key === 'invoice') {
    const patch = buildPaymentReceiptPatchFromInvoice(sourceDoc);
    if (Array.isArray(patch.paymentLines) && patch.paymentLines.length) {
      return patch.paymentLines;
    }
    const invRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
    return [{
      lineType: 'invoice',
      reference: invRef,
      description: invRef
        ? i18n.t('modules.facturation.pages.archivesFacturation.payment_line_invoice_collection_ref', { ref: invRef })
        : i18n.t('modules.facturation.pages.archivesFacturation.payment_line_invoice_collection'),
      amount: 0,
      relatedInvoice: invRef,
    }];
  }
  if (key === 'credit_note' && isClientCreditNoteDocument(sourceDoc)) {
    const patch = buildPaymentReceiptPatchFromClientCreditNote(sourceDoc);
    if (Array.isArray(patch.paymentLines) && patch.paymentLines.length) {
      return patch.paymentLines;
    }
    return [{
      lineType: 'credit_note',
      reference: sourceDoc.number ? String(sourceDoc.number).trim() : '',
      description: paymentLineCreditNoteClientDesc(
        sourceDoc.number ? String(sourceDoc.number).trim() : '',
      ),
      amount: 0,
      relatedInvoice: '',
    }];
  }
  return [];
}

function buildMergedPaymentReceiptFromSources(
  sourceDocs,
  clientsList,
  buildPayload,
  { regenerateTitle } = {},
) {
  const sorted = [...sourceDocs].sort((a, b) => {
    const rank = (doc) => {
      const k = canonicalArchiveTypeKey(doc?.type);
      if (k === 'invoice') return 0;
      if (k === 'credit_note') return 1;
      return 9;
    };
    return rank(a) - rank(b);
  });

  const newDoc = buildPayload(sorted[0], 'payment_receipt');
  const allLines = sorted.flatMap((source) => buildPaymentLinesFromRpMergeSource(source));
  const total = allLines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);

  newDoc.paymentLines = allLines;
  newDoc.payments = allLines;
  newDoc.amount = total;
  newDoc.paymentAmount = total;
  newDoc.payment_amount = total;
  newDoc.items = [];

  applyPaymentReceiptPartnerFromSource(newDoc, sorted[0], clientsList);
  newDoc.mergedSourceDocumentIds = sorted.map((d) => d.id).filter(Boolean);

  if (typeof regenerateTitle === 'function') {
    regenerateTitle(newDoc);
  }

  return newDoc;
}

/** BRL → Bordereau retour fournisseur : refs BC/BL, lignes, fournisseur, établi par. */
function applyReceptionToReturnConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  const srcKey = canonicalArchiveTypeKey(sourceDoc.type);
  if (srcKey !== 'receipt_note' && srcKey !== 'reception_note') return;

  const bcRef =
    sourceDoc.purchaseOrderReference ||
    sourceDoc.purchase_order_reference ||
    sourceDoc.receptionOrderRef ||
    sourceDoc.receptionOrder ||
    sourceDoc.reception_order ||
    '';
  if (bcRef && String(bcRef).trim()) {
    const ref = String(bcRef).trim();
    newDoc.returnOriginalPurchaseOrder = ref;
    newDoc.return_original_purchase_order = ref;
  }

  const blRef =
    sourceDoc.deliveryNoteReference ||
    sourceDoc.delivery_note_reference ||
    sourceDoc.receptionDeliveryNoteRef ||
    sourceDoc.reception_delivery_note ||
    '';
  if (blRef && String(blRef).trim()) {
    const ref = String(blRef).trim();
    newDoc.returnOriginalDelivery = ref;
    newDoc.return_original_delivery = ref;
  } else {
    const brlRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
    if (brlRef) {
      newDoc.returnOriginalDelivery = brlRef;
      newDoc.return_original_delivery = brlRef;
    }
  }

  const supplier =
    sourceDoc.supplier ||
    sourceDoc.supplierName ||
    sourceDoc.supplier_name ||
    sourceDoc.client ||
    '';
  if (supplier && String(supplier).trim()) {
    const name =
      sourceDoc.supplierName ||
      sourceDoc.supplier_name ||
      String(supplier).trim();
    newDoc.supplier = String(supplier).trim();
    newDoc.supplierName = name;
    if (!newDoc.client) newDoc.client = String(supplier).trim();
  }

  if (Array.isArray(sourceDoc.items) && sourceDoc.items.length > 0) {
    newDoc.items = sourceDoc.items.map((item) => ({
      ...item,
      price: item.price ?? item.unitPrice ?? 0,
      unitPrice: item.unitPrice ?? item.price ?? 0,
    }));
  }

  const preparer = sourceDoc.issuedBy || sourceDoc.issued_by || '';
  if (preparer && String(preparer).trim()) {
    newDoc.issuedBy = String(preparer).trim();
    newDoc.issued_by = newDoc.issuedBy;
  }
}

/** BRF → Avoir sur achat : même type credit_note, partenaire fournisseur. */
function applyReturnSlipToCreditConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  const srcKey = canonicalArchiveTypeKey(sourceDoc.type);
  if (srcKey !== 'return_slip') return;

  newDoc.creditNoteParty = 'supplier';
  newDoc.credit_note_party = 'supplier';
  newDoc.isSupplier = true;
  newDoc.is_supplier = true;

  const supplier =
    sourceDoc.supplier ||
    sourceDoc.supplierName ||
    sourceDoc.supplier_name ||
    sourceDoc.client ||
    '';
  if (supplier && String(supplier).trim()) {
    const name =
      sourceDoc.supplierName ||
      sourceDoc.supplier_name ||
      String(supplier).trim();
    newDoc.supplier = String(supplier).trim();
    newDoc.supplierName = name;
    newDoc.client = String(supplier).trim();
    if (sourceDoc.supplierId || sourceDoc.clientId) {
      newDoc.clientId = sourceDoc.supplierId || sourceDoc.clientId;
    }
  }

  const invRef =
    sourceDoc.returnOriginalInvoice ||
    sourceDoc.return_original_invoice ||
    sourceDoc.returnOriginalInvoiceRef ||
    '';
  if (invRef && String(invRef).trim()) {
    newDoc.originalInvoiceNumber = String(invRef).trim();
    newDoc.original_invoice_number = newDoc.originalInvoiceNumber;
  }

  const invDate =
    sourceDoc.returnOriginalInvoiceDate ||
    sourceDoc.return_original_invoice_date ||
    '';
  if (invDate && String(invDate).trim()) {
    const iso = String(invDate).trim().slice(0, 10);
    newDoc.originalInvoiceDate = iso;
    newDoc.original_invoice_date = iso;
    newDoc.originalInvoice = {
      ...(newDoc.originalInvoice && typeof newDoc.originalInvoice === 'object'
        ? newDoc.originalInvoice
        : {}),
      number: newDoc.originalInvoiceNumber || invRef || '',
      date: iso,
    };
  }

  const brfRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
  if (brfRef && !newDoc.originalInvoiceNumber) {
    newDoc.originalInvoiceNumber = brfRef;
    newDoc.original_invoice_number = brfRef;
  }

  const returnReason =
    sourceDoc.returnReason ||
    sourceDoc.return_reason ||
    sourceDoc.motif ||
    '';
  if (returnReason && String(returnReason).trim()) {
    newDoc.creditNoteReason = String(returnReason).trim();
    newDoc.credit_note_reason = newDoc.creditNoteReason;
  }

  // Priorité et type d'avoir : ne pas reprendre du BRF — choix laissé à l'utilisateur
  newDoc.priority = '';
  newDoc.creditNoteType = '';
  newDoc.credit_type = '';
  newDoc.creditType = '';
  if (newDoc.creditNote && typeof newDoc.creditNote === 'object') {
    newDoc.creditNote = { ...newDoc.creditNote, type: '' };
  }

  if (Array.isArray(sourceDoc.items) && sourceDoc.items.length > 0) {
    newDoc.items = sourceDoc.items.map((item) => ({
      ...item,
      price: item.price ?? item.unitPrice ?? 0,
      unitPrice: item.unitPrice ?? item.price ?? 0,
    }));
    const subtotal = newDoc.items.reduce(
      (sum, item) => sum + (Number(item.total) || Number(item.quantity) * Number(item.price || item.unitPrice) || 0),
      0,
    );
    if (subtotal > 0) {
      newDoc.creditNoteAmount = subtotal;
      newDoc.credit_note_amount = subtotal;
      newDoc.amount = subtotal;
    }
  }
}

/** Icône / couleur / libellé type en Archives (Avoir sur vente vs Avoir sur achat). */
function getArchiveDocTypePresentation(document, documentTypesMap) {
  const displayTypeKey = resolvePaymentDocumentDisplayType(document) ?? document?.type;
  const docType =
    documentTypesMap[displayTypeKey] ||
    documentTypesMap[String(displayTypeKey).replace(/_/g, '-')] ||
    documentTypesMap[document?.type] ||
    documentTypesMap.default;
  const creditLabel = resolveCreditNoteDisplayLabel(document);
  if (creditLabel) {
    return {
      ...docType,
      label: creditLabel,
      color: resolveCreditNoteDisplayColor(document),
      icon: FileX,
    };
  }
  return docType;
}

/** DD/DPR → BC : préremplit Demande de devis / Demande de proforma avec le N° source. */
function applySourceRequestRefsToConvertedPurchaseOrder(newDoc, sourceDoc, normalizedTargetType) {
  if (!newDoc || normalizedTargetType !== 'purchase_order') return;
  const srcKey = canonicalArchiveTypeKey(sourceDoc?.type);
  const srcNumber =
    sourceDoc?.number != null ? String(sourceDoc.number).trim() : '';
  if (!srcNumber) return;

  if (srcKey === 'quote_request') {
    newDoc.quoteRequestRef = srcNumber;
    newDoc.documentReference = srcNumber;
    newDoc.document_reference = srcNumber;
  } else if (srcKey === 'proforma_request') {
    newDoc.proformaRequestRef = srcNumber;
    newDoc.originalDocumentReference = srcNumber;
    newDoc.original_document_reference = srcNumber;
  }
}

/** BC Archives / PDF : mappe document_reference → quoteRequestRef, etc. */
function applyArchivePurchaseOrderRefFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  if (canonicalArchiveTypeKey(document.type) !== 'purchase_order') return;

  const ddRef = [
    docToDisplay.quoteRequestRef,
    document.quoteRequestRef,
    docToDisplay.documentReference,
    document.documentReference,
    document.document_reference,
  ]
    .map((v) => (v != null ? String(v).trim() : ''))
    .find(Boolean);
  if (ddRef) {
    docToDisplay.quoteRequestRef = ddRef;
    if (!docToDisplay.documentReference) docToDisplay.documentReference = ddRef;
  }

  const dprRef = [
    docToDisplay.proformaRequestRef,
    document.proformaRequestRef,
    docToDisplay.originalDocumentReference,
    document.originalDocumentReference,
    document.original_document_reference,
  ]
    .map((v) => (v != null ? String(v).trim() : ''))
    .find(Boolean);
  if (dprRef) {
    docToDisplay.proformaRequestRef = dprRef;
    if (!docToDisplay.originalDocumentReference) {
      docToDisplay.originalDocumentReference = dprRef;
    }
  }
}

/** BC Archives / PDF : mappe champs DB → noms lus par TemplateWrapper (délai, mode, paiement). */
function applyArchivePurchaseOrderDetailFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  if (canonicalArchiveTypeKey(document.type) !== 'purchase_order') return;

  const timeframe = [
    docToDisplay.deliveryTimeframe,
    document.deliveryTimeframe,
    docToDisplay.desiredDeliveryTime,
    document.desiredDeliveryTime,
    document.desired_delivery_time,
  ]
    .map((v) => (v != null ? String(v).trim() : ''))
    .find(Boolean);
  if (timeframe) {
    docToDisplay.deliveryTimeframe = timeframe;
    if (!docToDisplay.desiredDeliveryTime) docToDisplay.desiredDeliveryTime = timeframe;
  }

  const mode = [
    docToDisplay.deliveryMode,
    document.deliveryMode,
    document.delivery_mode,
  ]
    .map((v) => (v != null ? String(v).trim() : ''))
    .find(Boolean);
  if (mode) docToDisplay.deliveryMode = mode;

  const payment = [
    docToDisplay.paymentTerms,
    document.paymentTerms,
    docToDisplay.desiredPaymentTerms,
    document.desiredPaymentTerms,
    document.desired_payment_terms,
    document.payment_terms,
  ]
    .map((v) => (v != null ? String(v).trim() : ''))
    .find(Boolean);
  if (payment) {
    docToDisplay.paymentTerms = payment;
    if (!docToDisplay.desiredPaymentTerms) docToDisplay.desiredPaymentTerms = payment;
  }

  const buyer = [
    docToDisplay.buyerContact,
    document.buyerContact,
    document.buyer_contact,
  ]
    .map((v) => (v != null ? String(v).trim() : ''))
    .find(Boolean);
  if (buyer) docToDisplay.buyerContact = buyer;
}

/** Devis / Facture proforma Archives / PDF : mappe colonnes SQLite → champs lus par TemplateWrapper. */
function applyArchiveQuoteDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  const typeKey = canonicalArchiveTypeKey(document.type);
  if (typeKey !== 'quote' && typeKey !== 'proforma') return;

  const pick = (...vals) => {
    for (const v of vals) {
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const sales = pick(
    docToDisplay.salesPerson,
    document.salesPerson,
    docToDisplay.commercial,
    document.commercial,
    document.sales_person,
  );
  if (sales) {
    docToDisplay.salesPerson = sales;
    docToDisplay.commercial = sales;
  }

  const realisation = pick(
    docToDisplay.deliveryTime,
    document.deliveryTime,
    docToDisplay.estimatedDelivery,
    document.estimatedDelivery,
    docToDisplay.estimatedDeliveryTime,
    document.estimatedDeliveryTime,
    document.estimated_delivery_time,
    document.delivery_time,
  );
  if (realisation) {
    docToDisplay.deliveryTime = realisation;
    docToDisplay.estimatedDelivery = realisation;
    docToDisplay.estimatedDeliveryTime = realisation;
  }

  const deliveryDelay = pick(
    docToDisplay.deliveryDelay,
    document.deliveryDelay,
    docToDisplay.desiredDeliveryTime,
    document.desiredDeliveryTime,
    document.desired_delivery_time,
    document.delivery_delay,
  );
  if (deliveryDelay) {
    docToDisplay.deliveryDelay = deliveryDelay;
    docToDisplay.desiredDeliveryTime = deliveryDelay;
  }

  const paymentTerms = pick(
    docToDisplay.paymentTerms,
    document.paymentTerms,
    docToDisplay.desiredPaymentTerms,
    document.desiredPaymentTerms,
    document.desired_payment_terms,
    document.payment_terms,
  );
  if (paymentTerms) {
    docToDisplay.paymentTerms = paymentTerms;
    docToDisplay.desiredPaymentTerms = paymentTerms;
  }

  const paymentMethod = pick(
    docToDisplay.paymentMethod,
    document.paymentMethod,
    docToDisplay.plannedPaymentMethod,
    document.plannedPaymentMethod,
    document.payment_method,
  );
  if (paymentMethod) {
    docToDisplay.paymentMethod = paymentMethod;
    docToDisplay.plannedPaymentMethod = paymentMethod;
  }
}

/** Facture Archives / PDF : mappe champs DB → noms lus par TemplateWrapper. */
function applyArchiveInvoiceDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  if (canonicalArchiveTypeKey(document.type) !== 'invoice') return;

  const pick = (...vals) => {
    for (const v of vals) {
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const bc = pick(
    docToDisplay.orderReference,
    document.orderReference,
    document.order_reference,
    docToDisplay.purchaseOrderReference,
    document.purchaseOrderReference,
    document.purchase_order_reference,
  );
  if (bc) {
    docToDisplay.orderReference = bc;
    docToDisplay.purchaseOrderReference = bc;
  }

  const bl = pick(
    docToDisplay.deliveryReference,
    document.deliveryReference,
    document.delivery_reference,
    docToDisplay.deliveryNoteReference,
    document.deliveryNoteReference,
    document.delivery_note_reference,
  );
  if (bl) {
    docToDisplay.deliveryReference = bl;
    docToDisplay.deliveryNoteReference = bl;
  }

  const cor = pick(
    docToDisplay.customerOrderReference,
    document.customerOrderReference,
    document.customer_order_reference,
    docToDisplay.originalDocumentReference,
    document.originalDocumentReference,
    document.original_document_reference,
  );
  if (cor) {
    docToDisplay.customerOrderReference = cor;
    docToDisplay.originalDocumentReference = cor;
  }

  const pj = pick(
    docToDisplay.projectNumber,
    document.projectNumber,
    document.project_number,
    docToDisplay.projectReference,
    document.projectReference,
    document.project_reference,
  );
  if (pj) {
    docToDisplay.projectNumber = pj;
    docToDisplay.projectReference = pj;
  }

  const pc = pick(
    docToDisplay.paymentConditions,
    document.paymentConditions,
    document.payment_conditions,
  );
  const pcFromConditions = pick(docToDisplay.conditions, document.conditions);
  const resolvedPc = isInvoicePaymentConditionSlug(pc)
    ? pc
    : isInvoicePaymentConditionSlug(pcFromConditions)
      ? pcFromConditions
      : '';
  if (resolvedPc) {
    docToDisplay.paymentConditions = resolvedPc;
    docToDisplay.conditions = resolvedPc;
  } else if (
    docToDisplay.paymentConditions &&
    !isInvoicePaymentConditionSlug(docToDisplay.paymentConditions)
  ) {
    docToDisplay.paymentConditions = '';
    docToDisplay.payment_conditions = '';
  }

  const sales = pick(
    docToDisplay.salesPerson,
    document.salesPerson,
    document.sales_person,
    docToDisplay.commercial,
    document.commercial,
  );
  if (sales) {
    docToDisplay.salesPerson = sales;
    docToDisplay.commercial = sales;
  }

  const dl = pick(
    docToDisplay.deliveryLocation,
    document.deliveryLocation,
    document.delivery_location,
    docToDisplay.deliveryPlace,
    document.deliveryPlace,
    document.delivery_place,
    docToDisplay.deliveryAddress,
    document.deliveryAddress,
    document.delivery_address,
  );
  if (dl) {
    docToDisplay.deliveryLocation = dl;
    docToDisplay.deliveryPlace = dl;
    docToDisplay.deliveryAddress = dl;
  }

  const dd = pick(
    docToDisplay.deliveryDate,
    document.deliveryDate,
    document.delivery_date,
  );
  if (dd) docToDisplay.deliveryDate = dd.slice(0, 10);

  const pm = pick(
    docToDisplay.paymentMethod,
    document.paymentMethod,
    document.payment_method,
  );
  if (pm) docToDisplay.paymentMethod = pm;
}

function pickDocStringField(doc, ...keys) {
  if (!doc || typeof doc !== 'object') return '';
  for (const k of keys) {
    const v = doc[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

function resolveCarrierTrackingFromArchiveDoc(doc) {
  if (!doc || typeof doc !== 'object') return '';
  const ui = decodeArchiveDocumentUiOverrides(doc.validityNotes ?? doc.validity_notes);
  return (
    pickDocStringField(
      doc,
      'carrierTrackingNumber',
      'carrier_tracking_number',
      'shipmentCarrierTrackingNumber',
    ) ||
    (ui?.carrierTrackingNumber && String(ui.carrierTrackingNumber).trim()) ||
    (ui?.shipmentCarrierTrackingNumber && String(ui.shipmentCarrierTrackingNumber).trim()) ||
    ''
  );
}

function assignCarrierTrackingFields(target, value) {
  if (!target || !value || !String(value).trim()) return;
  const v = String(value).trim();
  target.shipmentCarrierTrackingNumber = v;
  target.carrierTrackingNumber = v;
  target.carrier_tracking_number = v;
}

/** Reprend champs commerciaux devis / facture proposée pour les conversions Ventes. */
function carryQuoteProformaCommercialFields(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;

  const sales = pickDocStringField(
    sourceDoc,
    'salesPerson',
    'sales_person',
    'commercial',
  );
  if (sales) {
    newDoc.salesPerson = sales;
    newDoc.commercial = sales;
  }

  const realisation = pickDocStringField(
    sourceDoc,
    'deliveryTime',
    'delivery_time',
    'estimatedDelivery',
    'estimated_delivery',
    'estimatedDeliveryTime',
    'estimated_delivery_time',
  );
  if (realisation) {
    newDoc.deliveryTime = realisation;
    newDoc.estimatedDelivery = realisation;
    newDoc.estimatedDeliveryTime = realisation;
    newDoc.estimated_delivery_time = realisation;
  }

  const deliveryDelay = pickDocStringField(
    sourceDoc,
    'deliveryDelay',
    'delivery_delay',
    'desiredDeliveryTime',
    'desired_delivery_time',
  );
  if (deliveryDelay) {
    newDoc.deliveryDelay = deliveryDelay;
    newDoc.desiredDeliveryTime = deliveryDelay;
    newDoc.desired_delivery_time = deliveryDelay;
  }

  const paymentTerms = pickDocStringField(
    sourceDoc,
    'paymentTerms',
    'payment_terms',
    'desiredPaymentTerms',
    'desired_payment_terms',
  );
  if (paymentTerms) {
    newDoc.paymentTerms = paymentTerms;
    newDoc.desiredPaymentTerms = paymentTerms;
    newDoc.desired_payment_terms = paymentTerms;
  }

  const paymentMethod = pickDocStringField(
    sourceDoc,
    'paymentMethod',
    'payment_method',
    'plannedPaymentMethod',
    'planned_payment_method',
  );
  if (paymentMethod) {
    newDoc.paymentMethod = paymentMethod;
    newDoc.payment_method = paymentMethod;
    newDoc.plannedPaymentMethod = paymentMethod;
  }

  const deliveryLoc = pickDocStringField(
    sourceDoc,
    'deliveryLocation',
    'delivery_location',
    'deliveryPlace',
    'delivery_place',
    'deliveryAddress',
    'delivery_address',
    'executionLocation',
    'execution_location',
  );
  if (deliveryLoc) {
    newDoc.deliveryLocation = deliveryLoc;
    newDoc.delivery_location = deliveryLoc;
    newDoc.deliveryPlace = deliveryLoc;
    newDoc.deliveryAddress = deliveryLoc;
  }

  const projectType = pickDocStringField(sourceDoc, 'projectType', 'project_type');
  if (projectType) {
    newDoc.projectType = projectType;
    newDoc.project_type = projectType;
  }

  const projectRef = pickDocStringField(
    sourceDoc,
    'projectReference',
    'project_reference',
    'projectNumber',
    'project_number',
  );
  if (projectRef) {
    newDoc.projectReference = projectRef;
    newDoc.project_reference = projectRef;
    newDoc.projectNumber = projectRef;
    newDoc.project_number = projectRef;
  }
}

function stripInvoiceConversionIrrelevantFields(newDoc) {
  if (!newDoc) return;
  [
    'validUntil',
    'valid_until',
    'acceptanceDeadline',
    'acceptance_deadline',
    'proformaStatus',
    'proforma_status',
    'plannedPaymentMethod',
    'planned_payment_method',
    'downPayment',
    'down_payment',
    'paymentDueDate',
    'payment_due_date',
    'paymentConditions',
    'payment_conditions',
    'conditions',
    'customerOrderReference',
    'customer_order_reference',
    'originalDocumentReference',
    'original_document_reference',
    'paymentTerms',
    'payment_terms',
    'desiredPaymentTerms',
    'desired_payment_terms',
    'priority',
  ].forEach((k) => {
    newDoc[k] = '';
  });
}

function applyInvoicePaymentConditionsFromSalesSource(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;

  const existingPc = pickDocStringField(
    sourceDoc,
    'paymentConditions',
    'payment_conditions',
  );
  const quotePt = pickDocStringField(
    sourceDoc,
    'paymentTerms',
    'payment_terms',
    'desiredPaymentTerms',
    'desired_payment_terms',
  );

  const mappedPc =
    resolveInvoicePaymentConditionsFromArchive(sourceDoc) ||
    mapQuotePaymentTermsToInvoicePaymentConditions(existingPc) ||
    mapQuotePaymentTermsToInvoicePaymentConditions(quotePt);

  if (!mappedPc) return;

  newDoc.paymentConditions = mappedPc;
  newDoc.payment_conditions = mappedPc;
  newDoc.conditions = mappedPc;
}

/** Devis ou facture proforma → facture directe (même règles de reprise / nettoyage). */
function applySalesSourceToInvoiceConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  const srcKey = canonicalArchiveTypeKey(sourceDoc.type);
  if (srcKey !== 'quote' && srcKey !== 'proforma') return;

  if (srcKey === 'proforma') {
    const quoteRef = pickDocStringField(
      sourceDoc,
      'quoteReference',
      'quote_reference',
      'originalDocumentReference',
      'original_document_reference',
    );
    if (quoteRef) {
      newDoc.quoteReference = quoteRef;
      newDoc.quote_reference = quoteRef;
    }
  }

  carryQuoteProformaCommercialFields(newDoc, sourceDoc);
  stripInvoiceConversionIrrelevantFields(newDoc);
  applyInvoicePaymentConditionsFromSalesSource(newDoc, sourceDoc);

  const pm = pickDocStringField(
    sourceDoc,
    'paymentMethod',
    'payment_method',
    'plannedPaymentMethod',
    'planned_payment_method',
  );
  if (pm) {
    newDoc.paymentMethod = pm;
    newDoc.payment_method = pm;
  }
}

/** Devis proposé → Facture proposée */
function applyQuoteToProformaConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  if (canonicalArchiveTypeKey(sourceDoc.type) !== 'quote') return;

  const quoteRef = sourceDoc.number ? String(sourceDoc.number).trim() : '';
  if (quoteRef) {
    newDoc.quoteReference = quoteRef;
    newDoc.quote_reference = quoteRef;
    newDoc.originalDocumentReference = quoteRef;
    newDoc.original_document_reference = quoteRef;
  }

  carryQuoteProformaCommercialFields(newDoc, sourceDoc);

  const pm = pickDocStringField(newDoc, 'paymentMethod', 'plannedPaymentMethod');
  if (pm) {
    newDoc.plannedPaymentMethod = pm;
    newDoc.paymentMethod = pm;
    newDoc.payment_method = pm;
  }

  newDoc.acceptanceDeadline = '';
  newDoc.acceptance_deadline = '';
  newDoc.proformaStatus = '';
}

/** Devis proposé → Facture directe */
function applyQuoteToInvoiceConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  if (canonicalArchiveTypeKey(sourceDoc.type) !== 'quote') return;
  applySalesSourceToInvoiceConversion(newDoc, sourceDoc);
}

/** Facture proposée → Facture directe */
function applyProformaToInvoiceConversion(newDoc, sourceDoc) {
  if (!newDoc || !sourceDoc) return;
  if (canonicalArchiveTypeKey(sourceDoc.type) !== 'proforma') return;
  applySalesSourceToInvoiceConversion(newDoc, sourceDoc);
}

/** BRF Archives / PDF : mappe colonnes SQLite → noms lus par TemplateWrapper (*Ref + RMA). */
function applyReturnSlipArchiveDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  if (canonicalArchiveTypeKey(document.type) !== 'return_slip') return;

  const pick = (...keys) => {
    for (const key of keys) {
      const v = document[key] ?? docToDisplay[key];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const por = pick(
    'returnOriginalPurchaseOrder',
    'return_original_purchase_order',
    'purchaseOrderReference',
    'purchase_order_reference',
  );
  const dor = pick('returnOriginalDelivery', 'return_original_delivery');
  const ivr = pick('returnOriginalInvoice', 'return_original_invoice');
  const ivd = pick('returnOriginalInvoiceDate', 'return_original_invoice_date');
  const rma = pick('returnRMANumber', 'returnRmaNumber', 'return_rma_number');
  const track = pick('returnTrackingNumber', 'return_tracking_number');
  const wh = pick('returnWarehouse', 'return_warehouse');
  const insp = pick(
    'returnInspector',
    'return_inspector',
    'controlledByName',
    'controlled_by_name',
  );
  const cond = pick(
    'returnCondition',
    'return_condition',
    'goodsCondition',
    'goods_condition',
  );

  if (por) {
    docToDisplay.returnOriginalPurchaseOrder = por;
    docToDisplay.returnOriginalPurchaseOrderRef = por;
  }
  if (dor) {
    docToDisplay.returnOriginalDelivery = dor;
    docToDisplay.returnOriginalDeliveryRef = dor;
  }
  if (ivr) {
    docToDisplay.returnOriginalInvoice = ivr;
    docToDisplay.returnOriginalInvoiceRef = ivr;
  }
  if (ivd) {
    docToDisplay.returnOriginalInvoiceDate = ivd;
  }
  if (rma) {
    docToDisplay.returnRMANumber = rma;
    docToDisplay.returnRmaNumber = rma;
  }
  if (track) docToDisplay.returnTrackingNumber = track;
  if (wh) docToDisplay.returnWarehouse = wh;
  if (insp) {
    docToDisplay.returnInspector = insp;
    docToDisplay.controlledByName = insp;
  }
  if (cond) {
    docToDisplay.returnCondition = cond;
    docToDisplay.goodsCondition = cond;
  }
}

/** Bordereau retour client Archives / PDF : colonnes return_* → champs clientReturn* du formulaire / PDF. */
function applyClientReturnSlipArchiveDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  if (canonicalArchiveTypeKey(document.type) !== 'client_return_slip') return;

  const pick = (...keys) => {
    for (const key of keys) {
      const v = document[key] ?? docToDisplay[key];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const por = pick(
    'clientReturnOriginalPurchaseOrderRef',
    'returnOriginalPurchaseOrder',
    'return_original_purchase_order',
  );
  if (por) {
    docToDisplay.clientReturnOriginalPurchaseOrderRef = por;
    docToDisplay.returnOriginalPurchaseOrder = por;
  }

  const dor = pick(
    'clientReturnOriginalDeliveryRef',
    'returnOriginalDelivery',
    'return_original_delivery',
  );
  if (dor) {
    docToDisplay.clientReturnOriginalDeliveryRef = dor;
    docToDisplay.returnOriginalDelivery = dor;
  }

  const ivr = pick(
    'clientReturnOriginalInvoiceRef',
    'returnOriginalInvoice',
    'return_original_invoice',
  );
  if (ivr) {
    docToDisplay.clientReturnOriginalInvoiceRef = ivr;
    docToDisplay.returnOriginalInvoice = ivr;
  }

  const wh = pick('clientReturnWarehouse', 'returnWarehouse', 'return_warehouse');
  if (wh) {
    docToDisplay.clientReturnWarehouse = wh;
    docToDisplay.returnWarehouse = wh;
  }

  const car = pick('clientReturnCarrier', 'returnCarrier', 'return_carrier', 'carrier');
  if (car) {
    docToDisplay.clientReturnCarrier = car;
    docToDisplay.returnCarrier = car;
  }

  const track = pick(
    'clientReturnTrackingNumber',
    'returnTrackingNumber',
    'return_tracking_number',
    'trackingNumber',
    'tracking_number',
  );
  if (track) {
    docToDisplay.clientReturnTrackingNumber = track;
    docToDisplay.returnTrackingNumber = track;
    docToDisplay.trackingNumber = track;
  }

  const cond = pick(
    'clientReturnCondition',
    'returnCondition',
    'return_condition',
    'goodsCondition',
    'goods_condition',
  );
  if (cond) {
    docToDisplay.clientReturnCondition = cond;
    docToDisplay.returnCondition = cond;
    docToDisplay.goodsCondition = cond;
  }

  const rtype = pick('clientReturnType', 'returnType', 'return_type');
  if (rtype) {
    docToDisplay.clientReturnType = rtype;
    docToDisplay.returnType = rtype;
  }

  const motif = pick(
    'clientReturnMotif',
    'clientReturnReason',
    'returnReason',
    'return_reason',
    'motif',
  );
  if (motif) {
    docToDisplay.clientReturnMotif = motif;
    docToDisplay.clientReturnReason = motif;
    docToDisplay.returnReason = motif;
  }

  const ctrl = pick(
    'clientReturnControlledBy',
    'controlledByName',
    'controlled_by_name',
    'returnInspector',
    'return_inspector',
  );
  if (ctrl) {
    docToDisplay.clientReturnControlledBy = ctrl;
    docToDisplay.controlledByName = ctrl;
  }

  const rd = pick('clientReturnReturnDate', 'returnDate', 'return_date', 'date');
  if (rd) {
    docToDisplay.clientReturnReturnDate = rd.slice(0, 10);
    docToDisplay.returnDate = docToDisplay.clientReturnReturnDate;
  }

  const status = pick(
    'clientReturnStatus',
    'returnStatus',
    'return_status',
    'status',
  );
  if (status) {
    docToDisplay.clientReturnStatus = status;
    docToDisplay.returnStatus = status;
    docToDisplay.status = status;
  }

  const issued = pick('issuedBy', 'issued_by', 'createdBy', 'created_by');
  if (issued) {
    docToDisplay.issuedBy = issued;
    docToDisplay.createdBy = issued;
  }

  const clientName = pick('client', 'clientName', 'client_name');
  if (clientName) {
    docToDisplay.client = clientName;
    docToDisplay.clientName = clientName;
  }
}

/** Bon de livraison Archives / PDF : colonnes SQLite → champs lus par TemplateWrapper. */
function applyArchiveDeliveryDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  const typeKey = canonicalArchiveTypeKey(document.type);
  if (typeKey !== 'delivery' && typeKey !== 'material_exit') return;

  const pick = (...vals) => {
    for (const v of vals) {
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const cor = pick(
    docToDisplay.customerOrderReference,
    document.customerOrderReference,
    document.customer_order_reference,
    docToDisplay.documentReference,
    document.documentReference,
    document.document_reference,
  );
  if (cor) {
    docToDisplay.customerOrderReference = cor;
    docToDisplay.documentReference = cor;
  }

  const explicitQuote = pick(
    docToDisplay.quoteReference,
    document.quoteReference,
    document.quote_reference,
  );
  const origDoc = pick(
    docToDisplay.originalDocumentReference,
    document.originalDocumentReference,
    document.original_document_reference,
  );
  const fromInvoice = pick(
    docToDisplay.invoiceReference,
    document.invoiceReference,
    document.invoice_reference,
  );
  let quoteRef = explicitQuote || (origDoc && origDoc !== cor ? origDoc : '');
  if (!explicitQuote && origDoc && !cor && fromInvoice) {
    docToDisplay.customerOrderReference = origDoc;
    docToDisplay.documentReference = origDoc;
    quoteRef = '';
  }
  if (quoteRef) {
    docToDisplay.quoteReference = quoteRef;
  }

  const bc = pick(
    docToDisplay.orderReference,
    document.orderReference,
    document.order_reference,
    docToDisplay.purchaseOrderReference,
    document.purchaseOrderReference,
    document.purchase_order_reference,
  );
  if (bc) {
    docToDisplay.orderReference = bc;
    docToDisplay.purchaseOrderReference = bc;
  }

  const inv = pick(
    docToDisplay.invoiceReference,
    document.invoiceReference,
    document.invoice_reference,
  );
  if (inv) docToDisplay.invoiceReference = inv;

  const wh = pick(
    docToDisplay.departureWarehouse,
    document.departureWarehouse,
    document.departure_warehouse,
    docToDisplay.originWarehouseName,
    document.originWarehouseName,
    document.origin_warehouse_name,
  );
  if (wh) {
    docToDisplay.departureWarehouse = wh;
    docToDisplay.originWarehouseName = wh;
  }

  const addr = pick(
    docToDisplay.deliveryAddress,
    document.deliveryAddress,
    document.delivery_address,
    docToDisplay.deliveryLocation,
    document.deliveryLocation,
    document.delivery_location,
    docToDisplay.deliveryPlace,
    document.deliveryPlace,
    document.delivery_place,
  );
  if (addr) {
    docToDisplay.deliveryAddress = addr;
    docToDisplay.deliveryLocation = addr;
    docToDisplay.deliveryPlace = addr;
  }

  const transp = pick(
    docToDisplay.transporterName,
    document.transporterName,
    document.transporter_name,
    docToDisplay.carrier,
    document.carrier,
  );
  if (transp) {
    docToDisplay.carrier = transp;
    docToDisplay.transporterName = transp;
  }

  const track = pick(
    docToDisplay.trackingNumber,
    document.trackingNumber,
    document.tracking_number,
  );
  if (track) docToDisplay.trackingNumber = track;

  const carrierTrack = pick(
    docToDisplay.carrierTrackingNumber,
    document.carrierTrackingNumber,
    document.carrier_tracking_number,
    document.shipmentCarrierTrackingNumber,
    decodeArchiveDocumentUiOverrides(document.validityNotes ?? document.validity_notes)
      ?.carrierTrackingNumber,
    decodeArchiveDocumentUiOverrides(document.validityNotes ?? document.validity_notes)
      ?.shipmentCarrierTrackingNumber,
  );
  if (carrierTrack) {
    docToDisplay.carrierTrackingNumber = carrierTrack;
    docToDisplay.shipmentCarrierTrackingNumber = carrierTrack;
  }

  const pkgRaw =
    document.shipmentPackageCount ??
    document.packageCount ??
    document.package_count ??
    docToDisplay.shipmentPackageCount;
  const pkgNum = Number(pkgRaw);
  if (Number.isFinite(pkgNum) && pkgNum >= 0) {
    docToDisplay.shipmentPackageCount = pkgNum;
    docToDisplay.packageCount = pkgNum;
  }

  const dm = pick(
    docToDisplay.deliveryMethod,
    document.deliveryMethod,
    docToDisplay.deliveryMode,
    document.deliveryMode,
    document.delivery_mode,
  );
  if (dm) {
    docToDisplay.deliveryMethod = dm;
    docToDisplay.deliveryMode = dm;
  }

  const sched = pick(
    docToDisplay.deliveryDate,
    document.deliveryDate,
    document.delivery_date,
    docToDisplay.expectedDeliveryDate,
    document.expectedDeliveryDate,
    document.expected_delivery_date,
  );
  if (sched) {
    const iso = sched.slice(0, 10);
    docToDisplay.deliveryDate = iso;
    docToDisplay.expectedDeliveryDate = iso;
  }

  const dt = pick(docToDisplay.deliveryTime, document.deliveryTime, document.delivery_time);
  if (dt) docToDisplay.deliveryTime = dt;

  const driverVal = pick(docToDisplay.driver, document.driver);
  if (driverVal) docToDisplay.driver = driverVal;

  const vt = pick(docToDisplay.vehicleType, document.vehicleType, document.vehicle_type);
  if (vt) docToDisplay.vehicleType = vt;

  const vr = pick(
    docToDisplay.vehicleRegistration,
    document.vehicleRegistration,
    document.vehicle_registration,
  );
  if (vr) docToDisplay.vehicleRegistration = vr;

  const dn = pick(
    docToDisplay.deliveryNotes,
    document.deliveryNotes,
    document.delivery_notes,
    docToDisplay.notes,
    document.notes,
  );
  if (dn) docToDisplay.deliveryNotes = dn;
}

/** Bon de réception Archives / PDF : colonnes SQLite → champs formulaire / TemplateWrapper. */
function applyArchiveReceptionDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  const typeKey = canonicalArchiveTypeKey(document.type);
  if (typeKey !== 'reception_note' && typeKey !== 'receipt_note') return;

  const pick = (...vals) => {
    for (const v of vals) {
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const ord = pick(
    docToDisplay.receptionOrderRef,
    document.receptionOrderRef,
    document.receptionOrder,
    document.reception_order,
    docToDisplay.purchaseOrderReference,
    document.purchaseOrderReference,
    document.purchase_order_reference,
  );
  if (ord) {
    docToDisplay.receptionOrderRef = ord;
    docToDisplay.purchaseOrderReference = ord;
  }

  const bl = pick(
    docToDisplay.receptionDeliveryNoteRef,
    document.receptionDeliveryNoteRef,
    document.receptionDeliveryNote,
    document.reception_delivery_note,
    docToDisplay.deliveryNoteReference,
    document.deliveryNoteReference,
    document.delivery_note_reference,
  );
  if (bl) {
    docToDisplay.receptionDeliveryNoteRef = bl;
    docToDisplay.deliveryNoteReference = bl;
  }

  const addr = pick(
    docToDisplay.receiverAddress,
    document.receiverAddress,
    document.receiver_address,
    docToDisplay.receptionAddress,
    document.receptionAddress,
    document.reception_address,
  );
  if (addr) {
    docToDisplay.receiverAddress = addr;
    docToDisplay.receptionAddress = addr;
  }

  const exp = pick(
    docToDisplay.expectedReceptionDate,
    document.expectedReceptionDate,
    document.expected_reception_date,
    docToDisplay.expectedDeliveryDate,
    document.expectedDeliveryDate,
    document.expected_delivery_date,
  );
  if (exp) {
    const iso = exp.slice(0, 10);
    docToDisplay.expectedReceptionDate = iso;
    docToDisplay.expectedDeliveryDate = iso;
  }

  docToDisplay.receptionTime = pick(
    docToDisplay.receptionTime,
    document.receptionTime,
    document.reception_time,
  );
  docToDisplay.receptionDate = pick(
    docToDisplay.receptionDate,
    document.receptionDate,
    document.reception_date,
    document.date,
  );
  docToDisplay.receptionControlledBy = pick(
    docToDisplay.receptionControlledBy,
    document.receptionControlledBy,
    document.controlledByName,
    document.controlled_by_name,
  );
  docToDisplay.receptionCarrier = pick(
    docToDisplay.receptionCarrier,
    document.receptionCarrier,
    document.carrier,
  );
  docToDisplay.receptionWarehouse = pick(
    docToDisplay.receptionWarehouse,
    document.receptionWarehouse,
    document.warehouseName,
    document.warehouse_name,
  );
  docToDisplay.receptionOverallCondition = pick(
    docToDisplay.receptionOverallCondition,
    document.receptionOverallCondition,
    document.generalCondition,
    document.general_condition,
  );
  docToDisplay.receptionQuantityCompliance = pick(
    docToDisplay.receptionQuantityCompliance,
    document.receptionQuantityCompliance,
    document.quantityConformity,
    document.quantity_conformity,
  );
  docToDisplay.receptionQualityCompliance = pick(
    docToDisplay.receptionQualityCompliance,
    document.receptionQualityCompliance,
    document.qualityConformity,
    document.quality_conformity,
  );
  docToDisplay.receptionStockDestination = pick(
    docToDisplay.receptionStockDestination,
    document.receptionStockDestination,
    document.stockDestination,
    document.stock_destination,
  );
  docToDisplay.receptionStatus = pick(
    docToDisplay.receptionStatus,
    document.receptionStatus,
    document.reception_status,
  );
}

/** Bon d'entrée stock Archives / PDF. */
function applyArchiveStockEntryDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  if (canonicalArchiveTypeKey(document.type) !== 'stock_entry') return;

  const pick = (...vals) => {
    for (const v of vals) {
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const wh = pick(
    docToDisplay.stockEntryWarehouse,
    document.stockEntryWarehouse,
    document.warehouseName,
    document.warehouse_name,
  );
  if (wh) {
    docToDisplay.stockEntryWarehouse = wh;
    docToDisplay.warehouseName = wh;
  }

  const entryDt = pick(
    docToDisplay.stockEntryEntryDate,
    document.stockEntryEntryDate,
    document.entryDate,
    document.entry_date,
  );
  if (entryDt) {
    docToDisplay.stockEntryEntryDate = entryDt.slice(0, 10);
    docToDisplay.expectedEntryDate = docToDisplay.stockEntryEntryDate;
    docToDisplay.entryDate = docToDisplay.stockEntryEntryDate;
  }

  docToDisplay.stockEntryEntryTime = pick(
    docToDisplay.stockEntryEntryTime,
    document.stockEntryEntryTime,
    document.entryTime,
    document.entry_time,
  );
  docToDisplay.stockEntrySource = pick(
    docToDisplay.stockEntrySource,
    document.stockEntrySource,
    document.entrySource,
    document.entry_source,
  );
  docToDisplay.stockEntryDocumentReference = pick(
    docToDisplay.stockEntryDocumentReference,
    document.stockEntryDocumentReference,
    document.entryDocumentReference,
    document.entry_document_reference,
  );
  docToDisplay.stockEntrySupplier = pick(
    docToDisplay.stockEntrySupplier,
    document.stockEntrySupplier,
    document.supplierName,
    document.supplier_name,
    document.client,
  );
  docToDisplay.stockEntryReceivedBy = pick(
    docToDisplay.stockEntryReceivedBy,
    document.stockEntryReceivedBy,
    document.receivedByName,
    document.received_by_name,
  );
  docToDisplay.stockEntryControlledBy = pick(
    docToDisplay.stockEntryControlledBy,
    document.stockEntryControlledBy,
    document.controlledByName,
    document.controlled_by_name,
  );
  docToDisplay.stockEntryCondition = pick(
    docToDisplay.stockEntryCondition,
    document.stockEntryCondition,
    document.goodsCondition,
    document.goods_condition,
  );
  docToDisplay.stockEntryPurchaseOrderRef = pick(
    docToDisplay.stockEntryPurchaseOrderRef,
    document.stockEntryPurchaseOrderRef,
    document.purchaseOrderReference,
    document.purchase_order_reference,
  );
  docToDisplay.stockEntryReceptionNoteRef = pick(
    docToDisplay.stockEntryReceptionNoteRef,
    document.stockEntryReceptionNoteRef,
    document.deliveryNoteReference,
    document.delivery_note_reference,
  );
  docToDisplay.stockEntryTransferNoteRef = pick(
    docToDisplay.stockEntryTransferNoteRef,
    document.stockEntryTransferNoteRef,
    document.originalDocumentReference,
    document.original_document_reference,
  );
}

/** Bon de sortie Archives / PDF. */
function applyArchiveExitSlipDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  const typeKey = canonicalArchiveTypeKey(document.type);
  if (typeKey !== 'exit_slip' && typeKey !== 'exit_note') return;

  const pick = (...vals) => {
    for (const v of vals) {
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  docToDisplay.exitNoteClientOrderRef = pick(
    docToDisplay.exitNoteClientOrderRef,
    document.exitNoteClientOrderRef,
    document.purchaseOrderReference,
    document.purchase_order_reference,
  );
  docToDisplay.exitNoteInvoiceRef = pick(
    docToDisplay.exitNoteInvoiceRef,
    document.exitNoteInvoiceRef,
    document.invoiceReference,
    document.invoice_reference,
  );
  docToDisplay.exitNoteDeliveryNoteRef = pick(
    docToDisplay.exitNoteDeliveryNoteRef,
    document.exitNoteDeliveryNoteRef,
    document.deliveryNoteReference,
    document.delivery_note_reference,
  );
  docToDisplay.exitNoteTransferNoteRef = pick(
    docToDisplay.exitNoteTransferNoteRef,
    document.exitNoteTransferNoteRef,
    document.originalDocumentReference,
    document.original_document_reference,
  );
  docToDisplay.exitNoteDocumentReference = pick(
    docToDisplay.exitNoteDocumentReference,
    document.exitNoteDocumentReference,
    document.entryDocumentReference,
    document.entry_document_reference,
  );
  docToDisplay.exitNoteOriginWarehouse = pick(
    docToDisplay.exitNoteOriginWarehouse,
    document.exitNoteOriginWarehouse,
    document.originWarehouseName,
    document.origin_warehouse_name,
  );
  docToDisplay.exitNoteDestinationWarehouse = pick(
    docToDisplay.exitNoteDestinationWarehouse,
    document.exitNoteDestinationWarehouse,
    document.destinationWarehouseName,
    document.destination_warehouse_name,
  );
  docToDisplay.exitNoteDestination = pick(
    docToDisplay.exitNoteDestination,
    document.exitNoteDestination,
    document.deliveryAddress,
    document.delivery_address,
  );
  docToDisplay.exitNoteTransportMode = pick(
    docToDisplay.exitNoteTransportMode,
    document.exitNoteTransportMode,
    document.deliveryMode,
    document.delivery_mode,
  );
  docToDisplay.exitNoteAuthorizedBy = pick(
    docToDisplay.exitNoteAuthorizedBy,
    document.exitNoteAuthorizedBy,
    document.approvedByName,
    document.approved_by_name,
  );
  docToDisplay.exitNoteHandler = pick(
    docToDisplay.exitNoteHandler,
    document.exitNoteHandler,
    document.transferResponsibleName,
    document.transfer_responsible_name,
  );
  docToDisplay.exitNoteExitTime = pick(
    docToDisplay.exitNoteExitTime,
    document.exitNoteExitTime,
    document.departureTime,
    document.departure_time,
  );
  docToDisplay.exitNoteRecipient = pick(
    docToDisplay.exitNoteRecipient,
    document.exitNoteRecipient,
    document.receivedByName,
    document.received_by_name,
  );
  docToDisplay.exitNoteExpectedDate = pick(
    docToDisplay.exitNoteExpectedDate,
    document.exitNoteExpectedDate,
    document.expectedDeliveryDate,
    document.expected_delivery_date,
    document.date,
  );
}

/** Bon de transfert Archives / PDF. */
function applyArchiveTransferSlipDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  const typeKey = canonicalArchiveTypeKey(document.type);
  if (typeKey !== 'transfer_slip' && typeKey !== 'transfer_note') return;

  const pick = (...vals) => {
    for (const v of vals) {
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  docToDisplay.transferNoteOriginWarehouse = pick(
    docToDisplay.transferNoteOriginWarehouse,
    document.transferNoteOriginWarehouse,
    document.originWarehouseName,
    document.origin_warehouse_name,
  );
  docToDisplay.transferNoteDestinationWarehouse = pick(
    docToDisplay.transferNoteDestinationWarehouse,
    document.transferNoteDestinationWarehouse,
    document.destinationWarehouseName,
    document.destination_warehouse_name,
  );
  docToDisplay.transferNoteDocumentReference = pick(
    docToDisplay.transferNoteDocumentReference,
    document.transferNoteDocumentReference,
    document.entryDocumentReference,
    document.entry_document_reference,
  );
  docToDisplay.transferNoteTransferRequestRef = pick(
    docToDisplay.transferNoteTransferRequestRef,
    document.transferNoteTransferRequestRef,
    document.originalDocumentReference,
    document.original_document_reference,
  );
  docToDisplay.transferNoteRequestedBy = pick(
    docToDisplay.transferNoteRequestedBy,
    document.transferNoteRequestedBy,
    document.transferRequestedByName,
    document.transfer_requested_by_name,
  );
  docToDisplay.transferNoteHandler = pick(
    docToDisplay.transferNoteHandler,
    document.transferNoteHandler,
    document.transferResponsibleName,
    document.transfer_responsible_name,
  );
  docToDisplay.transferNoteApprovedBy = pick(
    docToDisplay.transferNoteApprovedBy,
    document.transferNoteApprovedBy,
    document.approvedByName,
    document.approved_by_name,
  );
  docToDisplay.transferNoteTransportMode = pick(
    docToDisplay.transferNoteTransportMode,
    document.transferNoteTransportMode,
    document.shipmentMode,
    document.shipment_mode,
    document.deliveryMode,
  );
  docToDisplay.transferNoteDepartureTime = pick(
    docToDisplay.transferNoteDepartureTime,
    document.transferNoteDepartureTime,
    document.departureTime,
    document.departure_time,
  );
  docToDisplay.transferNoteArrivalTime = pick(
    docToDisplay.transferNoteArrivalTime,
    document.transferNoteArrivalTime,
    document.arrivalTime,
    document.arrival_time,
  );
  docToDisplay.transferNoteTrackingNumber = pick(
    docToDisplay.transferNoteTrackingNumber,
    document.transferNoteTrackingNumber,
    document.trackingNumber,
    document.tracking_number,
  );
  docToDisplay.transferNoteExpectedDate = pick(
    docToDisplay.transferNoteExpectedDate,
    document.transferNoteExpectedDate,
    document.expectedDeliveryDate,
    document.expected_delivery_date,
    document.date,
  );
}

/** Bordereau d'envoi Archives / PDF. */
function applyArchiveShippingSlipDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  const typeKey = canonicalArchiveTypeKey(document.type);
  if (typeKey !== 'shipping' && typeKey !== 'shipping_slip') return;

  const pick = (...vals) => {
    for (const v of vals) {
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  docToDisplay.shipmentDate = pick(
    docToDisplay.shipmentDate,
    document.shipmentDate,
    document.shipment_date,
    document.date,
  );
  docToDisplay.shipmentOriginWarehouse = pick(
    docToDisplay.shipmentOriginWarehouse,
    document.shipmentOriginWarehouse,
    document.originWarehouseName,
    document.origin_warehouse_name,
  );
  docToDisplay.deliveryLocation = pick(
    docToDisplay.deliveryLocation,
    document.deliveryLocation,
    document.delivery_location,
    document.deliveryAddress,
    document.delivery_address,
    document.lieu_livraison,
  );
  if (docToDisplay.deliveryLocation) {
    docToDisplay.deliveryAddress = docToDisplay.deliveryLocation;
  }
  docToDisplay.shipmentMethod = pick(
    docToDisplay.shipmentMethod,
    document.shipmentMethod,
    document.shipmentMode,
    document.shipment_mode,
    docToDisplay.deliveryMethod,
    document.deliveryMethod,
    document.delivery_method,
    document.delivery_mode,
  );
  docToDisplay.shipmentCarrier = pick(
    docToDisplay.shipmentCarrier,
    document.shipmentCarrier,
    document.carrier,
  );
  docToDisplay.shipmentCarrierTrackingNumber = pick(
    docToDisplay.shipmentCarrierTrackingNumber,
    document.shipmentCarrierTrackingNumber,
    document.carrierTrackingNumber,
    document.carrier_tracking_number,
    decodeArchiveDocumentUiOverrides(document.validityNotes ?? document.validity_notes)
      ?.shipmentCarrierTrackingNumber,
    decodeArchiveDocumentUiOverrides(document.validityNotes ?? document.validity_notes)
      ?.carrierTrackingNumber,
  );
  if (docToDisplay.shipmentCarrierTrackingNumber) {
    docToDisplay.carrierTrackingNumber = docToDisplay.shipmentCarrierTrackingNumber;
    docToDisplay.carrier_tracking_number = docToDisplay.shipmentCarrierTrackingNumber;
  }
  docToDisplay.shipmentTrackingNumber = pick(
    docToDisplay.shipmentTrackingNumber,
    document.shipmentTrackingNumber,
    document.trackingNumber,
    document.tracking_number,
  );
  const issuedBy = pick(
    docToDisplay.issuedBy,
    document.issuedBy,
    document.issued_by,
    document.createdBy,
    document.created_by,
    decodeArchiveDocumentUiOverrides(document.validityNotes ?? document.validity_notes)
      ?.etablieParName,
  );
  if (issuedBy) {
    docToDisplay.issuedBy = issuedBy;
    if (!docToDisplay.etablieParName) {
      docToDisplay.etablieParName = issuedBy;
    }
  }

  docToDisplay.shipmentClientOrderRef = pick(
    docToDisplay.shipmentClientOrderRef,
    document.shipmentClientOrderRef,
    document.purchaseOrderReference,
    document.purchase_order_reference,
  );
  docToDisplay.shipmentInvoiceRef = pick(
    docToDisplay.shipmentInvoiceRef,
    document.shipmentInvoiceRef,
    document.invoiceReference,
    document.invoice_reference,
  );
  docToDisplay.shipmentDeliveryNoteRef = pick(
    docToDisplay.shipmentDeliveryNoteRef,
    document.shipmentDeliveryNoteRef,
    document.deliveryNoteReference,
    document.delivery_note_reference,
  );
  const pkgRaw =
    document.shipmentPackageCount ??
    document.packageCount ??
    document.package_count;
  const pkgNum = Number(pkgRaw);
  if (Number.isFinite(pkgNum) && pkgNum >= 0) {
    docToDisplay.shipmentPackageCount = pkgNum;
    docToDisplay.packageCount = pkgNum;
  }
  const wt = Number(document.weight ?? document.shipmentTotalWeight ?? document.totalWeight);
  if (Number.isFinite(wt) && wt >= 0) {
    docToDisplay.shipmentTotalWeight = wt;
    docToDisplay.weight = wt;
  }
  const vol = Number(document.volume ?? document.shipmentVolume);
  if (Number.isFinite(vol) && vol >= 0) {
    docToDisplay.shipmentVolume = vol;
    docToDisplay.volume = vol;
  }
  const dv = Number(document.declaredValue ?? document.shipmentDeclaredValue);
  if (Number.isFinite(dv) && dv >= 0) {
    docToDisplay.shipmentDeclaredValue = dv;
    docToDisplay.declaredValue = dv;
  }
  docToDisplay.shipmentPackageType = pick(
    docToDisplay.shipmentPackageType,
    document.shipmentPackageType,
    document.packageType,
    document.package_type,
  );
  docToDisplay.shipmentProductType = pick(
    docToDisplay.shipmentProductType,
    document.shipmentProductType,
    document.productType,
    document.product_type,
  );
}

/** Avoir Archives / PDF : colonnes SQLite credit_type / refund_method → champs TemplateWrapper. */
function applyCreditNoteArchiveDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  const typeKey = canonicalArchiveTypeKey(document.type);
  if (typeKey !== 'credit_note') return;

  const pick = (...keys) => {
    for (const key of keys) {
      const v = document[key] ?? docToDisplay[key];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const cnType = pick('creditNoteType', 'creditType', 'credit_type');
  if (cnType) {
    docToDisplay.creditNoteType = cnType;
    docToDisplay.creditType = cnType;
    docToDisplay.credit_note_type = cnType;
    docToDisplay.creditNote = {
      ...(docToDisplay.creditNote && typeof docToDisplay.creditNote === 'object'
        ? docToDisplay.creditNote
        : {}),
      type: cnType,
    };
  }

  const refund = pick(
    'creditNoteRefundMethod',
    'refundMethod',
    'refund_method',
  );
  if (refund) {
    docToDisplay.creditNoteRefundMethod = refund;
    docToDisplay.refundMethod = refund;
    docToDisplay.refund_method = refund;
    docToDisplay.creditNote = {
      ...(docToDisplay.creditNote && typeof docToDisplay.creditNote === 'object'
        ? docToDisplay.creditNote
        : {}),
      refundMethod: refund,
    };
  }

  const oin = pick('originalInvoiceNumber', 'original_invoice_number');
  if (oin) {
    docToDisplay.originalInvoiceNumber = oin;
    docToDisplay.original_invoice_number = oin;
  }
  const oid = pick('originalInvoiceDate', 'original_invoice_date');
  if (oid) {
    docToDisplay.originalInvoiceDate = oid;
    docToDisplay.original_invoice_date = oid;
  }
  const oamtRaw =
    document.originalInvoiceAmount ??
    document.original_invoice_amount ??
    docToDisplay.originalInvoiceAmount;
  const oamt = Number(oamtRaw);
  if (Number.isFinite(oamt) && oamt > 0) {
    docToDisplay.originalInvoiceAmount = oamt;
    docToDisplay.original_invoice_amount = oamt;
  }

  const party = pick('creditNoteParty', 'credit_note_party');
  if (party) {
    docToDisplay.creditNoteParty = party;
    docToDisplay.credit_note_party = party;
  }

  const reason = pick(
    'creditNoteReason',
    'credit_note_reason',
    'motif',
    'notes',
  );
  if (reason) {
    docToDisplay.creditNoteReason = reason;
    docToDisplay.credit_note_reason = reason;
  }

  const cnAmt = Number(
    document.creditNoteAmount ??
      document.credit_note_amount ??
      document.amount ??
      docToDisplay.creditNoteAmount,
  );
  if (Number.isFinite(cnAmt) && cnAmt >= 0) {
    docToDisplay.creditNoteAmount = cnAmt;
    docToDisplay.credit_note_amount = cnAmt;
    docToDisplay.amount = cnAmt;
  }
  const cnVat = Number(document.creditNoteVatAmount ?? document.credit_note_vat_amount);
  if (Number.isFinite(cnVat)) {
    docToDisplay.creditNoteVatAmount = cnVat;
  }
  const cnTotal = Number(
    document.creditNoteTotalAmount ??
      document.credit_note_total_amount ??
      document.totalTtc ??
      document.total_ttc,
  );
  if (Number.isFinite(cnTotal) && cnTotal >= 0) {
    docToDisplay.creditNoteTotalAmount = cnTotal;
    docToDisplay.totalTtc = cnTotal;
    docToDisplay.total_ttc = cnTotal;
  }
  const cnVatRate = Number(document.creditNoteVatRate ?? document.credit_note_vat_rate);
  if (Number.isFinite(cnVatRate)) {
    docToDisplay.creditNoteVatRate = cnVatRate;
  }

  const appDate = pick(
    'creditNoteApplicationDate',
    'credit_note_application_date',
    'orderDate',
    'order_date',
  );
  const resolvedAppDate = appDate || pick('date') || '';
  if (resolvedAppDate) {
    docToDisplay.creditNoteApplicationDate = resolvedAppDate;
    docToDisplay.credit_note_application_date = resolvedAppDate;
    docToDisplay.orderDate = resolvedAppDate;
    docToDisplay.order_date = resolvedAppDate;
    docToDisplay.creditNote = {
      ...(docToDisplay.creditNote && typeof docToDisplay.creditNote === 'object'
        ? docToDisplay.creditNote
        : {}),
      applicationDate: resolvedAppDate,
    };
  }
}

/** Bon de versement Archives / PDF : colonnes SQLite → champs formulaire / TemplateWrapper. */
function applyArchivePaymentVoucherDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  const typeKey = canonicalArchiveTypeKey(document.type);
  if (typeKey !== 'payment_voucher' && typeKey !== 'payment_slip') return;

  const pick = (...keys) => {
    for (const key of keys) {
      const v = document[key] ?? docToDisplay[key];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const lines =
    (Array.isArray(document.payments) && document.payments.length
      ? document.payments
      : null) ||
    (Array.isArray(document.paymentLines) && document.paymentLines.length
      ? document.paymentLines
      : null) ||
    (Array.isArray(docToDisplay.payments) && docToDisplay.payments.length
      ? docToDisplay.payments
      : null) ||
    (Array.isArray(docToDisplay.paymentLines) && docToDisplay.paymentLines.length
      ? docToDisplay.paymentLines
      : null);
  if (lines?.length) {
    docToDisplay.paymentLines = lines;
    docToDisplay.payments = lines;
  }

  const due = pick('expectedCashingDate', 'dueDate', 'due_date');
  if (due) {
    const iso = due.slice(0, 10);
    docToDisplay.expectedCashingDate = iso;
    docToDisplay.dueDate = iso;
    docToDisplay.due_date = iso;
  }

  const reason = pick('paymentReason', 'motif');
  if (reason) {
    docToDisplay.paymentReason = formatPaymentVoucherReason(reason) || reason;
    docToDisplay.motif = docToDisplay.paymentReason;
  }

  const txn = pick('transactionNumber', 'transaction_number');
  if (txn) docToDisplay.transactionNumber = txn;

  const pm = pick('paymentMethod', 'payment_method');
  if (pm) docToDisplay.paymentMethod = pm;

  const payAmtRaw =
    document.paymentAmount ??
    document.payment_amount ??
    docToDisplay.paymentAmount ??
    document.amount;
  const payAmt = Number(payAmtRaw);
  if (Number.isFinite(payAmt) && payAmt >= 0) {
    docToDisplay.paymentAmount = payAmt;
    docToDisplay.payment_amount = payAmt;
    if (!docToDisplay.amount) docToDisplay.amount = payAmt;
  }

  const ib = pick('issuerBank', 'bankName', 'bank_name');
  if (ib) {
    docToDisplay.issuerBank = ib;
    docToDisplay.bankName = ib;
  }

  const rb = pick('receiverBank', 'beneficiaryName', 'beneficiary_name');
  if (rb) {
    docToDisplay.receiverBank = rb;
    docToDisplay.beneficiaryName = rb;
  }

  const inv = pick('relatedInvoices', 'invoicesPaid', 'invoices_paid');
  if (inv) {
    docToDisplay.relatedInvoices = inv;
    docToDisplay.invoicesPaid = inv;
  }

  const cnRef = pick(
    'supplierCreditNoteRef',
    'originalDocumentReference',
    'original_document_reference',
  );
  if (cnRef) {
    docToDisplay.supplierCreditNoteRef = cnRef;
    docToDisplay.originalDocumentReference = cnRef;
    docToDisplay.original_document_reference = cnRef;
  }

  const poRef = pick(
    'purchaseOrderRef',
    'purchaseOrderReference',
    'purchase_order_reference',
    'orderReference',
    'order_reference',
  );
  if (poRef) {
    docToDisplay.purchaseOrderRef = poRef;
    docToDisplay.purchaseOrderReference = poRef;
    docToDisplay.purchase_order_reference = poRef;
  }

  docToDisplay.documentReference = '';
  docToDisplay.document_reference = '';
  docToDisplay.quoteRequestRef = '';
  docToDisplay.proformaRequestRef = '';

  docToDisplay.items = [];
}

/** Reçu de paiement Archives / PDF : colonnes SQLite → champs formulaire / TemplateWrapper. */
function applyArchivePaymentReceiptDisplayFields(docToDisplay, document) {
  if (!docToDisplay || !document) return;
  const typeKey = canonicalArchiveTypeKey(document.type);
  if (typeKey !== 'payment_receipt') return;

  const pick = (...keys) => {
    for (const key of keys) {
      const v = document[key] ?? docToDisplay[key];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const lines =
    (Array.isArray(document.payments) && document.payments.length ? document.payments : null) ||
    (Array.isArray(document.paymentLines) && document.paymentLines.length ? document.paymentLines : null) ||
    (Array.isArray(docToDisplay.payments) && docToDisplay.payments.length ? docToDisplay.payments : null) ||
    (Array.isArray(docToDisplay.paymentLines) && docToDisplay.paymentLines.length ? docToDisplay.paymentLines : null);
  if (lines?.length) {
    docToDisplay.paymentLines = lines;
    docToDisplay.payments = lines;
  }

  const payDate = pick('paymentDate', 'payment_date');
  if (payDate) {
    docToDisplay.paymentDate = payDate.slice(0, 10);
    docToDisplay.payment_date = docToDisplay.paymentDate;
  }

  const txn = pick('transactionNumber', 'transaction_number', 'paymentReference', 'payment_reference');
  if (txn) {
    docToDisplay.paymentReference = txn;
    docToDisplay.transactionNumber = txn;
    docToDisplay.transaction_number = txn;
  }

  const pm = pick('paymentMethod', 'payment_method');
  if (pm) docToDisplay.paymentMethod = pm;

  const bank = pick('paymentBank', 'bankName', 'bank_name');
  if (bank) {
    docToDisplay.paymentBank = bank;
    docToDisplay.bankName = bank;
    docToDisplay.bank_name = bank;
  }

  const settlement = pick('paymentSettlementType', 'paymentType', 'payment_type');
  if (settlement) {
    docToDisplay.paymentSettlementType = settlement;
    docToDisplay.paymentType = settlement;
    docToDisplay.payment_type = settlement;
  }

  const receivedBy = pick('receiptReceivedBy', 'receivedBy', 'received_by');
  if (receivedBy) {
    docToDisplay.receiptReceivedBy = receivedBy;
    docToDisplay.receivedBy = receivedBy;
    docToDisplay.received_by = receivedBy;
  }

  const inv = pick('paymentInvoiceNumbers', 'invoicesPaid', 'invoices_paid');
  if (inv) {
    docToDisplay.paymentInvoiceNumbers = inv;
    docToDisplay.invoicesPaid = inv;
    docToDisplay.invoices_paid = inv;
  }

  const cnRef = pick('clientCreditNoteRef', 'originalDocumentReference', 'original_document_reference');
  if (cnRef) {
    docToDisplay.clientCreditNoteRef = cnRef;
    docToDisplay.originalDocumentReference = cnRef;
    docToDisplay.original_document_reference = cnRef;
  }

  const payAmtRaw =
    document.paymentAmount ?? document.payment_amount ?? docToDisplay.paymentAmount ?? document.amount;
  const payAmt = Number(payAmtRaw);
  if (Number.isFinite(payAmt) && payAmt >= 0) {
    docToDisplay.paymentAmount = payAmt;
    docToDisplay.payment_amount = payAmt;
    if (!docToDisplay.amount) docToDisplay.amount = payAmt;
  }

  const initAmt = Number(document.initialInvoiceAmount ?? document.initial_invoice_amount);
  if (Number.isFinite(initAmt) && initAmt >= 0) {
    docToDisplay.paymentOriginalAmount = initAmt;
    docToDisplay.initialInvoiceAmount = initAmt;
  }

  const remBal = Number(document.remainingBalance ?? document.remaining_balance);
  if (Number.isFinite(remBal) && remBal >= 0) {
    docToDisplay.paymentRemainingBalance = remBal;
    docToDisplay.remainingBalance = remBal;
  }

  docToDisplay.items = [];
}

const ARCHIVE_UI_NOTE_PREFIX = 'QAWAEM_UI_JSON:';

function decodeArchiveDocumentUiOverrides(note) {
  const s = String(note ?? '');
  if (!s.startsWith(ARCHIVE_UI_NOTE_PREFIX)) return null;
  try {
    return JSON.parse(s.slice(ARCHIVE_UI_NOTE_PREFIX.length));
  } catch {
    return null;
  }
}

/** Restaure texte complémentaire, coches sections et signatures pour l'aperçu Archives / PDF. */
function applyArchiveSupplementaryAndUiOverrides(docToDisplay, document) {
  if (!docToDisplay || typeof docToDisplay !== 'object') return;

  const typeKey = String(document.type ?? '')
    .trim()
    .replace(/-/g, '_')
    .toLowerCase();

  if (typeKey === 'quote_request' || typeKey === 'proforma_request') {
    const reqText =
      document.requirements ||
      document.notes ||
      docToDisplay.requirements ||
      docToDisplay.notes ||
      '';
    if (reqText && String(reqText).trim()) {
      docToDisplay.requirements = String(reqText).trim();
    }
  }

  const dec = decodeArchiveDocumentUiOverrides(
    document.validityNotes ?? document.validity_notes,
  );
  if (dec) {
    if (Array.isArray(dec.sectionOrder) && dec.sectionOrder.length > 0) {
      docToDisplay.sectionOrder = dec.sectionOrder;
    }

    const boolUiKeys = [
      'showNotes',
      'showConditions',
      'showObjectif',
      'showObservations',
      'showGaranties',
      'showValidite',
      'showMotif',
      'showInstructions',
      'showEcheancier',
      'showSupplementarySections',
      'showBonPour',
      'showEtabliePar',
      'showRecuePar',
      'showTva',
      'showTap',
      'showTimbre',
      'showRemise',
      'showRabais',
      'showEscompte',
      'showTransport',
      'showRetour',
      'showAcompte',
      'showAvance',
      'showGarantie',
      'showIrg',
      'showIbs',
    ];
    boolUiKeys.forEach((k) => {
      if (typeof dec[k] === 'boolean') docToDisplay[k] = dec[k];
    });

    const taxNumKeys = [
      'tvaRate',
      'tapRate',
      'remise',
      'rabais',
      'escompte',
      'transport',
      'retour',
      'acompte',
      'avance',
      'garantie',
      'irg',
      'ibs',
    ];
    taxNumKeys.forEach((k) => {
      if (dec[k] != null && dec[k] !== '' && !Number.isNaN(Number(dec[k]))) {
        docToDisplay[k] = Number(dec[k]);
      }
    });
    if (Array.isArray(dec.taxOrder) && dec.taxOrder.length > 0) {
      docToDisplay.taxOrder = dec.taxOrder;
    }

    const strUiKeys = [
      'etablieParName',
      'etablieParRole',
      'etablieParDate',
      'etablieParPlace',
      'recueParName',
      'recueParRole',
      'recueParDate',
      'recueParPlace',
    ];
    strUiKeys.forEach((k) => {
      if (dec[k] != null && String(dec[k]).trim() !== '') docToDisplay[k] = dec[k];
    });
    if (dec.invoiceType != null && String(dec.invoiceType).trim() !== '') {
      docToDisplay.invoiceType = String(dec.invoiceType).trim();
    }
  } else if (docToDisplay.issuedBy || docToDisplay.createdBy) {
    const typeKey = canonicalArchiveTypeKey(document.type);
    const inlineEtabliePar =
      typeKey === 'shipping' || typeKey === 'shipping_slip';
    if (!inlineEtabliePar) {
      docToDisplay.showEtabliePar = true;
    }
    if (!docToDisplay.etablieParName) {
      docToDisplay.etablieParName = docToDisplay.issuedBy || docToDisplay.createdBy;
    }
    if (!docToDisplay.etablieParDate && docToDisplay.date) {
      docToDisplay.etablieParDate = docToDisplay.date;
    }
  }

  if (docToDisplay.showEtabliePar) {
    if (!docToDisplay.etablieParName && (docToDisplay.issuedBy || docToDisplay.createdBy)) {
      docToDisplay.etablieParName = docToDisplay.issuedBy || docToDisplay.createdBy;
    }
    if (!docToDisplay.etablieParDate && docToDisplay.date) {
      docToDisplay.etablieParDate = docToDisplay.date;
    }
  }

  if (typeKey === 'shipping' || typeKey === 'shipping_slip') {
    docToDisplay.showEtabliePar = false;
  }

  const pickArchiveTaxNum = pickArchiveNum;

  if (docToDisplay.showTva === undefined) {
    docToDisplay.showTva = resolveShowTvaFromArchive(document);
  }
  if (docToDisplay.tvaRate === undefined) {
    const rate = pickArchiveTaxNum(document.tvaRate, document.tva_rate);
    docToDisplay.tvaRate = rate != null ? rate : 19;
  }

  if (docToDisplay.showTap === undefined) {
    const tapAmt = pickArchiveTaxNum(document.totalTap, document.total_tap);
    docToDisplay.showTap = tapAmt != null && tapAmt > 0;
  }
  if (docToDisplay.showTap && docToDisplay.tapRate === undefined) {
    const rate = pickArchiveTaxNum(document.tapRate, document.tap_rate);
    if (rate != null && rate > 0) {
      docToDisplay.tapRate = rate;
    } else {
      const ht = pickArchiveTaxNum(document.totalHt, document.total_ht, document.subtotal);
      const tapAmt = pickArchiveTaxNum(document.totalTap, document.total_tap);
      if (ht != null && ht > 0 && tapAmt != null && tapAmt > 0) {
        docToDisplay.tapRate = Math.round((tapAmt / ht) * 10000) / 100;
      }
    }
  }

  const inferArchiveTaxToggle = (showKey, valueKey, ...amountSources) => {
    if (docToDisplay[showKey] !== undefined) return;
    const amt = pickArchiveTaxNum(...amountSources);
    if (amt != null && amt > 0) {
      docToDisplay[showKey] = true;
      if (docToDisplay[valueKey] === undefined) docToDisplay[valueKey] = amt;
    }
  };

  inferArchiveTaxToggle('showTransport', 'transport', document.transportCost, document.transport_cost);
  inferArchiveTaxToggle('showRemise', 'remise', document.discountAmount, document.discount_amount);
  inferArchiveTaxToggle('showEscompte', 'escompte', document.earlyPaymentDiscount, document.early_payment_discount);
  inferArchiveTaxToggle('showAcompte', 'acompte', document.downPayment, document.down_payment);
  inferArchiveTaxToggle('showRetour', 'retour', document.returnCost, document.return_cost);
  inferArchiveTaxToggle('showGarantie', 'garantie', document.guarantee, document.garantie);
  const stampAmt = pickArchiveTaxNum(document.stampDuty, document.stamp_duty, document.stamp);
  if (docToDisplay.showTimbre === undefined && stampAmt != null && stampAmt > 0) {
    docToDisplay.showTimbre = true;
  }
}

const getArchiveStatusIcon = (statusKey, color) => {
  const size = 14;
  const style = { color };

  if (statusKey === 'paid') return <CheckCircleIcon size={size} style={style} />;
  if (statusKey === 'partially_paid') return <DollarSign size={size} style={style} />;
  if (statusKey === 'pending') return <ClockIcon size={size} style={style} />;
  if (statusKey === 'overdue') return <ExclamationTriangleIcon size={size} style={style} />;
  if (statusKey === 'draft') return <FileText size={size} style={style} />;
  if (statusKey === 'partial') return <PackagePlus size={size} style={style} />;
  if (statusKey === 'accepted') return <PlusCircle size={size} style={style} />;
  if (statusKey === 'rejected') return <FileX size={size} style={style} />;
  if (statusKey === 'approved') return <FileCheck size={size} style={style} />;
  if (statusKey === 'negotiating') return <ArrowRightLeft size={size} style={style} />;
  if (statusKey === 'expired') return <CalendarIcon size={size} style={style} />;
  if (statusKey === 'delivered') return <Truck size={size} style={style} />;
  if (statusKey === 'in_transit') return <Package size={size} style={style} />;
  if (statusKey === 'shipped') return <Send size={size} style={style} />;
  if (statusKey === 'received') return <Download size={size} style={style} />;
  if (statusKey === 'canceled') return <X size={size} style={style} />;
  if (statusKey === 'cancelled') return <X size={size} style={style} />;
  if (statusKey === 'processed') return <Settings size={size} style={style} />;
  if (statusKey === 'sent') return <ArrowRight size={size} style={style} />;
  if (statusKey === 'answered') return <Receipt size={size} style={style} />;
  if (statusKey === 'processing') return <RefreshCw size={size} style={style} />;
  if (statusKey === 'completed') return <CheckSquare size={size} style={style} />;
  if (statusKey === 'verified') return <Eye size={size} style={style} />;
  if (statusKey === 'issued') return <FileOutput size={size} style={style} />;
  if (statusKey === 'applied') return <Save size={size} style={style} />;
  if (statusKey === 'refunded') return <Banknote size={size} style={style} />;
  if (statusKey === 'transferred') return <RefreshCcw size={size} style={style} />;
  if (statusKey === 'consolidated') return <ClipboardList size={size} style={style} />;

  return <FileText size={size} style={style} />;
};

const computeArchiveStatusDropdownPosition = (buttonEl) => {
  const rect = buttonEl.getBoundingClientRect();
  const dropdownWidth = 200;
  const dropdownHeight = 300;

  let left = rect.left + rect.width / 2;
  let top = rect.bottom + 8;

  if (left + dropdownWidth / 2 > window.innerWidth - 20) {
    left = window.innerWidth - dropdownWidth - 20;
  }

  if (left - dropdownWidth / 2 < 20) {
    left = dropdownWidth / 2 + 20;
  }

  if (top + dropdownHeight > window.innerHeight - 20) {
    top = rect.top - dropdownHeight - 8;
  }

  return { top, left };
};

const ArchivesClickableStatusBadge = React.memo(function ArchivesClickableStatusBadge({
  document: doc,
  isMenuOpen,
  onToggleMenu,
  statusConfig,
  resolveArchiveDocTypeKeyForStatuses,
  getStatusLabelForDocumentType,
  clickChangeStatusTitle,
}) {
  const buttonRef = useRef(null);
  const archiveTypeKey = resolveArchiveDocTypeKeyForStatuses(doc);
  const uiStatusKey = mapArchiveDbStatusToUiStatus(archiveTypeKey, doc.status);
  const statusMeta =
    statusConfig[uiStatusKey] ||
    statusConfig[doc.status] || {
      label: doc.status || '\u2014',
      color: '#64748b',
    };
  const statusDisplayLabel =
    getStatusLabelForDocumentType(doc.type, uiStatusKey) ||
    getStatusLabelForDocumentType(doc.type, doc.status) ||
    statusMeta.label;

  return (
    <div className="status-container relative inline-block">
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleMenu(doc, buttonRef.current);
        }}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border cursor-pointer transition-all hover:opacity-80"
        style={{
          backgroundColor: `${statusMeta.color}26`,
          color: statusMeta.color,
          borderColor: `${statusMeta.color}44`,
        }}
        title={clickChangeStatusTitle}
      >
        <div className="flex-shrink-0">{getArchiveStatusIcon(uiStatusKey, statusMeta.color)}</div>
        <span>{statusDisplayLabel}</span>
        <ChevronDown size={12} className={`transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
      </button>
    </div>
  );
});

const ArchivesStatusDropdownOverlay = ({
  document: doc,
  position,
  orderedStatuses,
  statusConfig,
  resolveArchiveDocTypeKeyForStatuses,
  getStatusLabelForDocumentType,
  onStatusChange,
  changeStatusTitle,
}) => {
  if (!doc || !position) return null;

  const archiveTypeKey = resolveArchiveDocTypeKeyForStatuses(doc);
  const uiStatusKey = mapArchiveDbStatusToUiStatus(archiveTypeKey, doc.status);

  return (
    <div
      key={`archives-status-menu-${doc.id}`}
      className="status-dropdown border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden min-w-[200px] transform -translate-x-1/2"
      style={{
        position: 'fixed',
        zIndex: 99999,
        top: `${position.top}px`,
        left: `${position.left}px`,
        backgroundImage:
          'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
        boxShadow:
          '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)',
      }}
    >
      <div className="p-2">
        <div className="px-4 py-2 text-xs font-semibold text-white/70 border-b border-white/10">
          {changeStatusTitle}
        </div>
        {orderedStatuses.map((key) => {
          const st = statusConfig[key];
          if (!st) return null;
          const rowLabel = getStatusLabelForDocumentType(doc.type, key) || st.label;
          return (
            <button
              key={key}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onStatusChange(doc, key);
              }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left rounded-xl ${
                uiStatusKey === key ? 'bg-blue-500/25 shadow-inner' : ''
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {getArchiveStatusIcon(key, st.color)}
              </div>
              <span className="text-white text-sm font-medium">{rowLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ArchivesFacturation = ({ moduleScope = ARCHIVE_MODULE_FACTURATION, sectionConfig = null }) => {
  const facturationConfig = useFacturationConfig();
  const documentsPerPage = resolveFacturationPageSize(facturationConfig);
  const isSectionArchives = Boolean(sectionConfig);
  const isInventaireArchives = moduleScope === ARCHIVE_MODULE_INVENTAIRE;
  const isLogistiqueArchives = moduleScope === ARCHIVE_MODULE_LOGISTIQUE;
  const documentCreationBasePath = isInventaireArchives
    ? '/inventaire/document-creation'
    : isLogistiqueArchives
      ? '/logistique/document-creation'
      : '/document-creation';
  const documentCreationEditFromQuery = isInventaireArchives
    ? '&from=inventaire'
    : isLogistiqueArchives
      ? '&from=logistique'
      : sectionConfig
        ? `&from=${encodeURIComponent(sectionConfig.route.replace(/^\//, ''))}`
        : '';
  const { isDashboardLightMode } = useGlobalTheme();
  const { t, i18n: i18nInstance } = useTranslation();
  const p = (key, opts) => t(`modules.facturation.pages.archivesFacturation.${key}`, opts);
  const formatUserDate = (value, options) => {
    if (value == null || value === '') return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(i18nInstance.language || i18n.language || 'ar', options);
  };
  const formatUserDateTime = (value) => {
    if (value == null || value === '') return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleString(i18nInstance.language || i18n.language || 'ar');
  };
  const getDocumentDisplayTitle = (doc) => resolveArchiveDocumentTitle(doc, i18nInstance.language);
  const getPreviewDocumentTitle = (doc) =>
    resolveArchiveDocumentTitle(doc, i18nInstance.language, ARCHIVE_TITLE_PREVIEW_MAX_LEN) ||
    doc?.number ||
    '';
  const translateDocumentType = useDocumentTypeTranslator();
  const { documents, addDocument, updateDocument, deleteDocument } = useDocuments();
  const { settings } = useSettings();
  const { clients, suppliers } = useClients();
  const { sales, purchases, getOrders } = useAppData();
  const location = useLocation();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [mainViewMode, setMainViewMode] = useState('all_documents');
  const [activeTab, setActiveTab] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [viewMode, setViewMode] = useState(() => facturationConfig.defaultView || 'table'); // 'cards' or 'table'

  useEffect(() => {
    setViewMode(facturationConfig.defaultView || 'table');
  }, [facturationConfig.defaultView]);

  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false); // Status filter dropdown
  const statusFilterRef = useRef(null); // Ref for status filter
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false); // Date range picker dropdown
  const dateRangeRef = useRef(null); // Ref for date range picker
  const [isImportOpen, setIsImportOpen] = useState(false); // Import dropdown
  const [isExportOpen, setIsExportOpen] = useState(false); // Export dropdown
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false); // Options Avancées toggle
  const importRef = useRef(null);
  const exportRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Bulk selection state
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  
  // Helper function to find client data for a document
  const findClientData = (document) => {
    let clientData = null;

    const typeKey = canonicalArchiveTypeKey(document.type);
    const isSupplierSideDoc =
      document.isSupplier ||
      document.is_supplier ||
      document.creditNoteParty === 'supplier' ||
      document.credit_note_party === 'supplier' ||
      (canonicalArchiveTypeKey(document.type) === 'credit_note' &&
        isSupplierCreditNoteDocument(document)) ||
      typeKey === 'payment_voucher' ||
      typeKey === 'payment_slip' ||
      typeKey === 'purchase_order' ||
      document.type?.includes('purchase') ||
      document.type?.includes('quote-request');

    const matchSupplierRecord = (supplier) => {
      if (!supplier) return false;
      const label = String(
        document.client ||
          document.supplierName ||
          document.supplier_name ||
          '',
      ).trim();
      return (
        String(supplier.id) === String(document.supplierId || document.supplier_id || '') ||
        String(supplier.id) === String(document.clientId || document.client_id || '') ||
        (label &&
          (supplier.name === label ||
            supplier.company_name === label ||
            supplier.company_name === document.supplierName ||
            supplier.company_name === document.supplier_name))
      );
    };

    // For supplier documents, search in suppliers first
    if (isSupplierSideDoc) {
      if (document.supplierId || document.supplier_id) {
        const sid = document.supplierId || document.supplier_id;
        clientData = suppliers.find((supplier) => String(supplier.id) === String(sid));
        if (clientData) return clientData;
      }

      if (document.clientId) {
        clientData = suppliers.find((supplier) => String(supplier.id) === String(document.clientId));
        if (clientData) return clientData;
      }

      if (document.client || document.supplierName || document.supplier_name) {
        clientData = suppliers.find(matchSupplierRecord);
        if (clientData) return clientData;
      }
    }
    
    // For client documents or fallback, search in clients
    // Try to find by ID first (more reliable)
    const clientId = document.clientId || document.client_id;
    const clientField = String(document.client || '').trim();
    if (clientId) {
      clientData = clients.find((client) => String(client.id) === String(clientId));
      if (clientData) {
        return clientData;
      }
    }
    if (looksLikeArchivePartnerId(clientField)) {
      clientData = clients.find(
        (client) =>
          String(client.id) === clientField ||
          String(client.client_number || client.clientNumber || '') === clientField,
      );
      if (clientData) {
        return clientData;
      }
    }

    // Fallback to name-based search in clients
    const clientNameHint = String(
      document.clientName || document.client_name || document.client || '',
    ).trim();
    if (!isArchiveClientLabelPlaceholder(clientNameHint)) {
      clientData = clients.find(
        (client) =>
          client.name === clientNameHint ||
          client.company_name === clientNameHint ||
          client.companyName === clientNameHint,
      );
      if (clientData) {
        return clientData;
      }
    }

    return clientData;
  };

  const normalizeArchiveClientCategory = (typeRaw) => {
    const t = String(typeRaw || '').trim().toLowerCase();
    if (!t) return null;
    if (t === 'entreprise' || t === 'company') return 'entreprise';
    if (t === 'particulier' || t === 'individual') return 'particulier';
    if (t === 'institution') return 'institution';
    return null;
  };

  const inferArchiveClientCategoryFromName = (clientName) => {
    if (!clientName) return null;
    const name = String(clientName);
    if (
      name.includes('SARL') ||
      name.includes('SPA') ||
      name.includes('EURL') ||
      name.includes('Entreprise') ||
      name.includes('Soci\u00e9t\u00e9') ||
      name.includes('Company') ||
      name.includes('Clinique') ||
      name.includes('H\u00f4pital') ||
      name.includes('Centre')
    ) {
      return 'entreprise';
    }
    if (
      name.includes('Université') ||
      name.includes('École') ||
      name.includes('Institut') ||
      name.includes('Ministry') ||
      name.includes('Ministère') ||
      name.includes('Administration') ||
      name.includes('Gouvernement') ||
      name.includes('Mairie') ||
      name.includes('Wilaya')
    ) {
      return 'institution';
    }
    return null;
  };

  const resolveArchiveDocumentClientCategory = (doc) => {
    const clientData = findClientData(doc);
    const fromRecord = normalizeArchiveClientCategory(
      clientData?.client_type ||
        clientData?.type ||
        doc.client_type ||
        doc.clientType,
    );
    if (fromRecord) return fromRecord;
    return inferArchiveClientCategoryFromName(doc.client) || 'particulier';
  };

  // Helper function to format partner number using same logic as DocumentCreation
  const formatPartnerNumber = (partner, isSupplier) => {
    if (!partner) return '';
    const stored = resolvePartnerReferenceCode(partner, { isSupplier });
    if (stored) return stored;

    if (isSupplier) {
      const supplierIndex = suppliers.findIndex((s) => s.id === partner.id);
      const number = String(supplierIndex >= 0 ? supplierIndex + 1 : 1).padStart(2, '0');
      return `FRN-${number}`;
    }

    let clientIndex = clients.findIndex((c) => c.id === partner.id);
    if (clientIndex < 0) {
      clientIndex = suppliers.findIndex((s) => s.id === partner.id);
    }
    const number = String(clientIndex >= 0 ? clientIndex + 1 : 1).padStart(2, '0');
    return `CLI-${number}`;
  };
  
  // Preview modal state
  const [previewDocument, setPreviewDocument] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  useFacturationModalBodyLock(showPreviewModal);
  
  // Download status
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  
  // Document to be exported as PDF
  const [documentToPrint, setDocumentToPrint] = useState(null);

  // Status management state
  // State for status dropdown positioning
  const [activeStatusMenu, setActiveStatusMenu] = useState(null);
  const [statusDropdownPosition, setStatusDropdownPosition] = useState(null);

  // Toast notification state
  const [toast, setToast] = useState({ visible: false, message: '', status: '' });

  // Define valid facturation document types (excluding inventory and other modules)
  const FACTURATION_DOCUMENT_TYPES = [
    'invoice', 'proforma', 'quote', 'quote_request', 'quote-request',
    'proforma_request', 'proforma-request', 'purchase_order', 'purchase-order',
    'delivery', 'delivery-note', 'material-exit', 'material_exit',
    'shipping', 'shipping-slip',
    'return-slip', 'return_slip', 'reception-note', 'reception_note',
    'receipt-note', 'receipt_note', 'credit-note', 'credit_note',
    'payment-slip', 'payment_slip', 'payment-voucher', 'payment_voucher',
    'payment-receipt', 'payment_receipt',
    'client-return-slip', 'client_return_slip',
    'exit-slip', 'exit_slip', 'outbound-slip', 'outbound_slip',
    'transfer-slip', 'transfer_slip'
  ];

  const INVENTORY_ARCHIVE_TYPES = [
    'reception_note', 'reception-note', 'receipt_note', 'receipt-note',
    'stock_entry', 'stock-entry', 'exit_note', 'exit-note',
    'exit_slip', 'exit-slip', 'outbound_slip', 'outbound-slip',
    'transfer_note', 'transfer-note', 'transfer_slip', 'transfer-slip',
  ];

  const LOGISTICS_ARCHIVE_TYPES = [
    'shipping', 'shipping-slip', 'shipping_slip',
  ];

  // Filter documents by module (Facturation vs Inventaire vs Logistique) + types reconnus.
  const facturationDocuments = useMemo(() => {
    const typeKeys =
      moduleScope === ARCHIVE_MODULE_INVENTAIRE
        ? INVENTORY_ARCHIVE_TYPES
        : moduleScope === ARCHIVE_MODULE_LOGISTIQUE
          ? LOGISTICS_ARCHIVE_TYPES
          : FACTURATION_DOCUMENT_TYPES;
    const typeSet = new Set(
      typeKeys.flatMap((k) => {
        const s = String(k);
        return [s, s.replace(/-/g, '_'), s.replace(/_/g, '-')];
      }),
    );

    const matchesArchiveType = (raw) => {
      if (!raw) return false;
      const x = String(raw).trim();
      if (!x) return false;
      if (typeSet.has(x)) return true;
      if (typeSet.has(x.replace(/-/g, '_'))) return true;
      if (typeSet.has(x.replace(/_/g, '-'))) return true;
      return false;
    };

    return documents
      .filter((doc) => doc?.type && matchesArchiveType(doc.type))
      .filter((doc) => documentBelongsToArchiveModule(doc, moduleScope))
      .sort(compareDocumentsNewestFirst);
  }, [documents, moduleScope]);

  const scopedDocuments = useMemo(() => {
    if (!sectionConfig) return facturationDocuments;
    return filterDocumentsForSection(facturationDocuments, sectionConfig);
  }, [facturationDocuments, sectionConfig]);

  // Improve the documentTypes definitions to ensure proper document type display
  const docLabel = (type, pageKey) => (pageKey ? p(pageKey) : translateDocumentType(type));
  const documentTypes = {
    // VENTES - GREEN #10b981
    invoice: { 
      label: docLabel('invoice'), 
      icon: Receipt, 
      color: '#10b981', 
      financialImpact: 'positive', 
      allowedStatuses: ['pending', 'draft', 'paid', 'partially_paid', 'overdue', 'cancelled'],
      category: 'financial'
    },
    proforma: { 
      label: docLabel('proforma'), 
      icon: FileCheck, 
      color: '#10b981', 
      financialImpact: 'positive', 
      allowedStatuses: ['pending', 'draft', 'paid', 'partially_paid', 'overdue', 'cancelled'],
      category: 'financial'
    },
    quote: { 
      label: docLabel('quote'), 
      icon: DollarSign, 
      color: '#10b981', 
      financialImpact: 'neutral', 
      allowedStatuses: ['pending', 'draft', 'accepted', 'rejected', 'negotiating', 'expired'],
      category: 'preparatory'
    },
    // ACHATS - LIGHT BLUE #60a5fa
    quote_request: { 
      label: docLabel('quote_request'), 
      icon: ClipboardList, 
      color: '#60a5fa', 
      financialImpact: 'neutral', 
      allowedStatuses: ['pending', 'draft', 'rejected', 'expired', 'sent', 'answered'],
      category: 'preparatory'
    },
    proforma_request: { 
      label: docLabel('proforma_request'), 
      icon: FileSearch, 
      color: '#60a5fa', 
      financialImpact: 'neutral', 
      allowedStatuses: ['pending', 'draft', 'rejected', 'expired', 'sent', 'answered'],
      category: 'preparatory'
    },
    purchase_order: { 
      label: docLabel('purchase_order'), 
      icon: ShoppingCart, 
      color: '#60a5fa', 
      financialImpact: 'negative', 
      allowedStatuses: ['pending', 'draft', 'paid', 'partially_paid', 'overdue', 'cancelled'],
      category: 'financial'
    },
    delivery: { 
      label: docLabel('delivery'), 
      icon: Truck, 
      color: '#10b981', 
      financialImpact: 'neutral', 
      allowedStatuses: ['pending', 'draft', 'partial', 'delivered', 'in_transit', 'canceled'],
      category: 'logistic'
    },
    'material-exit': { 
      label: docLabel('delivery'), 
      icon: Truck, 
      color: '#10b981', 
      financialImpact: 'neutral', 
      allowedStatuses: ['pending', 'draft', 'partial', 'delivered', 'in_transit', 'canceled'],
      category: 'logistic'
    },
    material_exit: { 
      label: docLabel('delivery'), 
      icon: Truck, 
      color: '#10b981', 
      financialImpact: 'neutral', 
      allowedStatuses: ['pending', 'draft', 'partial', 'delivered', 'in_transit', 'canceled'],
      category: 'logistic'
    },
    shipping: { 
      label: docLabel('shipping', 'shipping_expedition'), 
      icon: FileText, 
      color: '#84cc16', 
      financialImpact: 'neutral', 
      allowedStatuses: ['pending', 'shipped', 'delivered'],
      category: 'logistic'
    },
    'return-slip': { 
      label: docLabel('return_slip', 'supplier_return_slip'), 
      icon: ArrowLeft, 
      color: '#60a5fa', 
      financialImpact: 'negative', 
      allowedStatuses: ['pending', 'processing', 'received', 'accepted', 'rejected', 'processed', 'refunded', 'canceled'],
      category: 'logistic'
    },
    // Also support hyphenated format for backward compatibility
    'quote-request': { 
      label: docLabel('quote_request'), 
      icon: ClipboardList, 
      color: '#60a5fa', 
      financialImpact: 'neutral', 
      allowedStatuses: ['pending', 'draft', 'rejected', 'expired', 'sent', 'answered'],
      category: 'preparatory'
    },
    'proforma-request': { 
      label: docLabel('proforma_request'), 
      icon: FileSearch, 
      color: '#60a5fa', 
      financialImpact: 'neutral', 
      allowedStatuses: ['pending', 'draft', 'rejected', 'expired', 'sent', 'answered'],
      category: 'preparatory'
    },
    'purchase-order': { 
      label: docLabel('purchase_order'), 
      icon: ShoppingCart, 
      color: '#60a5fa', 
      financialImpact: 'negative', 
      allowedStatuses: ['pending', 'draft', 'paid', 'partially_paid', 'overdue', 'cancelled'],
      category: 'financial'
    },
    'delivery-note': { 
      label: docLabel('delivery'), 
      icon: Truck, 
      color: '#10b981', 
      financialImpact: 'neutral', 
      allowedStatuses: ['pending', 'draft', 'partial', 'delivered', 'in_transit', 'canceled'],
      category: 'logistic'
    },
    'shipping-slip': { 
      label: docLabel('shipping', 'shipping_expedition'), 
      icon: FileText, 
      color: '#84cc16', 
      financialImpact: 'neutral', 
      allowedStatuses: ['pending', 'shipped', 'delivered'],
      category: 'logistic'
    },
    // Additional document types for complete conversion support
    receipt_note: {
      label: docLabel('receipt_note'), 
      icon: Package,
      color: '#10b981',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'partial', 'received'],
      category: 'logistic'
    },
    return_slip: {
      label: docLabel('return_slip', 'supplier_return_slip'), 
      icon: ArrowLeft,
      color: '#60a5fa',
      financialImpact: 'negative',
      allowedStatuses: ['pending', 'processing', 'received', 'accepted', 'rejected', 'processed', 'refunded', 'canceled'],
      category: 'logistic'
    },
    shipping_slip: {
      label: docLabel('shipping_slip'), 
      icon: Send,
      color: '#06b6d4',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'shipped', 'delivered'],
      category: 'logistic'
    },
    payment_slip: {
      label: docLabel('payment_voucher'), 
      icon: Banknote,
      color: '#60a5fa',
      financialImpact: 'positive',
      allowedStatuses: ['pending', 'paid', 'processing'],
      category: 'financial'
    },
    'payment-slip': {
      label: docLabel('payment_voucher'), 
      icon: Banknote,
      color: '#60a5fa',
      financialImpact: 'positive',
      allowedStatuses: ['pending', 'paid', 'processing'],
      category: 'financial'
    },
    payment_voucher: {
      label: docLabel('payment_voucher'), 
      icon: Banknote,
      color: '#60a5fa',
      financialImpact: 'positive',
      allowedStatuses: ['pending', 'paid', 'processing'],
      category: 'financial'
    },
    'payment-voucher': {
      label: docLabel('payment_voucher'), 
      icon: Banknote,
      color: '#60a5fa',
      financialImpact: 'positive',
      allowedStatuses: ['pending', 'paid', 'processing'],
      category: 'financial'
    },
    payment_receipt: {
      label: docLabel('payment_receipt'), 
      icon: CreditCard,
      color: '#10b981',
      financialImpact: 'positive',
      allowedStatuses: ['pending', 'completed', 'verified'],
      category: 'financial'
    },
    credit_note: {
      label: CREDIT_NOTE_LABEL_CLIENT,
      icon: FileX,
      color: '#10b981',
      financialImpact: 'negative',
      allowedStatuses: ['pending', 'issued', 'applied'],
      category: 'financial'
    },
    'client-return-slip': {
      label: docLabel('client_return_slip', 'client_return_slip_label'), 
      icon: Undo2,
      color: '#10b981',
      financialImpact: 'negative',
      allowedStatuses: ['pending', 'received', 'processed', 'refunded'],
      category: 'logistic'
    },
    client_return_slip: {
      label: docLabel('client_return_slip', 'client_return_slip_label'), 
      icon: Undo2,
      color: '#10b981',
      financialImpact: 'negative',
      allowedStatuses: ['pending', 'received', 'processed', 'refunded'],
      category: 'logistic'
    },
    exit_slip: {
      label: docLabel('exit_slip', 'stock_exit'), 
      icon: Package,
      color: '#8b5cf6',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'canceled', 'processed'],
      category: 'logistic'
    },
    transfer_slip: {
      label: docLabel('transfer_slip', 'transfer_slip'), 
      icon: RefreshCw,
      color: '#06b6d4',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'canceled', 'transferred'],
      category: 'logistic'
    },
    stock_entry: {
      label: docLabel('stock_entry', 'stock_entry'), 
      icon: Package,
      color: '#06b6d4',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'received', 'verified'],
      category: 'logistic'
    },
    'stock-entry': {
      label: docLabel('stock_entry', 'stock_entry'), 
      icon: Package,
      color: '#06b6d4',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'received', 'verified'],
      category: 'logistic'
    },
    // Add a default entry for any undefined document types
    // Add support for hyphenated variants to cover all conversion cases
    'reception-note': {
      label: docLabel('receipt_note'), 
      icon: Package,
      color: '#10b981',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'partial', 'received'],
      category: 'logistic'
    },
    'reception_note': {
      label: docLabel('receipt_note'), 
      icon: Package,
      color: '#10b981',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'partial', 'received'],
      category: 'logistic'
    },
    'receipt-note': {
      label: docLabel('receipt_note'), 
      icon: Package,
      color: '#10b981',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'partial', 'received'],
      category: 'logistic'
    },
    'credit-note': {
      label: CREDIT_NOTE_LABEL_CLIENT,
      icon: FileX,
      color: '#10b981',
      financialImpact: 'negative',
      allowedStatuses: ['pending', 'issued', 'applied'],
      category: 'financial'
    },
    'payment-receipt': {
      label: docLabel('payment_receipt'), 
      icon: CreditCard,
      color: '#10b981',
      financialImpact: 'positive',
      allowedStatuses: ['pending', 'completed', 'verified'],
      category: 'financial'
    },
    'client-return-slip': {
      label: docLabel('client_return_slip', 'client_return_slip_label'), 
      icon: Undo2,
      color: '#10b981',
      financialImpact: 'negative',
      allowedStatuses: ['pending', 'received', 'processed', 'refunded'],
      category: 'logistic'
    },
    exit_slip: {
      label: docLabel('exit_slip', 'stock_exit'), 
      icon: Package,
      color: '#8b5cf6',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'canceled', 'processed'],
      category: 'logistic'
    },
    'exit-slip': {
      label: docLabel('exit_slip', 'stock_exit'), 
      icon: Package,
      color: '#8b5cf6',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'canceled', 'processed'],
      category: 'logistic'
    },
    /** Alias (conversionMatrix + legacy) — même affichage que exit_slip */
    outbound_slip: {
      label: docLabel('exit_slip', 'stock_exit'), 
      icon: Package,
      color: '#8b5cf6',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'canceled', 'processed'],
      category: 'logistic'
    },
    'outbound-slip': {
      label: docLabel('exit_slip', 'stock_exit'), 
      icon: Package,
      color: '#8b5cf6',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'canceled', 'processed'],
      category: 'logistic'
    },
    transfer_slip: {
      label: docLabel('transfer_slip', 'transfer_slip'), 
      icon: RefreshCw,
      color: '#14b8a6',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'canceled', 'transferred'],
      category: 'logistic'
    },
    'transfer-slip': {
      label: docLabel('transfer_slip', 'transfer_slip'), 
      icon: RefreshCw,
      color: '#14b8a6',
      financialImpact: 'neutral',
      allowedStatuses: ['pending', 'canceled', 'transferred'],
      category: 'logistic'
    },
    default: {
      label: docLabel('default', 'document_type'), 
      icon: FileText, 
      color: '#64748b', 
      financialImpact: 'neutral', 
      // Matches DocumentCreation getStatusOptions fallback order
      allowedStatuses: ['pending', 'draft', 'sent', 'approved', 'rejected', 'cancelled'],
      category: 'other'
    }
  };

  /**
   * Same resolution as row label/icon (`displayTypeKey` + hyphen/underscore),
   * so status menu matches Nouveau Document for payment coercions etc.
   */
  const resolveArchiveDocTypeKeyForStatuses = (doc) => {
    if (!doc) return 'default';
    const displayTypeKey = resolvePaymentDocumentDisplayType(doc);
    const candidates = [
      displayTypeKey,
      displayTypeKey != null && String(displayTypeKey).replace(/_/g, '-'),
      doc.type,
      doc.type != null && String(doc.type).replace(/_/g, '-'),
      doc.documentType,
    ].filter((x) => x != null && x !== '');

    for (const k of candidates) {
      if (documentTypes[k]) return k;
      const hyphen = String(k).replace(/_/g, '-');
      if (hyphen !== k && documentTypes[hyphen]) return hyphen;
      const unders = String(k).replace(/-/g, '_');
      if (unders !== k && documentTypes[unders]) return unders;
    }

    return doc.type || 'default';
  };

  /** Map canonical DB status → clé utilisée dans les menus Archives (shared with DocumentCreation). */
  // mapArchiveDbStatusToUiStatus imported from facturationArchiveStatusOrder.js

  /**
   * Filtre « Statut » : la pastille utilise des clés UI (negotiating, answered…)
   * alors que SQLite garde processing, completed, overdue, etc.
   */
  const archiveDocMatchesStatusFilter = (doc, filterKey) => {
    if (filterKey == null || filterKey === '' || filterKey === 'all') return true;
    const fk = String(filterKey).trim().toLowerCase();
    const raw =
      doc?.status != null && String(doc.status).trim() !== ''
        ? String(doc.status).trim().toLowerCase()
        : '';
    if (!raw) return false;
    if (raw === fk) return true;

    const archiveKey = resolveArchiveDocTypeKeyForStatuses(doc);
    const uiMapped = mapArchiveDbStatusToUiStatus(archiveKey, raw);
    const ui =
      uiMapped != null && String(uiMapped).trim() !== ''
        ? String(uiMapped).trim().toLowerCase()
        : '';
    return ui === fk;
  };

  /** Accent-insensitive substring match for status labels (“negociation” ≈ “négociation”). */
  const normalizeArchiveSearchKey = (s) =>
    String(s)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  /**
   * Recherche : alignée sur ce que l’utilisateur voit (pastille / filtre Statut).
   * Inclut clés SQLite, clés UI mappées, libellés i18n + spécifiques type.
   */
  const archiveDocMatchesTextSearch = (doc, term) => {
    const trimmed = term != null ? String(term).trim() : '';
    if (!trimmed) return true;

    const needlePlain = trimmed.toLowerCase();
    const needleNorm = normalizeArchiveSearchKey(trimmed);

    const title = doc.title != null ? String(doc.title).toLowerCase() : '';
    const num = doc.number != null ? String(doc.number).toLowerCase() : '';
    const cli = doc.client != null ? String(doc.client).toLowerCase() : '';

    if (title.includes(needlePlain) || num.includes(needlePlain) || cli.includes(needlePlain)) {
      return true;
    }

    const ark = resolveArchiveDocTypeKeyForStatuses(doc);
    const uiKey = mapArchiveDbStatusToUiStatus(ark, doc.status);

    const keys = new Set();
    if (doc.status != null && String(doc.status).trim() !== '') keys.add(String(doc.status).trim());
    if (uiKey != null && String(uiKey).trim() !== '') keys.add(String(uiKey).trim());

    for (const key of keys) {
      const kNorm = normalizeArchiveSearchKey(key);

      const labelCandidates = [];
      const rowLbl = getStatusLabelForDocumentType(doc.type, key);
      if (rowLbl) labelCandidates.push(rowLbl);

      const st = statusConfig[key];
      const cfgLbl = typeof st?.label === 'string' ? st.label : '';
      if (cfgLbl && cfgLbl !== rowLbl) labelCandidates.push(cfgLbl);

      for (const lbl of labelCandidates) {
        if (!lbl) continue;
        if (normalizeArchiveSearchKey(lbl).includes(needleNorm)) return true;
      }

      const variants = [key, kNorm, key.replace(/_/g, '-'), key.replace(/-/g, '_')];
      if (variants.some((v) => v && normalizeArchiveSearchKey(v).includes(needleNorm))) return true;
    }

    return false;
  };

  /** Onglets rapides Facturation : même logique que filtre Statut pour paid / pending / overdue. */
  const archiveDocMatchesFinancialQuickTab = (doc, tab) => {
    if (tab !== 'paid' && tab !== 'pending' && tab !== 'overdue') return true;
    const fk = tab;
    if (fk === 'overdue') {
      const ark = resolveArchiveDocTypeKeyForStatuses(doc);
      const ui = mapArchiveDbStatusToUiStatus(ark, doc.status);
      /** Devis demandes / proforma request : retard DB “overdue”, pastille “Expiré”. */
      if (ui === 'expired') return true;
    }
    return archiveDocMatchesStatusFilter(doc, fk);
  };

  const getOrderedArchiveAllowedStatuses = (doc) => {
    const archiveTypeKey = resolveArchiveDocTypeKeyForStatuses(doc);
    const docTypeInfo =
      documentTypes[archiveTypeKey] || documentTypes[doc.type] || documentTypes.default;
    const raw = docTypeInfo.allowedStatuses || ['draft', 'pending', 'completed'];
    return reorderStatusesLikeDocumentCreation(archiveTypeKey, raw);
  };

  // Conversion matrix - defines available conversions for each document type
  const INV_EXIT_TO_TRANSFER = [
    { target: 'transfer_slip', label: docLabel('transfer_slip', 'transfer_slip'), icon: RefreshCw, color: 'text-teal-400' },
  ];
  const INV_TRANSFER_TARGETS = [
    { target: 'receipt_note', label: docLabel('receipt_note'), icon: Package, color: 'text-green-400' },
    { target: 'stock_entry', label: docLabel('stock_entry', 'stock_entry'), icon: PackagePlus, color: 'text-orange-400' },
  ];
  const INV_RECEPTION_TARGETS = [
    { target: 'return_slip', label: docLabel('return_slip', 'return_slip_short'), icon: Undo2, color: 'text-red-400' },
    { target: 'stock_entry', label: docLabel('stock_entry', 'stock_entry'), icon: PackagePlus, color: 'text-orange-400' },
  ];
  const INV_TERMINAL = [];

  const conversionMatrix = {
    // ====== CYCLE D'ACHAT (ACHATS) ======
    'quote_request': [
      { target: 'purchase_order', label: docLabel('purchase_order'), icon: ClipboardList, color: 'text-yellow-400' }
    ],
    'quote-request': [
      { target: 'purchase_order', label: docLabel('purchase_order'), icon: ClipboardList, color: 'text-yellow-400' }
    ],
    'proforma_request': [
      { target: 'purchase_order', label: docLabel('purchase_order'), icon: ClipboardList, color: 'text-yellow-400' }
    ],
    'proforma-request': [
      { target: 'purchase_order', label: docLabel('purchase_order'), icon: ClipboardList, color: 'text-yellow-400' }
    ],
    'purchase_order': [
      { target: 'receipt_note', label: docLabel('receipt_note'), icon: Package, color: 'text-green-400' },
      { target: 'payment_voucher', label: docLabel('payment_voucher'), icon: CreditCard, color: 'text-green-400' }
    ],
    'purchase-order': [
      { target: 'receipt_note', label: docLabel('receipt_note'), icon: Package, color: 'text-green-400' },
      { target: 'payment_voucher', label: docLabel('payment_voucher'), icon: CreditCard, color: 'text-green-400' }
    ],
    'receipt_note': INV_RECEPTION_TARGETS,
    'reception_note': INV_RECEPTION_TARGETS,
    'reception-note': INV_RECEPTION_TARGETS,
    'receipt-note': INV_RECEPTION_TARGETS,
    'return_slip': [
      { target: 'credit_note', label: CREDIT_NOTE_LABEL_SUPPLIER, icon: FileX, color: 'text-blue-400' }
    ],
    'return-slip': [
      { target: 'credit_note', label: CREDIT_NOTE_LABEL_SUPPLIER, icon: FileX, color: 'text-blue-400' }
    ],
    'client_return_slip': [
      { target: 'credit_note', label: CREDIT_NOTE_LABEL_CLIENT, icon: FileX, color: 'text-green-400' }
    ],
    'client-return-slip': [
      { target: 'credit_note', label: CREDIT_NOTE_LABEL_CLIENT, icon: FileX, color: 'text-green-400' }
    ],
    
    // ====== CYCLE DE VENTE (VENTES) ======
    'quote': [
      { target: 'proforma', label: docLabel('proforma'), icon: FileText, color: 'text-purple-400' },
      { target: 'invoice', label: docLabel('invoice', 'conversion_direct_invoice'), icon: FileOutput, color: 'text-blue-400' }
    ],
    'proforma': [
      { target: 'invoice', label: docLabel('invoice', 'conversion_direct_invoice'), icon: FileOutput, color: 'text-blue-400' }
    ],
    'invoice': [
      { target: 'delivery', label: docLabel('delivery'), icon: Truck, color: 'text-purple-400' },
      { target: 'payment_receipt', label: docLabel('payment_receipt'), icon: Receipt, color: 'text-indigo-400' },
      { target: 'credit_note', label: CREDIT_NOTE_LABEL_CLIENT, icon: FileX, color: 'text-green-400' }
    ],
    'delivery': [
      { target: 'shipping_slip', label: docLabel('shipping_slip'), icon: Send, color: 'text-cyan-400' }
    ],
    'delivery-note': [
      { target: 'shipping_slip', label: docLabel('shipping_slip'), icon: Send, color: 'text-cyan-400' }
    ],
    // Aucune chaîne cible produit (conversion vers facture récapitulative retirée du produit)
    'shipping_slip': [],
    'shipping': [],
    'shipping-slip': [],
    
    // ====== DOCUMENTS FINANCIERS ======
    // Bon de versement : décaissement (Achats) — document terminal, pas de reçu client
    'payment_voucher': [],
    'payment_slip': [], // Legacy alias for payment_voucher
    'payment-voucher': [],
    // Reçu de paiement : document terminal (encaissement) — pas de conversion vers avoir
    'payment_receipt': [],
    'payment-receipt': [],
    // credit_note : cible dynamique (BV achat / RP vente) via getAvailableConversions(doc, sourceDoc)
    
    // ====== OPÉRATIONS INTERNES (Inventaire / logistique) ======
    'exit_slip': INV_EXIT_TO_TRANSFER,
    'exit_note': INV_EXIT_TO_TRANSFER,
    'exit-note': INV_EXIT_TO_TRANSFER,
    'exit-slip': INV_EXIT_TO_TRANSFER,
    'outbound_slip': INV_EXIT_TO_TRANSFER,
    'outbound-slip': INV_EXIT_TO_TRANSFER,
    'transfer_slip': INV_TRANSFER_TARGETS,
    'transfer_note': INV_TRANSFER_TARGETS,
    'transfer-note': INV_TRANSFER_TARGETS,
    'transfer-slip': INV_TRANSFER_TARGETS,
    'stock_entry': INV_TERMINAL,
    'stock-entry': INV_TERMINAL,
    
    // Default fallback - basic conversions for any document
    'default': [
      { target: 'quote', label: docLabel('quote'), icon: FileSpreadsheet, color: 'text-green-400' },
      { target: 'invoice', label: docLabel('invoice'), icon: FileOutput, color: 'text-blue-400' },
      { target: 'purchase_order', label: docLabel('purchase_order'), icon: ClipboardList, color: 'text-yellow-400' },
      { target: 'delivery', label: docLabel('delivery'), icon: Truck, color: 'text-purple-400' }
    ]
  };

  // Get available conversions for a document type
  const getAvailableConversions = (documentType, sourceDoc) => {
    const typeKey = canonicalArchiveTypeKey(documentType);
    if (typeKey === 'credit_note' && sourceDoc) {
      return filterConversionsForModule(
        getCreditNoteConversions(sourceDoc, translateDocumentType),
        moduleScope,
      );
    }

    const matrixKey = resolveConversionMatrixKey(documentType);
    let conversions = [];

    if (
      documentType != null &&
      Object.prototype.hasOwnProperty.call(conversionMatrix, documentType)
    ) {
      conversions = conversionMatrix[documentType];
    } else if (
      matrixKey &&
      Object.prototype.hasOwnProperty.call(conversionMatrix, matrixKey)
    ) {
      conversions = conversionMatrix[matrixKey];
    } else if (matrixKey) {
      const hyphenKey = matrixKey.replace(/_/g, '-');
      if (Object.prototype.hasOwnProperty.call(conversionMatrix, hyphenKey)) {
        conversions = conversionMatrix[hyphenKey];
      }
    }

    if (!conversions || conversions.length === 0) {
      if (isInventoryLogisticsDocType(documentType)) {
        return [];
      }
      conversions = conversionMatrix.default || [];
    }

    return filterConversionsForModule(conversions, moduleScope);
  };

  // Status labels: Unicode escapes avoid mojibake; keys match DB + legacy aliases.
  // ORDER MATTERS - This order is used in dropdowns!
  const statusConfig = {
    // Common statuses (appear first for all documents)
    pending: { label: p('status_pending'), color: '#f59e0b', category: 'all' },
    draft: { label: p('status_draft'), color: '#64748b', category: 'all' },
    
    // Financial statuses
    paid: { label: p('status_paid'), color: '#10b981', category: 'financial' },
    partially_paid: { label: p('status_partially_paid'), color: '#84cc16', category: 'financial' },
    overdue: { label: p('status_overdue'), color: '#ef4444', category: 'financial' },
    partial: { label: p('status_partial'), color: '#eab308', category: 'financial' },

    // Preparatory statuses
    accepted: { label: p('status_accepted'), color: '#34d399', category: 'preparatory' },
    rejected: { label: p('status_rejected'), color: '#dc2626', category: 'preparatory' },
    approved: { label: p('status_approved'), color: '#22c55e', category: 'preparatory' },
    negotiating: { label: p('status_negotiating'), color: '#3b82f6', category: 'preparatory' },
    expired: { label: p('status_expired'), color: '#f97316', category: 'preparatory' },

    // Communication statuses
    sent: { label: p('status_sent'), color: '#0891b2', category: 'preparatory' },
    answered: { label: p('status_answered'), color: '#a855f7', category: 'preparatory' },
    processing: { label: p('status_processing'), color: '#6366f1', category: 'all' },

    // Logistic statuses
    delivered: { label: p('status_delivered'), color: '#059669', category: 'logistic' },
    in_transit: { label: p('status_in_transit'), color: '#06b6d4', category: 'logistic' },
    shipped: { label: p('status_shipped'), color: '#0284c7', category: 'logistic' },
    received: { label: p('status_received'), color: '#8b5cf6', category: 'logistic' },
    canceled: { label: p('status_canceled'), color: '#f43f5e', category: 'logistic' },
    cancelled: { label: p('status_canceled'), color: '#e11d48', category: 'financial' },
    processed: { label: p('status_processed'), color: '#14b8a6', category: 'logistic' },

    // Completion statuses
    completed: { label: p('status_completed'), color: '#16a34a', category: 'all' },
    verified: { label: p('status_verified'), color: '#15803d', category: 'financial' },
    
    // Credit note / return statuses
    issued: { label: p('status_issued'), color: '#0d9488', category: 'financial' },
    applied: { label: p('status_applied'), color: '#9333ea', category: 'financial' },
    refunded: { label: p('status_refunded'), color: '#10d5a0', category: 'financial' },
    transferred: { label: p('status_transferred'), color: '#22d3ee', category: 'logistic' },
    consolidated: { label: p('status_consolidated'), color: '#fb923c', category: 'financial' },
  };

  /** Row labels aligned with DocumentCreation where wording differs from global statusConfig / i18n. */
  const RETURN_SLIP_TYPE_KEYS = new Set(['return_slip', 'return-slip']);
  const PAYMENT_VOUCHER_TYPE_KEYS = new Set([
    'payment_voucher',
    'payment_slip',
    'payment-slip',
    'payment-voucher',
  ]);
  const getStatusLabelForDocumentType = (documentType, statusKey) => {
    if (!statusKey) return null;
    if (RETURN_SLIP_TYPE_KEYS.has(documentType)) {
      const slip = {
        pending: p('return_slip_pending'),
        processing: p('return_slip_processing'),
        received: p('return_slip_received'),
        accepted: p('return_slip_accepted'),
        rejected: p('return_slip_rejected'),
        processed: p('return_slip_processed'),
        refunded: p('return_slip_refunded'),
        canceled: p('return_slip_canceled'),
      };
      if (slip[statusKey]) return slip[statusKey];
    }
    if (PAYMENT_VOUCHER_TYPE_KEYS.has(documentType) && statusKey === 'paid') {
      return p('payment_voucher_paid');
    }
    const st = statusConfig[statusKey];
    return st ? st.label : statusKey;
  };

  const resolveExportStatusLabel = (status) =>
    statusConfig[status]?.label || statusConfig.draft?.label || status;

  const exportColumnHeaders = () => [
    p('export_col_number'),
    p('export_col_type'),
    p('export_col_client'),
    p('export_col_date'),
    p('export_col_amount'),
    p('export_col_status'),
  ];

  // Document type configurations
  const documentTypeConfig = {
    // Sales documents
    invoice: { label: translateDocumentType('invoice'), color: '#10b981', category: 'sales' },
    quote: { label: translateDocumentType('quote'), color: '#3b82f6', category: 'sales' },
    proforma: { label: translateDocumentType('proforma'), color: '#8b5cf6', category: 'sales' },
    
    // Purchase documents
    'purchase_order': { label: translateDocumentType('purchase_order'), color: '#f59e0b', category: 'purchase' },
    'purchase-order': { label: translateDocumentType('purchase-order'), color: '#f59e0b', category: 'purchase' },
    'quote_request': { label: translateDocumentType('quote_request'), color: '#06b6d4', category: 'purchase' },
    'quote-request': { label: translateDocumentType('quote-request'), color: '#06b6d4', category: 'purchase' },
    
    // Logistics documents
    delivery: { label: translateDocumentType('delivery'), color: '#10b981', category: 'logistics' },
    'delivery_note': { label: translateDocumentType('delivery_note'), color: '#10b981', category: 'logistics' },
    'delivery-note': { label: translateDocumentType('delivery-note'), color: '#10b981', category: 'logistics' },
    shipping: { label: translateDocumentType('shipping'), color: '#06b6d4', category: 'logistics' },
    'shipping_slip': { label: translateDocumentType('shipping_slip'), color: '#06b6d4', category: 'logistics' },
    'shipping-slip': { label: translateDocumentType('shipping-slip'), color: '#06b6d4', category: 'logistics' },
    'return_slip': { label: translateDocumentType('return_slip'), color: '#ef4444', category: 'logistics' },
    'return-slip': { label: translateDocumentType('return-slip'), color: '#ef4444', category: 'logistics' }
  };

  const filteredDocuments = scopedDocuments.filter(doc => {
    // First filter by main view mode
    if (mainViewMode === 'par_client' && selectedClient && doc.client !== selectedClient) {
      return false;
    }
    
    if (mainViewMode === 'par_type' && selectedDocumentType && doc.type !== selectedDocumentType) {
      return false;
    }
    
    if (mainViewMode === 'par_operation' && selectedOperation) {
      let belongsToOperation = false;
      
      
      // Method 1: Direct operation ID match (for documents with explicit operationId)
      if (doc.operationId && doc.operationId === selectedOperation) {
        belongsToOperation = true;
      }
      
      // Method 2: Check specific operation ID formats (these are synthetic operations)
      if (!belongsToOperation) {
        if (selectedOperation.startsWith('SYN-')) {
          // This is a synthetic operation - find source doc and its derived docs
          const sourceId = selectedOperation.replace('SYN-', '');
          
          // Include the document if it's the source or has this source
          if (doc.id === sourceId || doc.sourceDocumentId === sourceId) {
            belongsToOperation = true;
          }
        } else if (selectedOperation.startsWith('MONTH-')) {
          // This is a monthly grouping - Format: MONTH-Client-Name-Month-Year
          const withoutPrefix = selectedOperation.replace('MONTH-', '');
          const parts = withoutPrefix.split('-');
          
          if (parts.length >= 2) {
            // Last part is year, second-to-last is month (0-indexed)
            const year = parts.pop();
            const month = parts.pop(); // This should be 0-indexed month from getMonth()
            // Remaining parts joined back is the client name
            const clientName = parts.join('-').replace(/-/g, ' ');
            const expectedMonthYear = `${month}-${year}`;
            
            
            // Check if document belongs to this client and same month-year
            if (doc.client === clientName && doc.date) {
              const date = new Date(doc.date);
              if (!isNaN(date)) {
                const docMonthYear = `${date.getMonth()}-${date.getFullYear()}`;
                if (docMonthYear === expectedMonthYear) {
                  belongsToOperation = true;
                }
              }
            }
          }
        } else if (selectedOperation.startsWith('OP-')) {
          // This is a proper operation ID - find docs with this operation ID
          if (doc.operationId === selectedOperation) {
            belongsToOperation = true;
          }
        } else {
          // Generic client-monthYear format handling (fallback)
          try {
            const parts = selectedOperation.split('-');
            if (parts.length >= 2) {
              const monthYear = parts.pop();
              const clientName = parts.join('-').replace(/-/g, ' ');
              
              
              // Check if the document belongs to this client and month-year
              if (doc.client === clientName && doc.date) {
                const date = new Date(doc.date);
                if (!isNaN(date)) {
                  const docMonthYear = `${date.getMonth()}-${date.getFullYear()}`;
                  if (docMonthYear === monthYear.replace(/^-/, '')) {
                    belongsToOperation = true;
                  }
                }
              }
            }
          } catch (e) {
            console.warn('Error parsing operation format:', selectedOperation, e);
          }
        }
      }
      
      // If none of the methods matched, don't show this document
      if (!belongsToOperation) {
        return false;
      }
    }
    
    // Then filter based on tab selection (only for all_documents view)
    if (mainViewMode === 'all_documents') {
      if (activeTab === 'all') {
        // Apply additional filters
      } else if (activeTab === 'invoice' && doc.type !== 'invoice') {
        return false;
      } else if (activeTab === 'quote' && doc.type !== 'quote') {
        return false;
      } else if (activeTab === 'delivery' && doc.type !== 'delivery' && doc.type !== 'delivery-note') {
        return false;
      } else if (
        (activeTab === 'paid' || activeTab === 'pending' || activeTab === 'overdue') &&
        !archiveDocMatchesFinancialQuickTab(doc, activeTab)
      ) {
        return false;
      }
    }
    
    // Apply search term filter (titre/n°/client + statut affiché / clés DB–UI synchrones)
    const matchesSearch = archiveDocMatchesTextSearch(doc, searchTerm);
    
    // Apply status filter (clés UI ≠ valeurs SQLite brutes pour plusieurs familles de docs)
    const matchesStatus = archiveDocMatchesStatusFilter(doc, filterStatus);
    
    // Apply date filter
    let matchesDate = true;
    if (dateRange.start && dateRange.end) {
      matchesDate = doc.date >= dateRange.start && doc.date <= dateRange.end;
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  /** Facturation documents only, filtered by Statut + Période (for aggregate tabs pickers). */
  const aggregateFilteredDocs = useMemo(() => {
    return scopedDocuments.filter((doc) => {
      const matchesStatus = archiveDocMatchesStatusFilter(doc, filterStatus);
      let matchesDate = true;
      if (dateRange.start && dateRange.end) {
        matchesDate = doc.date >= dateRange.start && doc.date <= dateRange.end;
      }
      return matchesStatus && matchesDate;
    });
  }, [scopedDocuments, filterStatus, dateRange.start, dateRange.end]);

  const pickerClientsFiltered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return clients.filter((client) => {
      const clientDocs = aggregateFilteredDocs.filter((d) => d.client === client.name);
      if (!q) return true;
      if (client.name?.toLowerCase().includes(q)) return true;
      return clientDocs.some(
        (doc) => archiveDocMatchesTextSearch(doc, searchTerm.trim())
      );
    });
  }, [clients, aggregateFilteredDocs, searchTerm]);

  const documentTypePickerRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return Object.entries(documentTypes)
      .filter(([key]) => key !== 'default')
      .filter(([key]) => !(key.includes('-') && Object.keys(documentTypes).includes(key.replace('-', '_'))))
      .filter(([key]) => {
        const typeDocs = aggregateFilteredDocs.filter((doc) => doc.type === key);
        if (!q) return true;
        if (translateDocumentType(key).toLowerCase().includes(q)) return true;
        return typeDocs.some(
          (doc) => archiveDocMatchesTextSearch(doc, searchTerm.trim())
        );
      });
  }, [documentTypes, aggregateFilteredDocs, searchTerm, translateDocumentType]);

  // Paginate documents
  const startIndex = (currentPage - 1) * documentsPerPage;
  const endIndex = currentPage * documentsPerPage;
  const currentDocuments = filteredDocuments.slice(startIndex, endIndex);

  const archivePageIds = useMemo(
    () => currentDocuments.map((doc) => doc.id),
    [currentDocuments],
  );
  const allRowsSelected =
    archivePageIds.length > 0 && archivePageIds.every((id) => selectedDocuments.includes(id));
  const someRowsSelected = selectedDocuments.length > 0 && !allRowsSelected;
  
  
  const totalDocuments = filteredDocuments.length;
  const totalPages = Math.ceil(totalDocuments / documentsPerPage);

  const convertDocumentType = convertArchiveTypeForPdf;

  const buildConversionPrefixMap = () => buildFacturationConversionPrefixMap(settings);

  const buildConvertedDocumentPayload = (document, targetType, { idSuffix = '', existingDocsOverride } = {}) => {
    const newDocument = { ...document };
    const normalizedType = normalizeTypeForDb(targetType);
    newDocument.type = normalizedType;

    const typeInfo =
      documentTypes[targetType] || documentTypes[normalizedType] || documentTypes.default;
    const prefixMap = buildConversionPrefixMap();

    const carryTraceabilityFromSource = () => {
      if (normalizedType !== 'delivery' && normalizedType !== 'material_exit') return;
      const srcType = String(document.type || '').replace(/-/g, '_');
      if (!newDocument.invoiceReference && !newDocument.invoice_reference) {
        const fromInv =
          (srcType === 'invoice' || srcType === 'proforma') && document.number
            ? String(document.number).trim()
            : '';
        if (fromInv) newDocument.invoiceReference = fromInv;
      }
      const ord =
        newDocument.orderReference ||
        newDocument.order_reference ||
        document.orderReference ||
        document.order_reference ||
        document.customerOrderReference ||
        document.customer_order_reference ||
        document.purchase_order_reference ||
        document.purchaseOrderRef;
      if (ord && !(newDocument.orderReference || '').toString().trim()) {
        newDocument.orderReference = String(ord).trim();
      }
      const expectDeliv =
        newDocument.expectedDeliveryDate ||
        newDocument.expected_delivery_date ||
        document.expectedDeliveryDate ||
        document.expected_delivery_date ||
        document.deliveryDate ||
        document.delivery_date;
      if (expectDeliv && !(newDocument.expectedDeliveryDate || '').toString().trim()) {
        newDocument.expectedDeliveryDate = String(expectDeliv).trim().slice(0, 10);
      }
    };
    carryTraceabilityFromSource();

    const prefix =
      prefixMap[targetType] ||
      prefixMap[normalizedType] ||
      prefixMap[String(targetType || '').replace(/-/g, '_')] ||
      'DOC';
    const allDocs = Array.isArray(existingDocsOverride)
      ? existingDocsOverride
      : Array.isArray(documents)
        ? documents
        : [];
    const withSamePrefix = allDocs.filter(
      (d) => d && d.number && String(d.number).startsWith(`${prefix}-`),
    );
    let seq = withSamePrefix.reduce((max, d) => {
      const part = String(d.number).split('-')[1];
      const numPart = parseInt(part, 10);
      return !Number.isNaN(numPart) ? Math.max(max, numPart) : max;
    }, 0);
    let nextNumberStr;
    do {
      seq += 1;
      nextNumberStr = `${prefix}-${String(seq).padStart(2, '0')}`;
    } while (allDocs.some((d) => d.number === nextNumberStr));

    newDocument.number = nextNumberStr;

    newDocument.title = generateDocumentTitle(
      newDocument,
      { id: targetType, title: typeInfo?.label || translateDocumentType(targetType) },
      i18n.language,
    );

    applySupplementaryDefaultsForArchiveConversion(newDocument, targetType);

    stripRequestBudgetFromConvertedPurchaseOrder(newDocument, document, normalizedType);
    applySourceRequestRefsToConvertedPurchaseOrder(newDocument, document, normalizedType);

    if (normalizedType === 'reception_note') {
      const srcKeyRec = canonicalArchiveTypeKey(document.type);
      if (srcKeyRec === 'purchase_order') {
        applyPurchaseOrderToReceptionConversion(newDocument, document);
      } else if (srcKeyRec === 'transfer_slip' || srcKeyRec === 'transfer_note') {
        applyTransferToReceptionConversion(newDocument, document);
      }
    }

    if (normalizedType === 'transfer_slip' || normalizedType === 'transfer_note') {
      const srcKeyTr = canonicalArchiveTypeKey(document.type);
      if (srcKeyTr === 'exit_slip' || srcKeyTr === 'exit_note') {
        applyExitToTransferConversion(newDocument, document);
        newDocument.title = generateDocumentTitle(
          newDocument,
          { id: targetType, title: typeInfo?.label || translateDocumentType(targetType) },
          i18n.language,
        );
      }
    }

    if (normalizedType === 'stock_entry') {
      const srcKeyStock = canonicalArchiveTypeKey(document.type);
      if (srcKeyStock === 'reception_note' || srcKeyStock === 'receipt_note') {
        applyReceptionToStockEntryConversion(newDocument, document);
      } else if (srcKeyStock === 'transfer_slip' || srcKeyStock === 'transfer_note') {
        applyTransferToStockEntryConversion(newDocument, document);
      }
      newDocument.title = generateDocumentTitle(
        newDocument,
        { id: targetType, title: typeInfo?.label || translateDocumentType(targetType) },
        i18n.language,
      );
    }

    if (normalizedType === 'shipping' || normalizedType === 'shipping_slip') {
      const srcKeyShip = canonicalArchiveTypeKey(document.type);
      if (srcKeyShip === 'delivery') {
        applyDeliveryToShippingConversion(newDocument, document);
        newDocument.title = generateDocumentTitle(
          newDocument,
          { id: targetType, title: typeInfo?.label || translateDocumentType(targetType) },
          i18n.language,
        );
      }
    }

    if (normalizedType === 'payment_voucher') {
      const srcKey = canonicalArchiveTypeKey(document.type);
      if (srcKey === 'purchase_order') {
        applyPurchaseOrderToPaymentVoucherConversion(newDocument, document, suppliers);
      } else if (srcKey === 'credit_note' && isSupplierCreditNoteDocument(document)) {
        applyCreditNoteToPaymentVoucherConversion(newDocument, document, suppliers);
      }
      newDocument.paymentReason = '';
      newDocument.motif = '';
      newDocument.priority = '';
      stripIrrelevantFieldsFromConvertedPaymentVoucher(newDocument);
      newDocument.title = generateDocumentTitle(
        newDocument,
        { id: targetType, title: typeInfo?.label || translateDocumentType(targetType) },
        i18n.language,
      );
    }

    if (normalizedType === 'payment_receipt') {
      const srcKey = canonicalArchiveTypeKey(document.type);
      if (srcKey === 'invoice') {
        applyInvoiceToPaymentReceiptConversion(newDocument, document, clients);
      } else if (srcKey === 'credit_note' && isClientCreditNoteDocument(document)) {
        applyCreditNoteToPaymentReceiptConversion(newDocument, document, clients);
      }
      newDocument.title = generateDocumentTitle(
        newDocument,
        { id: targetType, title: typeInfo?.label || translateDocumentType(targetType) },
        i18n.language,
      );
    }

    if (normalizedType === 'delivery') {
      const srcKeyDelivery = canonicalArchiveTypeKey(document.type);
      if (srcKeyDelivery === 'invoice') {
        applyInvoiceToDeliveryConversion(newDocument, document);
        newDocument.title = generateDocumentTitle(
          newDocument,
          { id: targetType, title: typeInfo?.label || translateDocumentType(targetType) },
          i18n.language,
        );
      }
    }

    if (normalizedType === 'return_slip') {
      applyReceptionToReturnConversion(newDocument, document);
    }

    const srcKey = canonicalArchiveTypeKey(document.type);

    if (normalizedType === 'proforma' && srcKey === 'quote') {
      applyQuoteToProformaConversion(newDocument, document);
    }

    if (normalizedType === 'invoice') {
      if (srcKey === 'quote') {
        applyQuoteToInvoiceConversion(newDocument, document);
      } else if (srcKey === 'proforma') {
        applyProformaToInvoiceConversion(newDocument, document);
      }
    }

    if (normalizedType === 'credit_note') {
      applyReturnSlipToCreditConversion(newDocument, document);
      applyClientReturnSlipToCreditConversion(newDocument, document);
      applyInvoiceToCreditConversion(newDocument, document);
      const creditPartyLabel =
        resolveCreditNoteDisplayLabel(newDocument) ||
        typeInfo?.label ||
        translateDocumentType(targetType);
      newDocument.title = generateDocumentTitle(
        newDocument,
        { id: targetType, title: creditPartyLabel },
        i18n.language,
      );
    }

    const conversionTodayIso = new Date().toISOString().split('T')[0];
    const srcKeyForDates = canonicalArchiveTypeKey(document.type);
    const deliveryToShipping =
      (normalizedType === 'shipping' || normalizedType === 'shipping_slip') &&
      srcKeyForDates === 'delivery';

    if (deliveryToShipping) {
      if (!newDocument.date) {
        newDocument.date =
          pickFirstIsoDate(newDocument, ['date']) ||
          pickFirstIsoDate(document, ['date', 'deliveryDate', 'delivery_date']) ||
          conversionTodayIso;
      }
    } else {
      newDocument.date = conversionTodayIso;
    }

    applyArchiveConversionDatesAndSignatures(newDocument, targetType, document, {
      todayIso: conversionTodayIso,
      documentDelays: settings?.configuration?.documentDelays,
      preserveDeliveryToShippingDates: deliveryToShipping,
    });

    newDocument.id = `${Date.now()}${idSuffix ? `-${idSuffix}` : ''}`;

    if (!document.operationId) {
      newDocument.operationId = `OP-${Date.now()}`;
      newDocument.operationStep = 1;
      newDocument.operationFlow = [document.type, targetType];
    } else {
      newDocument.operationId = document.operationId;
      newDocument.operationStep = (document.operationStep || 1) + 1;
      newDocument.operationFlow = [...(document.operationFlow || [document.type]), targetType];
    }

    newDocument.sourceDocumentId = document.id;
    newDocument.operationName =
      document.operationName ||
      `${document.client} - ${formatUserDate(new Date(), { month: 'long', year: 'numeric' })}`;
    newDocument.operationClient = document.client;

    if (typeInfo.allowedStatuses && typeInfo.allowedStatuses.length > 0) {
      if (typeInfo.allowedStatuses.includes('pending')) {
        newDocument.status = 'pending';
      } else {
        newDocument.status = typeInfo.allowedStatuses[0];
      }
    } else {
      newDocument.status = 'draft';
    }

    applyArchiveModuleToDocumentPayload(
      newDocument,
      getArchiveModuleForDocType(normalizedType),
    );

    if (isInventoryLogisticsDocType(normalizedType)) {
      newDocument.type = normalizeInventoryDocTypeForUi(normalizedType);
    }

    applyConversionTaxProfile(newDocument, document, targetType);

    return { newDocument, normalizedType, typeInfo, targetType };
  };

  const showConversionSuccessToast = (savedDocument, newDocument, normalizedType, typeInfo, message) => {
    const targetArchivesRoute = getArchivesRouteForModule(
      getArchiveModuleForDocType(normalizedType),
    );
    const currentArchivesRoute = getArchivesRouteForModule(moduleScope);
    const toastPayload = {
      visible: true,
      message: message || p('conversion_success', { label: typeInfo.label }),
      status: 'success',
      documentId: savedDocument?.id || newDocument.id,
      documentNumber: newDocument.number,
      documentType: normalizedType,
    };

    if (targetArchivesRoute !== currentArchivesRoute) {
      navigate(targetArchivesRoute, { state: { toast: toastPayload } });
    } else {
      setToast(toastPayload);
      setTimeout(() => {
        setToast((prev) => ({ ...prev, visible: false }));
      }, 3000);
    }
  };

  const handleConversionError = (error) => {
    console.error('Error converting document:', error);
    const msg = error?.message || String(error || '');
    if (msg.includes('UNIQUE constraint') && msg.includes('number')) {
      alert(p('conversion_duplicate_number'));
    } else {
      alert(p('conversion_error'));
    }
  };

  const persistConvertedDocument = async (
    newDocument,
    { normalizedType, typeInfo },
    { suppressToast = false, toastMessage } = {},
  ) => {
    const savedDocument = await addDocument(newDocument);
    if (!suppressToast) {
      showConversionSuccessToast(savedDocument, newDocument, normalizedType, typeInfo, toastMessage);
    }
    return savedDocument;
  };

  const regenerateMergedPaymentVoucherTitle = (newDoc) => {
    const typeInfo =
      documentTypes.payment_voucher ||
      documentTypes['payment-voucher'] ||
      documentTypes.default;
    newDoc.title = generateDocumentTitle(
      newDoc,
      { id: 'payment_voucher', title: typeInfo?.label || translateDocumentType('payment_voucher') },
      i18n.language,
    );
    const core = summarizePaymentDocumentTitleCore(newDoc.paymentLines || []);
    if (core && newDoc.title) {
      newDoc.title = `${newDoc.title} — ${core}`;
    }
  };

  const regenerateMergedPaymentReceiptTitle = (newDoc) => {
    const typeInfo =
      documentTypes.payment_receipt ||
      documentTypes['payment-receipt'] ||
      documentTypes.default;
    newDoc.title = generateDocumentTitle(
      newDoc,
      { id: 'payment_receipt', title: typeInfo?.label || translateDocumentType('payment_receipt') },
      i18n.language,
    );
    const core = summarizePaymentDocumentTitleCore(newDoc.paymentLines || []);
    if (core && newDoc.title) {
      newDoc.title = `${newDoc.title} — ${core}`;
    }
  };

  /** Rangs liste Facturiers (FacturiersPage) : vue bleu · édit vert · supprimer rouge — pas de bloc coloré sous l’icône */
  const archivesFacturiersLikeActionBtn = {
    view: 'p-1.5 text-blue-400 hover:text-blue-300 transition-colors',
    edit: 'p-1.5 text-green-400 hover:text-green-300 transition-colors',
    download: 'p-1.5 text-sky-400 hover:text-sky-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-sky-400',
    delete: 'p-1.5 text-red-400 hover:text-red-300 transition-colors',
  };

  const prepareArchiveDocumentForPdf = (document) => {
      // Find client data for this document
      const clientDataRaw = findClientData(document);
      const clientData = clientDataRaw ? { ...clientDataRaw } : null;
      
      // Convert the document type
      const convertedType = convertDocumentType(document.type);
      
      // Fix partner numbering before generating PDF
      let docToDisplay = {...document};
      
      // Transfer document detail fields from database to display fields
      // These mappings ensure document detail fields appear in the archive view
      const detailFieldsMapping = {
        payment_terms: 'paymentTerms',
        delivery_date: 'deliveryDate',
        delivery_location: 'deliveryLocation',
        delivery_place: 'deliveryPlace',
        deliveryPlace: 'deliveryPlace',
        // Also map delivery_address to deliveryAddress for backward compatibility
        delivery_address: 'deliveryAddress', // Used in purchase-order template
        created_by: 'issuedBy', // Map to the field name used in templates
        createdBy: 'issuedBy',   // Handle camelCase stored value
        buyer: 'acheteur', // Map to French field name used in templates
        buyer: 'buyer', // Also map to English field name
        
        // Quote request specific fields
        requestType: 'requestType',
        responseDate: 'responseDate',
        desiredStartDate: 'desiredStartDate',
        request_type: 'requestType',
        response_date: 'responseDate',
        desired_start_date: 'desiredStartDate',
        
        // Quote request / proforma request detail fields
        project_reference: 'projectReference',
        desired_delivery_time: 'desiredDeliveryTime',
        required_documents: 'requiredDocuments',
        estimated_delivery_time: 'estimatedDeliveryTime',
        desired_payment_terms: 'desiredPaymentTerms',
        declared_value: 'declaredValue',
        priority: 'priority',
        commercial: 'commercial',
        response_deadline: 'responseDeadline',
        
        // Return slip specific fields
        returnDate: 'returnDate',
        returnReason: 'returnReason',
        returnType: 'returnType',
        returnCarrier: 'returnCarrier',
        returnCondition: 'returnCondition',
        returnInspector: 'returnInspector',
        returnStatus: 'returnStatus',
        returnOriginalDelivery: 'returnOriginalDelivery',
        returnOriginalInvoice: 'returnOriginalInvoice',
        returnOriginalInvoiceDate: 'returnOriginalInvoiceDate',
        
        // Also include underscore versions for backward compatibility
        return_date: 'returnDate',
        return_reason: 'returnReason',
        return_type: 'returnType',
        return_carrier: 'returnCarrier',
        return_condition: 'returnCondition',
        return_inspector: 'returnInspector',
        return_status: 'returnStatus',
        return_original_delivery: 'returnOriginalDelivery',
        return_original_invoice: 'returnOriginalInvoice',
        return_original_invoice_date: 'returnOriginalInvoiceDate',
        
        // Reception note specific fields
        receptionAddress: 'receptionAddress',
        receptionDeliveryNote: 'receptionDeliveryNote',
        receptionOrder: 'receptionOrder',
        receptionDate: 'receptionDate',
        receptionTime: 'receptionTime',
        receptionReceivedBy: 'receptionReceivedBy',
        receptionControlledBy: 'receptionControlledBy',
        receptionOverallCondition: 'receptionOverallCondition',
        receptionQuantityCompliance: 'receptionQuantityCompliance',
        receptionQualityCompliance: 'receptionQualityCompliance',
        receptionStockDestination: 'receptionStockDestination',
        receptionStatus: 'receptionStatus',
        
        // Reception note fields with underscores for backward compatibility
        reception_address: 'receptionAddress',
        reception_delivery_note: 'receptionDeliveryNote',
        reception_order: 'receptionOrder',
        reception_date: 'receptionDate',
        reception_time: 'receptionTime',
        reception_received_by: 'receptionReceivedBy',
        reception_controlled_by: 'receptionControlledBy',
        reception_overall_condition: 'receptionOverallCondition',
        reception_quantity_compliance: 'receptionQuantityCompliance',
        reception_quality_compliance: 'receptionQualityCompliance',
        reception_stock_destination: 'receptionStockDestination',
        reception_status: 'receptionStatus',
        
        // Invoice specific fields
        payment_method: 'paymentMethod',
        due_date: 'dueDate',
        issued_by: 'issuedBy', 
        sales_person: 'salesPerson',
        bank_details: 'bankDetails',
        amount_in_words: 'amountInWords',
        
        // Proforma specific fields
        valid_until: 'validUntil',
        estimated_delivery: 'estimatedDelivery',
        
        // Quote specific fields
        delivery_time: 'deliveryTime',
        project_type: 'projectType',
        payment_conditions: 'paymentConditions',
        conditions: 'conditions',
        
        // Shipping slip specific fields
        shipment_date: 'shipmentDate',
        shipment_method: 'shipmentMethod',
        tracking_number: 'trackingNumber',
        carrier_tracking_number: 'carrierTrackingNumber',
        total_weight: 'totalWeight',
        package_count: 'packageCount',
        package_type: 'packageType',
        instructions: 'instructions',
        
        // Delivery note specific fields
        delivery_date: 'deliveryDate',
        delivery_time: 'deliveryTime',
        order_reference: 'orderReference',
        purchase_order_reference: 'orderReference',
        invoice_reference: 'invoiceReference',
        document_reference: 'documentReference',
        origin_warehouse_name: 'departureWarehouse',
        carrier: 'carrier',
        driver: 'driver',
        vehicle_type: 'vehicleType',
        vehicle_registration: 'vehicleRegistration',
        delivery_conditions: 'deliveryConditions',
        package_count: 'packageCount',
        expected_delivery_date: 'expectedDeliveryDate',

        // Client return slip (shared return_* columns)
        return_original_purchase_order: 'returnOriginalPurchaseOrder',
        return_warehouse: 'returnWarehouse',
        controlled_by_name: 'controlledByName',

        // Logistics / inventory (Bon réception, entrée, sortie, transfert, expédition)
        warehouse_name: 'warehouseName',
        entry_date: 'entryDate',
        entry_time: 'entryTime',
        entry_source: 'entrySource',
        entry_document_reference: 'entryDocumentReference',
        expected_entry_date: 'expectedEntryDate',
        expected_reception_date: 'expectedReceptionDate',
        origin_warehouse_name: 'originWarehouseName',
        destination_warehouse_name: 'destinationWarehouseName',
        departure_time: 'departureTime',
        arrival_time: 'arrivalTime',
        shipment_mode: 'shipmentMode',
        delivery_note_reference: 'deliveryNoteReference',
        general_condition: 'generalCondition',
        quantity_conformity: 'quantityConformity',
        quality_conformity: 'qualityConformity',
        stock_destination: 'stockDestination',
        transfer_requested_by_name: 'transferRequestedByName',
        transfer_responsible_name: 'transferResponsibleName',
        approved_by_name: 'approvedByName',
        received_by_name: 'receivedByName',
        supplier_name: 'supplierName',
        goods_condition: 'goodsCondition',
        receiver_address: 'receiverAddress',
      };
      
      // Copy fields from database names to display names
      Object.entries(detailFieldsMapping).forEach(([dbField, displayField]) => {
        if (document[dbField] && !docToDisplay[displayField]) {
          docToDisplay[displayField] = document[dbField];
        }
      });
      
      // If document has document_options as JSON string, parse it
      if (document.document_options && typeof document.document_options === 'string') {
        try {
          const options = JSON.parse(document.document_options);
          // Apply display options
          Object.assign(docToDisplay, options);
        } catch (e) {
          console.error('Failed to parse document options:', e);
        }
      }
      
      // Special handling for delivery location field which might be stored in various formats
      if (!docToDisplay.deliveryLocation && !docToDisplay.deliveryAddress && !docToDisplay.deliveryPlace) {
        // Try to find delivery location in any possible format
        if (document.delivery_location) {
          docToDisplay.deliveryLocation = document.delivery_location;
        } else if (document.deliveryLocation) {
          docToDisplay.deliveryLocation = document.deliveryLocation;
        } else if (document.delivery_place) {
          docToDisplay.deliveryPlace = document.delivery_place;
          docToDisplay.deliveryLocation = document.delivery_place;
        } else if (document.deliveryPlace) {
          docToDisplay.deliveryPlace = document.deliveryPlace;
          docToDisplay.deliveryLocation = document.deliveryPlace;
        } else if (document.deliveryAddress) {
          docToDisplay.deliveryAddress = document.deliveryAddress;
        } else if (document.delivery_address) {
          docToDisplay.deliveryAddress = document.delivery_address;
        } else if (document.lieu_livraison) {
          docToDisplay.deliveryLocation = document.lieu_livraison;
        } else if (document.lieuLivraison) {
          docToDisplay.deliveryLocation = document.lieuLivraison;
        }
      } else if (!docToDisplay.deliveryLocation && docToDisplay.deliveryPlace) {
        docToDisplay.deliveryLocation = docToDisplay.deliveryPlace;
      } else if (!docToDisplay.deliveryPlace && docToDisplay.deliveryLocation) {
        docToDisplay.deliveryPlace = docToDisplay.deliveryLocation;
      }
      
      // Special handling for return slip fields stored in the return object
      if (document.type === 'return_slip' || document.type === 'return-slip') {
        // If return object exists, extract its fields
        if (document.return && typeof document.return === 'object') {
          // Map return object fields to top-level fields for display
          if (!docToDisplay.returnDate && document.return.date) {
            docToDisplay.returnDate = document.return.date;
          }
          if (!docToDisplay.returnReason && document.return.reason) {
            docToDisplay.returnReason = document.return.reason;
          }
          if (!docToDisplay.returnType && document.return.type) {
            docToDisplay.returnType = document.return.type;
          }
          if (!docToDisplay.returnCarrier && document.return.carrier) {
            docToDisplay.returnCarrier = document.return.carrier;
          }
          if (!docToDisplay.returnCondition && document.return.condition) {
            docToDisplay.returnCondition = document.return.condition;
          }
          if (!docToDisplay.returnInspector && document.return.inspector) {
            docToDisplay.returnInspector = document.return.inspector;
          }
          if (!docToDisplay.returnStatus && document.return.status) {
            docToDisplay.returnStatus = document.return.status;
          }
          if (!docToDisplay.returnOriginalDelivery && document.return.originalDeliveryRef) {
            docToDisplay.returnOriginalDelivery = document.return.originalDeliveryRef;
          }
          if (!docToDisplay.returnOriginalInvoice && document.return.originalInvoiceRef) {
            docToDisplay.returnOriginalInvoice = document.return.originalInvoiceRef;
          }
        }
      }
      
      // Special handling for reception note fields stored in the reception object
      if (document.type === 'reception_note' || document.type === 'reception-note') {
        if (!docToDisplay.receptionOrderRef) {
          docToDisplay.receptionOrderRef =
            document.purchaseOrderReference ||
            document.purchase_order_reference ||
            docToDisplay.receptionOrder ||
            document.receptionOrder ||
            document.reception_order ||
            '';
        }
        if (!docToDisplay.receptionDeliveryNoteRef) {
          docToDisplay.receptionDeliveryNoteRef =
            document.deliveryNoteReference ||
            document.delivery_note_reference ||
            docToDisplay.receptionDeliveryNote ||
            document.receptionDeliveryNote ||
            document.reception_delivery_note ||
            '';
        }
        if (!docToDisplay.receiverAddress) {
          docToDisplay.receiverAddress =
            document.receptionAddress ||
            document.reception_address ||
            docToDisplay.receptionAddress ||
            '';
        }
        if (!docToDisplay.receiverAddress) {
          docToDisplay.receiverAddress =
            document.receiverAddress ||
            document.receiver_address ||
            docToDisplay.receptionAddress ||
            document.receptionAddress ||
            document.reception_address ||
            '';
        }
        if (!docToDisplay.expectedReceptionDate) {
          docToDisplay.expectedReceptionDate =
            document.expectedReceptionDate ||
            document.expected_reception_date ||
            docToDisplay.expectedDeliveryDate ||
            document.expectedDeliveryDate ||
            document.expected_delivery_date ||
            docToDisplay.receptionDate ||
            document.reception_date ||
            document.date ||
            '';
        }
        // If reception object exists, extract its fields
        if (document.reception && typeof document.reception === 'object') {
          // Map reception object fields to top-level fields for display
          if (!docToDisplay.receptionAddress && document.reception.receiverAddress) {
            docToDisplay.receptionAddress = document.reception.receiverAddress;
          }
          if (!docToDisplay.receptionDeliveryNote && document.reception.deliveryNoteRef) {
            docToDisplay.receptionDeliveryNote = document.reception.deliveryNoteRef;
          }
          if (!docToDisplay.receptionOrder && document.reception.orderRef) {
            docToDisplay.receptionOrder = document.reception.orderRef;
          }
          if (!docToDisplay.receptionDate && document.reception.date) {
            docToDisplay.receptionDate = document.reception.date;
          }
          if (!docToDisplay.receptionTime && document.reception.time) {
            docToDisplay.receptionTime = document.reception.time;
          }
          if (!docToDisplay.receptionReceivedBy && document.reception.receivedBy) {
            docToDisplay.receptionReceivedBy = document.reception.receivedBy;
          }
          if (!docToDisplay.receptionControlledBy && document.reception.controlledBy) {
            docToDisplay.receptionControlledBy = document.reception.controlledBy;
          }
          if (!docToDisplay.receptionOverallCondition && document.reception.overallCondition) {
            docToDisplay.receptionOverallCondition = document.reception.overallCondition;
          }
          if (!docToDisplay.receptionQuantityCompliance && document.reception.quantityCompliance) {
            docToDisplay.receptionQuantityCompliance = document.reception.quantityCompliance;
          }
          if (!docToDisplay.receptionQualityCompliance && document.reception.qualityCompliance) {
            docToDisplay.receptionQualityCompliance = document.reception.qualityCompliance;
          }
          if (!docToDisplay.receptionStockDestination && document.reception.stockDestination) {
            docToDisplay.receptionStockDestination = document.reception.stockDestination;
          }
          if (!docToDisplay.receptionStatus && document.reception.status) {
            docToDisplay.receptionStatus = document.reception.status;
          }
        }
      }
      
      // Special handling for shipping slip fields stored in the shipment object
      if (document.type === 'shipping' || document.type === 'shipping-slip') {
        // If shipment object exists, extract its fields
        if (document.shipment && typeof document.shipment === 'object') {
          // Map shipment object fields to top-level fields for display
          if (!docToDisplay.shipmentDate && document.shipment.date) {
            docToDisplay.shipmentDate = document.shipment.date;
          }
          if (!docToDisplay.shipmentMethod && document.shipment.method) {
            docToDisplay.shipmentMethod = document.shipment.method;
          }
          if (!docToDisplay.trackingNumber && document.shipment.trackingNumber) {
            docToDisplay.trackingNumber = document.shipment.trackingNumber;
          }
          if (!docToDisplay.totalWeight && document.shipment.totalWeight) {
            docToDisplay.totalWeight = document.shipment.totalWeight;
          }
          if (!docToDisplay.packageCount && document.shipment.packageCount) {
            docToDisplay.packageCount = document.shipment.packageCount;
          }
          if (!docToDisplay.packageType && document.shipment.packageType) {
            docToDisplay.packageType = document.shipment.packageType;
          }
        }
      }
      
      // Special handling for delivery note fields stored in the delivery object
      if (document.type === 'delivery' || document.type === 'delivery-note') {
        // If delivery object exists, extract its fields
        if (document.delivery && typeof document.delivery === 'object') {
          // Map delivery object fields to top-level fields for display
          if (!docToDisplay.deliveryDate && document.delivery.date) {
            docToDisplay.deliveryDate = document.delivery.date;
          }
          if (!docToDisplay.deliveryTime && document.delivery.time) {
            docToDisplay.deliveryTime = document.delivery.time;
          }
          if (!docToDisplay.orderReference && document.delivery.orderReference) {
            docToDisplay.orderReference = document.delivery.orderReference;
          }
          if (!docToDisplay.carrier && document.delivery.carrier) {
            docToDisplay.carrier = document.delivery.carrier;
          }
          if (!docToDisplay.driver && document.delivery.driver) {
            docToDisplay.driver = document.delivery.driver;
          }
          if (!docToDisplay.vehicleType && document.delivery.vehicleType) {
            docToDisplay.vehicleType = document.delivery.vehicleType;
          }
          if (!docToDisplay.vehicleRegistration && document.delivery.vehicleRegistration) {
            docToDisplay.vehicleRegistration = document.delivery.vehicleRegistration;
          }
          if (!docToDisplay.deliveryConditions && document.delivery.conditions) {
            docToDisplay.deliveryConditions = document.delivery.conditions;
          }
        }
      }
      
      // If document doesn't have a partnerNumber or has incorrect raw ID format, generate one now
      const hasIncorrectPartnerNumber = docToDisplay.partnerNumber && (
        (docToDisplay.partnerNumber.includes('CLI-') && docToDisplay.partnerNumber.length > 10) ||
        (docToDisplay.partnerNumber.includes('FRN-') && docToDisplay.partnerNumber.length > 10)
      );
      
      if (!docToDisplay.partnerNumber || hasIncorrectPartnerNumber) {
        const isSupplierDocument = 
          document.type === 'purchase_order' || 
          document.type === 'quote_request' || 
          document.type === 'reception_note' || 
          document.type === 'return_slip' ||
          document.type === 'purchase-order' || 
          document.type === 'quote-request' || 
          document.type === 'reception-note' || 
          document.type === 'return-slip';
          
        if (clientData) {
          const oldPartnerNumber = docToDisplay.partnerNumber;
          docToDisplay.partnerNumber = formatPartnerNumber(clientData, isSupplierDocument);
          console.log("?? Archives: Fixed partner number for PDF - OLD:", oldPartnerNumber, "NEW:", docToDisplay.partnerNumber, "for", isSupplierDocument ? 'supplier' : 'client', clientData.name);
        }
      }
      
      // Clean up the document before sending it to the preview
      if (clientData) {
        // Ensure partner data is properly formatted
        if (clientData.clientNumber && clientData.clientNumber.startsWith('CLI-')) {
          // Extract just the number part if client number already has prefix
          clientData.clientNumber = clientData.clientNumber.replace('CLI-', '');
        }
      }

      applyArchiveQuoteBudgetFields(docToDisplay, document);
      applyArchiveSupplementaryAndUiOverrides(docToDisplay, document);
      applyArchivePurchaseOrderRefFields(docToDisplay, document);
      applyArchivePurchaseOrderDetailFields(docToDisplay, document);
      applyArchiveQuoteDisplayFields(docToDisplay, document);
      applyArchiveInvoiceDisplayFields(docToDisplay, document);
      applyArchiveDeliveryDisplayFields(docToDisplay, document);
      applyArchiveReceptionDisplayFields(docToDisplay, document);
      applyArchiveStockEntryDisplayFields(docToDisplay, document);
      applyArchiveExitSlipDisplayFields(docToDisplay, document);
      applyArchiveTransferSlipDisplayFields(docToDisplay, document);
      applyArchiveShippingSlipDisplayFields(docToDisplay, document);
      applyReturnSlipArchiveDisplayFields(docToDisplay, document);
      applyClientReturnSlipArchiveDisplayFields(docToDisplay, document);
      applyCreditNoteArchiveDisplayFields(docToDisplay, document);
      applyArchivePaymentVoucherDisplayFields(docToDisplay, document);
      applyArchivePaymentReceiptDisplayFields(docToDisplay, document);

      return { docToDisplay, clientData, convertedType };
  };

  const handleGeneratePDF = async (document) => {
    try {
      setIsGeneratingPDF(true);

      const { docToDisplay, clientData, convertedType } = prepareArchiveDocumentForPdf(document);

      // Set the document for the hidden DocumentPreview component
      setDocumentToPrint({
        document: docToDisplay,
        client: clientData,
        type: convertedType
      });
      
      // Use a timeout to allow the DocumentPreview component to render
      setTimeout(async () => {
        try {
          // Use the direct downloadDocumentPDF function
          await downloadDocumentPDF(convertedType, docToDisplay, clientData, settings);
          
          setTimeout(() => {
            setIsGeneratingPDF(false);
            setDocumentToPrint(null);
          }, 500);
        } catch (error) {
          console.error('Error generating PDF:', error);
          setIsGeneratingPDF(false);
          setDocumentToPrint(null);
          alert(p('download_error'));
        }
      }, 600);
    } catch (error) {
      console.error('Error preparing PDF generation:', error);
      setIsGeneratingPDF(false);
      setDocumentToPrint(null);
      alert(p('prepare_error'));
    }
  };

  const handleView = (document) => {
    try {
      // Find client data for this document
      const clientData = findClientData(document);
      
      // Convert the document type
      const convertedType = convertDocumentType(document.type);
      
      // Fix partner numbering before displaying document
      let docToDisplay = {...document};
      
      // Transfer document detail fields from database to display fields
      // These mappings ensure document detail fields appear in the archive view
      const detailFieldsMapping = {
        payment_terms: 'paymentTerms',
        delivery_date: 'deliveryDate',
        delivery_location: 'deliveryLocation',
        delivery_place: 'deliveryPlace',
        deliveryPlace: 'deliveryPlace',
        // Also map delivery_address to deliveryAddress for backward compatibility
        delivery_address: 'deliveryAddress', // Used in purchase-order template
        created_by: 'issuedBy', // Map to the field name used in templates
        createdBy: 'issuedBy',   // Handle camelCase stored value
        buyer: 'acheteur', // Map to French field name used in templates
        buyer: 'buyer', // Also map to English field name
        
        // Quote request specific fields
        requestType: 'requestType',
        responseDate: 'responseDate',
        desiredStartDate: 'desiredStartDate',
        request_type: 'requestType',
        response_date: 'responseDate',
        desired_start_date: 'desiredStartDate',
        
        // Quote request / proforma request detail fields
        project_reference: 'projectReference',
        desired_delivery_time: 'desiredDeliveryTime',
        required_documents: 'requiredDocuments',
        estimated_delivery_time: 'estimatedDeliveryTime',
        desired_payment_terms: 'desiredPaymentTerms',
        declared_value: 'declaredValue',
        priority: 'priority',
        commercial: 'commercial',
        response_deadline: 'responseDeadline',
        
        // Return slip specific fields
        returnDate: 'returnDate',
        returnReason: 'returnReason',
        returnType: 'returnType',
        returnCarrier: 'returnCarrier',
        returnCondition: 'returnCondition',
        returnInspector: 'returnInspector',
        returnStatus: 'returnStatus',
        returnOriginalDelivery: 'returnOriginalDelivery',
        returnOriginalInvoice: 'returnOriginalInvoice',
        returnOriginalInvoiceDate: 'returnOriginalInvoiceDate',
        
        // Also include underscore versions for backward compatibility
        return_date: 'returnDate',
        return_reason: 'returnReason',
        return_type: 'returnType',
        return_carrier: 'returnCarrier',
        return_condition: 'returnCondition',
        return_inspector: 'returnInspector',
        return_status: 'returnStatus',
        return_original_delivery: 'returnOriginalDelivery',
        return_original_invoice: 'returnOriginalInvoice',
        return_original_invoice_date: 'returnOriginalInvoiceDate',
        
        // Reception note specific fields
        receptionAddress: 'receptionAddress',
        receptionDeliveryNote: 'receptionDeliveryNote',
        receptionOrder: 'receptionOrder',
        receptionDate: 'receptionDate',
        receptionTime: 'receptionTime',
        receptionReceivedBy: 'receptionReceivedBy',
        receptionControlledBy: 'receptionControlledBy',
        receptionOverallCondition: 'receptionOverallCondition',
        receptionQuantityCompliance: 'receptionQuantityCompliance',
        receptionQualityCompliance: 'receptionQualityCompliance',
        receptionStockDestination: 'receptionStockDestination',
        receptionStatus: 'receptionStatus',
        
        // Reception note fields with underscores for backward compatibility
        reception_address: 'receptionAddress',
        reception_delivery_note: 'receptionDeliveryNote',
        reception_order: 'receptionOrder',
        reception_date: 'receptionDate',
        reception_time: 'receptionTime',
        reception_received_by: 'receptionReceivedBy',
        reception_controlled_by: 'receptionControlledBy',
        reception_overall_condition: 'receptionOverallCondition',
        reception_quantity_compliance: 'receptionQuantityCompliance',
        reception_quality_compliance: 'receptionQualityCompliance',
        reception_stock_destination: 'receptionStockDestination',
        reception_status: 'receptionStatus',
        
        // Invoice specific fields
        payment_method: 'paymentMethod',
        due_date: 'dueDate',
        issued_by: 'issuedBy', 
        sales_person: 'salesPerson',
        bank_details: 'bankDetails',
        amount_in_words: 'amountInWords',
        
        // Proforma specific fields
        valid_until: 'validUntil',
        estimated_delivery: 'estimatedDelivery',
        
        // Quote specific fields
        delivery_time: 'deliveryTime',
        project_type: 'projectType',
        payment_conditions: 'paymentConditions',
        conditions: 'conditions',
        
        // Shipping slip specific fields
        shipment_date: 'shipmentDate',
        shipment_method: 'shipmentMethod',
        tracking_number: 'trackingNumber',
        carrier_tracking_number: 'carrierTrackingNumber',
        total_weight: 'totalWeight',
        package_count: 'packageCount',
        package_type: 'packageType',
        instructions: 'instructions',
        
        // Delivery note specific fields
        delivery_date: 'deliveryDate',
        delivery_time: 'deliveryTime',
        order_reference: 'orderReference',
        purchase_order_reference: 'orderReference',
        invoice_reference: 'invoiceReference',
        document_reference: 'documentReference',
        origin_warehouse_name: 'departureWarehouse',
        carrier: 'carrier',
        driver: 'driver',
        vehicle_type: 'vehicleType',
        vehicle_registration: 'vehicleRegistration',
        delivery_conditions: 'deliveryConditions',
        package_count: 'packageCount',
        expected_delivery_date: 'expectedDeliveryDate',

        // Client return slip (shared return_* columns)
        return_original_purchase_order: 'returnOriginalPurchaseOrder',
        return_warehouse: 'returnWarehouse',
        controlled_by_name: 'controlledByName',

        // Logistics / inventory (Bon réception, entrée, sortie, transfert, expédition)
        warehouse_name: 'warehouseName',
        entry_date: 'entryDate',
        entry_time: 'entryTime',
        entry_source: 'entrySource',
        entry_document_reference: 'entryDocumentReference',
        expected_entry_date: 'expectedEntryDate',
        expected_reception_date: 'expectedReceptionDate',
        origin_warehouse_name: 'originWarehouseName',
        destination_warehouse_name: 'destinationWarehouseName',
        departure_time: 'departureTime',
        arrival_time: 'arrivalTime',
        shipment_mode: 'shipmentMode',
        delivery_note_reference: 'deliveryNoteReference',
        general_condition: 'generalCondition',
        quantity_conformity: 'quantityConformity',
        quality_conformity: 'qualityConformity',
        stock_destination: 'stockDestination',
        transfer_requested_by_name: 'transferRequestedByName',
        transfer_responsible_name: 'transferResponsibleName',
        approved_by_name: 'approvedByName',
        received_by_name: 'receivedByName',
        supplier_name: 'supplierName',
        goods_condition: 'goodsCondition',
        receiver_address: 'receiverAddress',
      };
      
      // Copy fields from database names to display names
      Object.entries(detailFieldsMapping).forEach(([dbField, displayField]) => {
        if (document[dbField] && !docToDisplay[displayField]) {
          docToDisplay[displayField] = document[dbField];
        }
      });
      
      // If document has document_options as JSON string, parse it
      if (document.document_options && typeof document.document_options === 'string') {
        try {
          const options = JSON.parse(document.document_options);
          // Apply display options
          Object.assign(docToDisplay, options);
        } catch (e) {
          console.error('Failed to parse document options:', e);
        }
      }
      
      // Special handling for delivery location field which might be stored in various formats
      if (!docToDisplay.deliveryLocation && !docToDisplay.deliveryAddress && !docToDisplay.deliveryPlace) {
        // Try to find delivery location in any possible format
        if (document.delivery_location) {
          docToDisplay.deliveryLocation = document.delivery_location;
        } else if (document.deliveryLocation) {
          docToDisplay.deliveryLocation = document.deliveryLocation;
        } else if (document.delivery_place) {
          docToDisplay.deliveryPlace = document.delivery_place;
          docToDisplay.deliveryLocation = document.delivery_place;
        } else if (document.deliveryPlace) {
          docToDisplay.deliveryPlace = document.deliveryPlace;
          docToDisplay.deliveryLocation = document.deliveryPlace;
        } else if (document.deliveryAddress) {
          docToDisplay.deliveryAddress = document.deliveryAddress;
        } else if (document.delivery_address) {
          docToDisplay.deliveryAddress = document.delivery_address;
        } else if (document.lieu_livraison) {
          docToDisplay.deliveryLocation = document.lieu_livraison;
        } else if (document.lieuLivraison) {
          docToDisplay.deliveryLocation = document.lieuLivraison;
        }
      } else if (!docToDisplay.deliveryLocation && docToDisplay.deliveryPlace) {
        docToDisplay.deliveryLocation = docToDisplay.deliveryPlace;
      } else if (!docToDisplay.deliveryPlace && docToDisplay.deliveryLocation) {
        docToDisplay.deliveryPlace = docToDisplay.deliveryLocation;
      }
      
      // Special handling for return slip fields stored in the return object
      if (document.type === 'return_slip' || document.type === 'return-slip') {
        // If return object exists, extract its fields
        if (document.return && typeof document.return === 'object') {
          // Map return object fields to top-level fields for display
          if (!docToDisplay.returnDate && document.return.date) {
            docToDisplay.returnDate = document.return.date;
          }
          if (!docToDisplay.returnReason && document.return.reason) {
            docToDisplay.returnReason = document.return.reason;
          }
          if (!docToDisplay.returnType && document.return.type) {
            docToDisplay.returnType = document.return.type;
          }
          if (!docToDisplay.returnCarrier && document.return.carrier) {
            docToDisplay.returnCarrier = document.return.carrier;
          }
          if (!docToDisplay.returnCondition && document.return.condition) {
            docToDisplay.returnCondition = document.return.condition;
          }
          if (!docToDisplay.returnInspector && document.return.inspector) {
            docToDisplay.returnInspector = document.return.inspector;
          }
          if (!docToDisplay.returnStatus && document.return.status) {
            docToDisplay.returnStatus = document.return.status;
          }
          if (!docToDisplay.returnOriginalDelivery && document.return.originalDeliveryRef) {
            docToDisplay.returnOriginalDelivery = document.return.originalDeliveryRef;
          }
          if (!docToDisplay.returnOriginalInvoice && document.return.originalInvoiceRef) {
            docToDisplay.returnOriginalInvoice = document.return.originalInvoiceRef;
          }
        }
      }
      
      // Special handling for reception note fields stored in the reception object
      if (document.type === 'reception_note' || document.type === 'reception-note') {
        if (!docToDisplay.receptionOrderRef) {
          docToDisplay.receptionOrderRef =
            document.purchaseOrderReference ||
            document.purchase_order_reference ||
            docToDisplay.receptionOrder ||
            document.receptionOrder ||
            document.reception_order ||
            '';
        }
        if (!docToDisplay.receptionDeliveryNoteRef) {
          docToDisplay.receptionDeliveryNoteRef =
            document.deliveryNoteReference ||
            document.delivery_note_reference ||
            docToDisplay.receptionDeliveryNote ||
            document.receptionDeliveryNote ||
            document.reception_delivery_note ||
            '';
        }
        if (!docToDisplay.receiverAddress) {
          docToDisplay.receiverAddress =
            document.receptionAddress ||
            document.reception_address ||
            docToDisplay.receptionAddress ||
            '';
        }
        if (!docToDisplay.receiverAddress) {
          docToDisplay.receiverAddress =
            document.receiverAddress ||
            document.receiver_address ||
            docToDisplay.receptionAddress ||
            document.receptionAddress ||
            document.reception_address ||
            '';
        }
        if (!docToDisplay.expectedReceptionDate) {
          docToDisplay.expectedReceptionDate =
            document.expectedReceptionDate ||
            document.expected_reception_date ||
            docToDisplay.expectedDeliveryDate ||
            document.expectedDeliveryDate ||
            document.expected_delivery_date ||
            docToDisplay.receptionDate ||
            document.reception_date ||
            document.date ||
            '';
        }
        // If reception object exists, extract its fields
        if (document.reception && typeof document.reception === 'object') {
          // Map reception object fields to top-level fields for display
          if (!docToDisplay.receptionAddress && document.reception.receiverAddress) {
            docToDisplay.receptionAddress = document.reception.receiverAddress;
          }
          if (!docToDisplay.receptionDeliveryNote && document.reception.deliveryNoteRef) {
            docToDisplay.receptionDeliveryNote = document.reception.deliveryNoteRef;
          }
          if (!docToDisplay.receptionOrder && document.reception.orderRef) {
            docToDisplay.receptionOrder = document.reception.orderRef;
          }
          if (!docToDisplay.receptionDate && document.reception.date) {
            docToDisplay.receptionDate = document.reception.date;
          }
          if (!docToDisplay.receptionTime && document.reception.time) {
            docToDisplay.receptionTime = document.reception.time;
          }
          if (!docToDisplay.receptionReceivedBy && document.reception.receivedBy) {
            docToDisplay.receptionReceivedBy = document.reception.receivedBy;
          }
          if (!docToDisplay.receptionControlledBy && document.reception.controlledBy) {
            docToDisplay.receptionControlledBy = document.reception.controlledBy;
          }
          if (!docToDisplay.receptionOverallCondition && document.reception.overallCondition) {
            docToDisplay.receptionOverallCondition = document.reception.overallCondition;
          }
          if (!docToDisplay.receptionQuantityCompliance && document.reception.quantityCompliance) {
            docToDisplay.receptionQuantityCompliance = document.reception.quantityCompliance;
          }
          if (!docToDisplay.receptionQualityCompliance && document.reception.qualityCompliance) {
            docToDisplay.receptionQualityCompliance = document.reception.qualityCompliance;
          }
          if (!docToDisplay.receptionStockDestination && document.reception.stockDestination) {
            docToDisplay.receptionStockDestination = document.reception.stockDestination;
          }
          if (!docToDisplay.receptionStatus && document.reception.status) {
            docToDisplay.receptionStatus = document.reception.status;
          }
        }
      }
      
      // Special handling for shipping slip fields stored in the shipment object
      if (document.type === 'shipping' || document.type === 'shipping-slip') {
        // If shipment object exists, extract its fields
        if (document.shipment && typeof document.shipment === 'object') {
          // Map shipment object fields to top-level fields for display
          if (!docToDisplay.shipmentDate && document.shipment.date) {
            docToDisplay.shipmentDate = document.shipment.date;
          }
          if (!docToDisplay.shipmentMethod && document.shipment.method) {
            docToDisplay.shipmentMethod = document.shipment.method;
          }
          if (!docToDisplay.trackingNumber && document.shipment.trackingNumber) {
            docToDisplay.trackingNumber = document.shipment.trackingNumber;
          }
          if (!docToDisplay.totalWeight && document.shipment.totalWeight) {
            docToDisplay.totalWeight = document.shipment.totalWeight;
          }
          if (!docToDisplay.packageCount && document.shipment.packageCount) {
            docToDisplay.packageCount = document.shipment.packageCount;
          }
          if (!docToDisplay.packageType && document.shipment.packageType) {
            docToDisplay.packageType = document.shipment.packageType;
          }
        }
      }
      
      // Special handling for delivery note fields stored in the delivery object
      if (document.type === 'delivery' || document.type === 'delivery-note') {
        // If delivery object exists, extract its fields
        if (document.delivery && typeof document.delivery === 'object') {
          // Map delivery object fields to top-level fields for display
          if (!docToDisplay.deliveryDate && document.delivery.date) {
            docToDisplay.deliveryDate = document.delivery.date;
          }
          if (!docToDisplay.deliveryTime && document.delivery.time) {
            docToDisplay.deliveryTime = document.delivery.time;
          }
          if (!docToDisplay.orderReference && document.delivery.orderReference) {
            docToDisplay.orderReference = document.delivery.orderReference;
          }
          if (!docToDisplay.carrier && document.delivery.carrier) {
            docToDisplay.carrier = document.delivery.carrier;
          }
          if (!docToDisplay.driver && document.delivery.driver) {
            docToDisplay.driver = document.delivery.driver;
          }
          if (!docToDisplay.vehicleType && document.delivery.vehicleType) {
            docToDisplay.vehicleType = document.delivery.vehicleType;
          }
          if (!docToDisplay.vehicleRegistration && document.delivery.vehicleRegistration) {
            docToDisplay.vehicleRegistration = document.delivery.vehicleRegistration;
          }
          if (!docToDisplay.deliveryConditions && document.delivery.conditions) {
            docToDisplay.deliveryConditions = document.delivery.conditions;
          }
        }
      }
      
      // If document doesn't have a partnerNumber or has incorrect raw ID format, generate one now
      const hasIncorrectPartnerNumber = docToDisplay.partnerNumber && (
        (docToDisplay.partnerNumber.includes('CLI-') && docToDisplay.partnerNumber.length > 10) ||
        (docToDisplay.partnerNumber.includes('FRN-') && docToDisplay.partnerNumber.length > 10)
      );
      
      if (!docToDisplay.partnerNumber || hasIncorrectPartnerNumber) {
        const isSupplierDocument = 
          document.type === 'purchase_order' || 
          document.type === 'quote_request' || 
          document.type === 'reception_note' || 
          document.type === 'return_slip' ||
          document.type === 'purchase-order' || 
          document.type === 'quote-request' || 
          document.type === 'reception-note' || 
          document.type === 'return-slip';
          
        if (clientData) {
          const oldPartnerNumber = docToDisplay.partnerNumber;
          docToDisplay.partnerNumber = formatPartnerNumber(clientData, isSupplierDocument);
          console.log("?? Archives: Fixed partner number for View - OLD:", oldPartnerNumber, "NEW:", docToDisplay.partnerNumber, "for", isSupplierDocument ? 'supplier' : 'client', clientData.name);
        }
      }
      
      // Clean up the document before sending it to the preview
      if (clientData) {
        // Ensure partner data is properly formatted
        if (clientData.clientNumber && clientData.clientNumber.startsWith('CLI-')) {
          // Extract just the number part if client number already has prefix
          clientData.clientNumber = clientData.clientNumber.replace('CLI-', '');
        }
      }

      applyArchiveQuoteBudgetFields(docToDisplay, document);
      applyArchiveSupplementaryAndUiOverrides(docToDisplay, document);
      applyArchivePurchaseOrderRefFields(docToDisplay, document);
      applyArchivePurchaseOrderDetailFields(docToDisplay, document);
      applyArchiveQuoteDisplayFields(docToDisplay, document);
      applyArchiveInvoiceDisplayFields(docToDisplay, document);
      applyArchiveDeliveryDisplayFields(docToDisplay, document);
      applyArchiveReceptionDisplayFields(docToDisplay, document);
      applyArchiveStockEntryDisplayFields(docToDisplay, document);
      applyArchiveExitSlipDisplayFields(docToDisplay, document);
      applyArchiveTransferSlipDisplayFields(docToDisplay, document);
      applyArchiveShippingSlipDisplayFields(docToDisplay, document);
      applyReturnSlipArchiveDisplayFields(docToDisplay, document);
      applyClientReturnSlipArchiveDisplayFields(docToDisplay, document);
      applyCreditNoteArchiveDisplayFields(docToDisplay, document);
      applyArchivePaymentVoucherDisplayFields(docToDisplay, document);
      applyArchivePaymentReceiptDisplayFields(docToDisplay, document);
      docToDisplay.title = getDocumentDisplayTitle({ ...document, ...docToDisplay });

      // Set the document for the preview
      setDocumentToPrint({
        document: docToDisplay,
        client: clientData,
        type: convertedType
      });
      
      // Set the document to preview and show modal
      setPreviewDocument(docToDisplay);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Error preparing document preview:', error);
      alert(p('preview_error'));
    }
  };

  // Stats calculation
  // Helper to safely parse numeric values (same as below but defined once)
  const parseNumeric = (val) => {
    if (val === undefined || val === null) return 0;
    if (typeof val === 'number') return val;
    const sanitized = String(val).replace(/[^0-9,.-]/g, '').replace(',', '.');
    const n = Number(sanitized);
    return isNaN(n) ? 0 : n;
  };

  const stats = {
    total: scopedDocuments.length,
    invoices: scopedDocuments.filter(d => d.type === 'invoice').length,
    quotes: scopedDocuments.filter(d => d.type === 'quote').length,
    totalAmount: scopedDocuments.reduce((sum, d) => sum + resolveArchiveDocumentAmount(d), 0),
    thisMonth: scopedDocuments.filter(d => {
      const docDate = new Date(d.date);
      const now = new Date();
      return docDate.getMonth() === now.getMonth() && docDate.getFullYear() === now.getFullYear();
    }).length
  };

  const filteredTotalAmount = useMemo(() => {
    return filteredDocuments.reduce((sum, d) => sum + resolveArchiveDocumentAmount(d), 0);
  }, [filteredDocuments]);

  const activeStatusDocument = useMemo(() => {
    if (!activeStatusMenu) return null;
    return scopedDocuments.find((d) => d.id === activeStatusMenu) || null;
  }, [activeStatusMenu, scopedDocuments]);

  const activeStatusOrderedStatuses = useMemo(() => {
    if (!activeStatusDocument) return [];
    return getOrderedArchiveAllowedStatuses(activeStatusDocument);
  }, [activeStatusDocument, documentTypes]);

  const handleToggleStatusMenu = (doc, buttonEl) => {
    if (activeStatusMenu === doc.id) {
      setActiveStatusMenu(null);
      setStatusDropdownPosition(null);
      return;
    }
    if (buttonEl) {
      setStatusDropdownPosition(computeArchiveStatusDropdownPosition(buttonEl));
      setActiveStatusMenu(doc.id);
    }
  };

  const handleStatusChange = (document, newStatus) => {
    try {
      console.log('?? Archives.handleStatusChange:', {
        documentId: document.id,
        documentType: document.type,
        currentStatus: document.status,
        newStatus: newStatus
      });
      
      const archiveTypeKey = resolveArchiveDocTypeKeyForStatuses(document);
      const docTypeInfo =
        documentTypes[archiveTypeKey] ||
        documentTypes[document.type] ||
        documentTypes.default;
      const allowed = docTypeInfo.allowedStatuses || [];

      // Check if the new status is allowed for this document type
      if (!allowed.includes(newStatus)) {
        console.warn(`Status ${newStatus} not allowed for document type ${document.type}`);
        return;
      }
      
      // Close the status menu
      setActiveStatusMenu(null);
      setStatusDropdownPosition(null);
      
      // Update the document with new status - ONLY send status field
      const statusUpdate = { status: newStatus };
      
      console.log('?? Archives calling updateDocument with:', statusUpdate);
      
      updateDocument(document.id, statusUpdate).then(() => {
        console.log('? Status update successful');

        const statusLabel =
          getStatusLabelForDocumentType(document.type, newStatus) ||
          statusConfig[newStatus]?.label ||
          newStatus;
        const displayTypeKey =
          resolvePaymentDocumentDisplayType(document) ?? document.type;

        // Même carte « Succès » + PDF / Créer un autre que après sauvegarde depuis création document
        setToast({
          visible: true,
          message: `${statusLabel} — ${p('toast_statut_saved_suffix')}`,
          status: 'success',
          documentId: document.id,
          documentNumber: document.number,
          documentType: displayTypeKey,
          showActions: true,
        });

        setTimeout(() => {
          setToast((prev) => ({ ...prev, visible: false }));
        }, 5000);
      }).catch(error => {
        console.error('? Status update failed:', error);
        
        // Show error toast
        setToast({
          visible: true,
          message: p('status_change_error'),
          status: 'error'
        });
        
        setTimeout(() => {
          setToast(prev => ({ ...prev, visible: false }));
        }, 3000);
      });
      
    } catch (error) {
      console.error('Error in handleStatusChange:', error);
    }
  };
  
  // Handler for deleting a document
  const handleDeleteDocument = (document) => {
    try {
      // Delete the document
      deleteDocument(document.id);
      
      // Remove from selected documents if it was selected
      if (selectedDocuments.includes(document.id)) {
        setSelectedDocuments(selectedDocuments.filter(id => id !== document.id));
      }
      
      // Show toast notification
      setToast({
        visible: true,
        message: p('document_deleted_with_title', {
          title: document.title || document.number,
        }),
        status: 'deleted'
      });
      
      // Hide toast after 3 seconds
      setTimeout(() => {
        setToast(prev => ({ ...prev, visible: false }));
      }, 3000);
    } catch (error) {
      console.error('Error deleting document:', error);
    }
  };

  // Toggle selection of a single document
  const handleSelectDocument = (id) => {
    setActiveStatusMenu(null);
    setStatusDropdownPosition(null);
    setSelectedDocuments(prev => {
      if (prev.includes(id)) {
        return prev.filter(docId => docId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Toggle selection of all documents on current page
  const handleSelectAll = () => {
    setActiveStatusMenu(null);
    setStatusDropdownPosition(null);
    const pageIds = currentDocuments.map((doc) => doc.id);
    const allOnPage =
      pageIds.length > 0 && pageIds.every((id) => selectedDocuments.includes(id));
    if (allOnPage) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(pageIds);
    }
  };

  // Handle bulk deletion of documents
  const handleBulkDelete = () => {
    if (
      window.confirm(
        p('bulk_delete_confirm', { count: selectedDocuments.length }),
      )
    ) {
      selectedDocuments.forEach((id) => {
        deleteDocument(id);
      });

      setToast({
        visible: true,
        message: p('bulk_deleted_count', { count: selectedDocuments.length }),
        status: 'deleted',
      });

      setSelectedDocuments([]);

      setTimeout(() => {
        setToast((prev) => ({ ...prev, visible: false }));
      }, 3000);
    }
  };

  // Bulk print: même rendu `.invoice-a4-page` que le bouton téléchargement PDF, fusionné dans une seule fenêtre d'impression
  const handleBulkPrint = async () => {
    if (selectedDocuments.length === 0) return;

    if (selectedDocuments.length > 10) {
      if (
        !window.confirm(
          p('bulk_print_confirm', { count: selectedDocuments.length }),
        )
      ) {
        return;
      }
    }

    const docsToPrint = scopedDocuments.filter((doc) => selectedDocuments.includes(doc.id));
    if (docsToPrint.length === 0) return;

    try {
      setIsGeneratingPDF(true);
      setToast({
        visible: true,
        message: p('bulk_print_preparing', { count: docsToPrint.length }),
        status: 'pending',
      });

      const fragments = [];
      await document.fonts.ready.catch(() => {});

      for (const raw of docsToPrint) {
        const { docToDisplay, clientData, convertedType } = prepareArchiveDocumentForPdf(raw);

        flushSync(() => {
          setDocumentToPrint({
            document: docToDisplay,
            client: clientData,
            type: convertedType,
          });
        });

        const deadline = Date.now() + 15000;
        let found = false;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 60));
          const nodes = document.querySelectorAll('.invoice-a4-page');
          if (nodes.length > 0) {
            nodes.forEach((n) => fragments.push(n.outerHTML));
            found = true;
            break;
          }
        }

        if (!found) {
          console.warn('Archives bulk print: no .invoice-a4-page for', raw?.id, raw?.type);
        }
      }

      flushSync(() => {
        setDocumentToPrint(null);
      });
      setIsGeneratingPDF(false);

      if (fragments.length === 0) {
        setToast({
          visible: true,
          message: p('print_no_compatible'),
          status: 'overdue',
        });
        setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 4500);
        return;
      }

      const title =
        docsToPrint.length === 1 && docsToPrint[0]?.number
          ? String(docsToPrint[0].number)
          : p('print_bulk_title', { count: docsToPrint.length });

      const opened = printMergedInvoicePagesHTML(fragments.join('\n'), title);
      setToast({
        visible: true,
        message: opened ? p('print_dialog_opened') : p('print_batch_failed'),
        status: opened ? 'completed' : 'overdue',
      });
      setTimeout(
        () => setToast((prev) => ({ ...prev, visible: false })),
        opened ? 3500 : 5500
      );
    } catch (error) {
      console.error('Error printing documents:', error);
      setIsGeneratingPDF(false);
      flushSync(() => setDocumentToPrint(null));
      setToast({
        visible: true,
        message: p('print_error'),
        status: 'overdue',
      });
      setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 4000);
    }
  };

  // Handle single document PDF download
  const handleDownloadPDF = async (document) => {
    try {
      const clientData = findClientData(document);
      const convertedType = convertDocumentType(document.type);
      await downloadDocumentPDF(convertedType, document, clientData, settings);
    } catch (error) {
      console.error('Error downloading document:', error);
      setToast({
        visible: true,
        message: p('download_doc_error'),
        status: 'error'
      });
      setTimeout(() => {
        setToast(prev => ({ ...prev, visible: false }));
      }, 3000);
    }
  };

  // Handle bulk download of documents
  const handleBulkDownload = async () => {
    if (selectedDocuments.length > 5) {
      if (!window.confirm(p('bulk_download_confirm', { count: selectedDocuments.length }))) {
        return;
      }
    }
    
    try {
      setIsGeneratingPDF(true);
      setToast({
        visible: true,
        message: p('bulk_download_preparing', { count: selectedDocuments.length }),
        status: 'pending'
      });
      
      // Get selected documents
      const docsToDownload = scopedDocuments.filter(doc => selectedDocuments.includes(doc.id));
      
      // Download each document in sequence
      for (let i = 0; i < docsToDownload.length; i++) {
        const doc = docsToDownload[i];
        const clientData = findClientData(doc);
        const convertedType = convertDocumentType(doc.type);
        
        // Update toast with progress
        setToast({
          visible: true,
          message: p('bulk_download_progress', {
            current: i + 1,
            total: docsToDownload.length,
          }),
          status: 'pending'
        });
        
        // Download document
        await downloadDocumentPDF(convertedType, doc, clientData, settings);
        
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      setIsGeneratingPDF(false);
      setToast({
        visible: true,
        message: p('bulk_download_success', { count: selectedDocuments.length }),
        status: 'completed'
      });
      setTimeout(() => {
        setToast(prev => ({ ...prev, visible: false }));
      }, 3000);
    } catch (error) {
      console.error('Error downloading documents:', error);
      setIsGeneratingPDF(false);
      setToast({
        visible: true,
        message: p('download_docs_error'),
        status: 'overdue'
      });
    }
  };

  // Dynamic distribution calculation based on view mode
  const getDistributionData = () => {
    switch (mainViewMode) {
      case 'par_client':
        // Client types statistics (Entreprise, Particulier, Institution)
        const entrepriseClients = facturationDocuments.filter(
          (doc) => resolveArchiveDocumentClientCategory(doc) === 'entreprise',
        ).length;

        const institutionClients = facturationDocuments.filter(
          (doc) => resolveArchiveDocumentClientCategory(doc) === 'institution',
        ).length;

        const particulierClients = facturationDocuments.filter(
          (doc) => resolveArchiveDocumentClientCategory(doc) === 'particulier',
        ).length;
        
        return {
          title: p('client_types'),
          data: [
            { 
              name: p('entreprise_clients'),
              count: entrepriseClients,
              color: 'bg-blue-500'
            },
            { 
              name: p('particulier_clients'),
              count: particulierClients,
              color: 'bg-purple-500'
            },
            { 
              name: p('institution_clients'),
              count: institutionClients,
              color: 'bg-green-500'
            }
          ]
        };

      case 'par_operation':
        // Operations statistics - Create diversified operations
        let operationData = [
          { name: p('business_operations'), count: 0, color: 'bg-blue-500' },
          { name: p('monthly_operations'), count: 0, color: 'bg-purple-500' },
          { name: p('conversion_operations'), count: 0, color: 'bg-green-500' }
        ];
        
        // Count business operations (explicit operationId)
        const businessOpsCount = facturationDocuments.filter(doc => 
          doc.operationId && doc.operationId.startsWith('OP-')
        ).length;
        
        // Count monthly operations (by month groupings)
        const monthlyGroups = {};
        documents.forEach(doc => {
          if (doc.client && doc.date) {
            const date = new Date(doc.date);
            if (!isNaN(date)) {
              const monthKey = `${date.getMonth()}-${date.getFullYear()}`;
              monthlyGroups[monthKey] = (monthlyGroups[monthKey] || 0) + 1;
            }
          }
        });
        const monthlyOpsCount = Object.values(monthlyGroups).reduce((sum, count) => sum + count, 0);
        
        // Count conversion operations (documents with sourceDocumentId)
        const conversionOpsCount = facturationDocuments.filter(doc => doc.sourceDocumentId).length;
        
        operationData[0].count = businessOpsCount;
        operationData[1].count = monthlyOpsCount;
        operationData[2].count = conversionOpsCount;
        
        // Filter out operations with 0 count and ensure we have at least some data
        operationData = operationData.filter(op => op.count > 0);
        if (operationData.length === 0) {
          operationData = [{ name: p('no_operations'), count: 0, color: 'bg-gray-500' }];
        }
        
        return {
          title: p('operations_distribution'),
          data: operationData
        };

      case 'par_type':
        // Document type distribution - Ventes vs Achats (business analysis)
        const purchaseOrders = facturationDocuments.filter(doc => 
          doc.type === 'purchase_order' || doc.type === 'purchase-order'
        ).length;
        
        return {
          title: p('sales_purchases_distribution'),
          data: [
            { 
              name: p('invoices_sales'),
              count: stats.invoices,
              color: 'bg-blue-500'
            },
            { 
              name: p('purchase_orders_achats'),
              count: purchaseOrders,
              color: 'bg-purple-500'
            },
            { 
              name: p('other_documents'),
              count: documents.length - stats.invoices - purchaseOrders,
              color: 'bg-green-500'
            }
          ]
        };

      case 'all_documents':
      default:
        // General document distribution (overview of all document types)
        const purchaseOrdersGeneral = facturationDocuments.filter(doc => 
          doc.type === 'purchase_order' || doc.type === 'purchase-order'
        ).length;
        
        return {
          title: p('document_overview'),
          data: [
            { 
              name: p('invoices'),
              count: stats.invoices,
              color: 'bg-blue-500'
            },
            { 
              name: p('purchase_orders'),
              count: purchaseOrdersGeneral,
              color: 'bg-purple-500'
            },
            { 
              name: p('other_documents'),
              count: documents.length - stats.invoices - purchaseOrdersGeneral,
              color: 'bg-green-500'
            }
          ]
        };
    }
  };

  /** Notifications Import / Export — même variante « cta » que la sauvegarde document (sans documentId ⇒ pas de boutons PDF). */
  const archivesImportExportToastMs = 5000;

  const showArchivesImportExportSuccessToast = (message) => {
    setToast({
      visible: true,
      message,
      status: 'success',
      showActions: true,
    });
    window.setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), archivesImportExportToastMs);
  };

  const showArchivesImportExportErrorToast = (message) => {
    setToast({
      visible: true,
      message,
      status: 'error',
      showActions: false,
    });
    window.setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), archivesImportExportToastMs);
  };

  // ─── Export handlers (comprehensive) ───────────────────────────────────────

  const handleExportCSV = () => {
    try {
      const headers = exportColumnHeaders();
      const rows = filteredDocuments.map(doc => [
        doc.reference || '',
        translateDocumentType(doc.type),
        doc.client || '',
        new Date(doc.createdAt).toLocaleDateString(i18nInstance.language || i18n.language || 'ar'),
        doc.totalAmount || 0,
        resolveExportStatusLabel(doc.status),
      ]);
      const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `archives-documents-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showArchivesImportExportSuccessToast(p('export_csv_success'));
    } catch (error) {
      console.error('CSV export error:', error);
      showArchivesImportExportErrorToast(p('export_csv_error'));
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const XLSX = await import('xlsx');
      const wsData = [
        exportColumnHeaders(),
        ...filteredDocuments.map(doc => [
          doc.reference || '',
          translateDocumentType(doc.type),
          doc.client || '',
          new Date(doc.createdAt).toLocaleDateString(i18nInstance.language || i18n.language || 'ar'),
          doc.totalAmount || 0,
          resolveExportStatusLabel(doc.status),
        ])
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, p('export_sheet_name'));
      XLSX.writeFile(wb, `archives-documents-${new Date().toISOString().split('T')[0]}.xlsx`);
      showArchivesImportExportSuccessToast(p('export_excel_success'));
    } catch (error) {
      console.error('Excel export error:', error);
      showArchivesImportExportErrorToast(p('export_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const docStatusLabel = (doc) => resolveExportStatusLabel(doc.status);
      await printListPdfWithInvoiceTemplate({
        settings,
        rows: filteredDocuments,
        columns: [
          { label: p('export_col_number'), value: (d) => d.reference },
          { label: p('export_col_type'), value: (d) => translateDocumentType(d.type) },
          { label: p('export_col_client'), value: (d) => d.client },
          { label: p('export_col_date'), value: (d) => formatUserDate(d.createdAt) },
          {
            label: p('export_col_amount'),
            align: 'right',
            value: (d) => formatBrandMoney(d.totalAmount || 0),
          },
          { label: p('export_col_status'), value: (d) => docStatusLabel(d) },
        ],
        listExportTitle: p('export_list_title'),
        clientTitle: p('export_client_title'),
        printDocumentTitle: p('export_pdf_document_title', {
          date: new Date().toISOString().split('T')[0],
        }),
      });
      showArchivesImportExportSuccessToast(p('export_pdf_success'));
    } catch (error) {
      console.error('PDF export error:', error);
      showArchivesImportExportErrorToast(p('export_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportJSON = () => {
    try {
      const dataStr = JSON.stringify(filteredDocuments, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `archives-documents-${new Date().toISOString().split('T')[0]}.json`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showArchivesImportExportSuccessToast(p('export_json_success'));
    } catch (error) {
      console.error('JSON export error:', error);
      showArchivesImportExportErrorToast(p('export_json_error'));
    }
  };

  const handleExportXML = () => {
    try {
      const xmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<archives>
${filteredDocuments.map(doc => `  <document>
    <reference>${doc.reference || ''}</reference>
    <type>${translateDocumentType(doc.type)}</type>
    <client>${doc.client || ''}</client>
    <date>${formatUserDate(doc.createdAt) || ''}</date>
    <amount>${doc.totalAmount || 0}</amount>
    <status>${resolveExportStatusLabel(doc.status)}</status>
  </document>`).join('\n')}
</archives>`;
      const blob = new Blob([xmlContent], { type: 'application/xml' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `archives-documents-${new Date().toISOString().split('T')[0]}.xml`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showArchivesImportExportSuccessToast(p('export_xml_success'));
    } catch (error) {
      console.error('XML export error:', error);
      showArchivesImportExportErrorToast(p('export_xml_error'));
    }
  };

  const handleExportHTML = () => {
    try {
      const exportHeaders = exportColumnHeaders();
      const [
        colNumber,
        colType,
        colClient,
        colDate,
        colAmount,
        colStatus,
      ] = exportHeaders;
      const pageTitle = p('export_client_title');
      const exportDateLabel = p('export_date_label');
      const htmlContent = `<!DOCTYPE html>
<html lang="${i18nInstance.language || 'ar'}">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${pageTitle}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    h1 { color: #333; }
    table { border-collapse: collapse; width: 100%; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #4CAF50; color: white; }
    tr:nth-child(even) { background-color: #f2f2f2; }
  </style>
</head>
<body>
  <h1>${pageTitle}</h1>
  <p>${exportDateLabel}: ${formatUserDateTime(new Date())}</p>
  <table>
    <thead>
      <tr>
        <th>${colNumber}</th>
        <th>${colType}</th>
        <th>${colClient}</th>
        <th>${colDate}</th>
        <th>${colAmount}</th>
        <th>${colStatus}</th>
      </tr>
    </thead>
    <tbody>
${filteredDocuments.map(doc => `      <tr>
        <td>${doc.reference || ''}</td>
        <td>${translateDocumentType(doc.type)}</td>
        <td>${doc.client || ''}</td>
        <td>${formatUserDate(doc.createdAt) || ''}</td>
        <td>${formatBrandMoney(doc.totalAmount || 0)}</td>
        <td>${resolveExportStatusLabel(doc.status)}</td>
      </tr>`).join('\n')}
    </tbody>
  </table>
</body>
</html>`;
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `archives-documents-${new Date().toISOString().split('T')[0]}.html`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showArchivesImportExportSuccessToast(p('export_html_success'));
    } catch (error) {
      console.error('HTML export error:', error);
      showArchivesImportExportErrorToast(p('export_html_error'));
    }
  };

  const handleExportTXT = () => {
    try {
      const [
        colNumber,
        colType,
        colClient,
        colDate,
        colAmount,
        colStatus,
      ] = exportColumnHeaders();
      const listTitle = p('export_list_title');
      const exportDateLabel = p('export_date_label');
      const txtContent = `${listTitle}
${exportDateLabel}: ${formatUserDateTime(new Date())}
${'='.repeat(80)}

${filteredDocuments.map(doc => `${colNumber}: ${doc.reference || ''}
${colType}: ${translateDocumentType(doc.type)}
${colClient}: ${doc.client || ''}
${colDate}: ${formatUserDate(doc.createdAt) || ''}
${colAmount}: ${formatBrandMoney(doc.totalAmount || 0)}
${colStatus}: ${resolveExportStatusLabel(doc.status)}
${'-'.repeat(80)}`).join('\n')}`;
      const blob = new Blob([txtContent], { type: 'text/plain' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `archives-documents-${new Date().toISOString().split('T')[0]}.txt`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showArchivesImportExportSuccessToast(p('export_txt_success'));
    } catch (error) {
      console.error('TXT export error:', error);
      showArchivesImportExportErrorToast(p('export_txt_error'));
    }
  };

  // ─── Import handlers (comprehensive) ────────────────────────────────────────

  const handleImportCSV = () => {
    const headers = [
      { key: 'type', label: p('import_col_type') },
      { key: 'number', label: p('import_col_number') },
      { key: 'title', label: p('import_col_title') },
      { key: 'client', label: p('export_col_client') },
      { key: 'date', label: p('export_col_date') },
      { key: 'amount', label: p('import_col_amount') },
      { key: 'status', label: p('export_col_status') },
      { key: 'description', label: p('import_col_description') }
    ];
    const schema = {
      type: (value) => value && ['invoice', 'quote', 'proforma', 'delivery', 'purchase_order', 'receipt_note', 'reception_note', 'stock_entry', 'return_slip', 'credit_note', 'payment_slip', 'payment_receipt', 'shipping_slip', 'exit_slip', 'transfer_slip', 'quote_request'].includes(value),
      number: (value) => value && value.toString().trim() !== '',
      title: (value) => value && value.toString().trim() !== '',
      client: (value) => value && value.toString().trim() !== '',
      date: (value) => value && !isNaN(Date.parse(value)),
      amount: (value) => !isNaN(parseFloat(value)) && parseFloat(value) >= 0,
      status: (value) => ['draft', 'pending', 'completed', 'paid', 'overdue', 'cancelled'].includes(value) || !value
    };
    triggerFileInput(async (file) => {
      try {
        const importResults = await importFromFile(file, headers, schema);
        if (importResults.success) {
          const newDocuments = importResults.data.map(doc => ({
            id: `DOC-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: doc.type,
            number: doc.number,
            title: doc.title,
            client: doc.client,
            date: doc.date,
            amount: parseFloat(doc.amount) || 0,
            totalAmount: parseFloat(doc.amount) || 0,
            status: doc.status || 'draft',
            description: doc.description || '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            items: [],
            taxRate: getBrandVatRate(),
            taxAmount: (parseFloat(doc.amount) || 0) * (getBrandVatRate() / 100),
            totalTTC: (parseFloat(doc.amount) || 0) * getBrandVatMultiplier(),
            currency: getBrandDefaultCurrency()
          }));
          let successCount = 0;
          for (const document of newDocuments) {
            try {
              await addDocument(document);
              successCount++;
            } catch (error) {
              console.error(`Error adding document ${document.number}:`, error);
            }
          }
          const skipped =
            newDocuments.length - successCount + (importResults.invalidCount || 0);
          let msg = `${p('import_success_intro')} : <strong>${successCount}</strong> ${p('import_documents_count')}.`;
          if (skipped > 0) {
            msg += `<br/><span class="text-white/80 text-[13px] font-normal">${p('import_partial_warning', {
              count: skipped,
            })}</span>`;
          }
          showArchivesImportExportSuccessToast(msg);
        } else {
          showArchivesImportExportErrorToast(
            `${p('import_validation_errors')} : ${importResults.errors.length} ${p('import_issues_detected')}.`,
          );
        }
      } catch (error) {
        console.error('Error during import:', error);
        showArchivesImportExportErrorToast(p('import_file_error'));
      }
    });
  };

  const handleImportExcel = () => handleImportCSV();
  const handleImportJSON = () => handleImportCSV();
  const handleImportXML = () => handleImportCSV();
  const handleImportTXT = () => handleImportCSV();

  const distributionInfo = getDistributionData();

  // Main view mode tabs definition
  const mainTabs = [
    { id: 'all_documents', name: p('overview'), icon: FileText },
    { id: 'par_client', name: p('by_client'), icon: UserIcon },
    { id: 'par_type', name: p('business_analysis'), icon: FolderOpenIcon },
    { id: 'par_operation', name: p('by_operation'), icon: ClipboardList }
  ];
  
  // Tabs definition for document filters
  const documentFilterTabs = [
    { id: 'all', name: p('all_documents'), icon: FileText },
    { id: 'invoice', name: p('invoices'), icon: FileText },
    { id: 'quote', name: p('quotes'), icon: FileText },
    { id: 'delivery', name: p('delivery_notes'), icon: FileText },
    { id: 'paid', name: p('status_paid'), icon: CheckCircleIcon },
    { id: 'pending', name: p('status_pending'), icon: ClockIcon },
    { id: 'overdue', name: p('status_overdue'), icon: ExclamationTriangleIcon }
  ];

  // Close dropdowns when clicking outside (status badge portals to document.body)
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (activeStatusMenu && 
          !event.target.closest('.status-container') && 
          !event.target.closest('.status-dropdown')) {
        setActiveStatusMenu(null);
        setStatusDropdownPosition(null);
      }
    };
    
    document.addEventListener('mousedown', handleOutsideClick);
    
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [activeStatusMenu]);

  useEffect(() => {
    if (activeStatusMenu && !scopedDocuments.some((d) => d.id === activeStatusMenu)) {
      setActiveStatusMenu(null);
      setStatusDropdownPosition(null);
    }
  }, [activeStatusMenu, scopedDocuments]);
  
  // Handle viewing a document when redirected from dashboard
  useEffect(() => {
    if (location.state?.viewDocument) {
      const docId = location.state.viewDocument;
      const documentToView = documents.find(doc => doc.id === docId);
      
      if (documentToView) {
        // Open document preview modal
        handleView(documentToView);
        
        // Reset location state to prevent reopening on refresh
        window.history.replaceState({}, document.title);
      }
    }
    
    // Handle toast notification from document creation
    if (location.state?.toast) {
      const toastData = location.state.toast;
      setCurrentPage(1);
      setToast({
        visible: true,
        message: toastData.message,
        status: toastData.status || 'success',
        documentId: toastData.documentId,
        documentNumber: toastData.documentNumber,
        documentType: toastData.documentType,
        showActions: true // Flag to show quick actions
      });
      
      // Clear toast after 5 seconds
      setTimeout(() => {
        setToast(prev => ({ ...prev, visible: false }));
      }, 5000);
      
      // Clear location state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, documents]);

  // Close import dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importRef.current && !importRef.current.contains(event.target)) {
        setIsImportOpen(false);
      }
    };

    if (isImportOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isImportOpen]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportRef.current && !exportRef.current.contains(event.target)) {
        setIsExportOpen(false);
      }
    };

    if (isExportOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isExportOpen]);

  /** Même carte que FacturiersPage « Détails Facturier » — en clair: coquille blanche + ombre douce */
  const FACTURIER_DETAIL_MODAL_SHELL = isDashboardLightMode
    ? 'backdrop-blur-lg border rounded-2xl qawaem-modal-light'
    : 'bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl';
  const FACTURIER_DETAIL_MODAL_SHADOW = isDashboardLightMode
    ? '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)'
    : '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)';

  /** Coquille pixel-aligned avec le toast après sauvegarde document (carte Succès principale). */
  const ARCHIVES_TOAST_SAVE_MATCH_SHELL = {
    wrapper:
      'bg-gradient-to-br from-slate-800/98 via-blue-950/35 to-slate-900/98 border-blue-400/55',
    shadow: '0 0 42px rgba(59,130,246,0.42), 0 0 80px rgba(37,99,235,0.14)',
    iconBox: 'bg-blue-500/22 border-blue-400/45 text-blue-50',
    iconBoxStyle: undefined,
    Icon: CheckCircleIcon,
  };

  const archivesToastShell = (() => {
    if (toast.status === 'error') {
      return {
        variant: 'error',
        wrapper:
          'bg-gradient-to-br from-slate-900/98 via-slate-950/95 to-red-950/30 border-red-400/55',
        shadow: '0 0 42px rgba(248,113,113,0.38), 0 0 80px rgba(239,68,68,0.16)',
        iconBox: 'bg-red-500/20 border-red-400/45 text-red-100',
        iconBoxStyle: undefined,
        Icon: ExclamationTriangleIcon,
      };
    }

    if (toast.showActions) {
      return {
        variant: 'cta',
        ...ARCHIVES_TOAST_SAVE_MATCH_SHELL,
      };
    }

    if (toast.status === 'deleted') {
      return {
        variant: 'deleted',
        wrapper:
          'bg-gradient-to-br from-slate-900/98 via-slate-950/95 to-rose-950/25 border-rose-400/45',
        shadow: '0 0 40px rgba(251,113,133,0.28), 0 0 72px rgba(244,63,94,0.12)',
        iconBox: 'bg-rose-500/15 border-rose-400/35 text-rose-50',
        iconBoxStyle: undefined,
        Icon: TrashIcon,
      };
    }

    if (toast.status === 'pending') {
      return {
        variant: 'progress',
        wrapper:
          'bg-gradient-to-br from-slate-800/98 via-blue-950/35 to-slate-900/98 border-amber-400/38',
        shadow: '0 0 38px rgba(59,130,246,0.42), 0 0 72px rgba(59,130,246,0.18)',
        iconBox: 'bg-amber-500/18 border-amber-400/40 text-amber-50',
        iconBoxStyle: undefined,
        Icon: ClockIcon,
      };
    }

    if (
      toast.status === 'completed' ||
      toast.status === 'success' ||
      toast.status === 'paid'
    ) {
      return {
        variant: 'success',
        ...ARCHIVES_TOAST_SAVE_MATCH_SHELL,
      };
    }

    return {
      variant: 'default',
      wrapper: `${FACTURIER_DETAIL_MODAL_SHELL}`,
      shadow: FACTURIER_DETAIL_MODAL_SHADOW,
      iconBox: 'bg-blue-500/20 border-blue-400/45 text-blue-50',
      iconBoxStyle: undefined,
      Icon: CheckCircleIcon,
    };
  })();

  const ArchivesToastIcon = archivesToastShell.Icon;

  const archivesToolbarProps = {
    t,
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    isImportOpen,
    setIsImportOpen,
    importRef,
    isExportOpen,
    setIsExportOpen,
    exportRef,
    handleImportCSV,
    handleImportExcel,
    handleImportJSON,
    handleImportXML,
    handleImportTXT,
    handleExportPDF,
    handleExportExcel,
    handleExportCSV,
    handleExportJSON,
    handleExportXML,
    handleExportHTML,
    handleExportTXT,
    isGeneratingPDF,
    isGeneratingExcel,
    selectedBulkCount: selectedDocuments.length,
    onBulkPrintArchives: handleBulkPrint,
    onBulkDeleteArchives: handleBulkDelete,
  };

  return (
    <div
      className={`space-y-6 archives-container employees-container`}
      data-qawaem-skip-toolbar-hoist="true"
    >
      {/* Toasts Archives — même carte « Succès » (+ PDF / Créer un autre si showActions) */}
      <AnimatePresence>
        {toast.visible && (
          <motion.div
            key="archives-toast"
            role="status"
            aria-live="polite"
            className={`fixed z-[100] overflow-hidden text-white left-4 right-4 top-24 mx-auto w-full max-w-lg rounded-2xl border-2 backdrop-blur-lg ${archivesToastShell.wrapper}`}
            style={{
              boxShadow: archivesToastShell.shadow,
            }}
            initial={{ opacity: 0, scale: 0.86, y: -28 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.93, y: -20 }}
            transition={{
              type: 'spring',
              stiffness: 540,
              damping: 20,
              mass: 0.58,
            }}
          >
            {(archivesToastShell.variant === 'success' ||
              archivesToastShell.variant === 'cta') && (
              <div
                className="pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-2xl"
                aria-hidden
              >
                <div className="absolute -bottom-12 -right-8 h-36 w-36 rounded-full bg-amber-400/18 blur-3xl" />
              </div>
            )}
            <div className="relative z-[1] flex items-start gap-4 p-5">
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border-2 border-solid ${
                  archivesToastShell.iconBoxStyle ? '' : archivesToastShell.iconBox
                }`}
                style={archivesToastShell.iconBoxStyle}
              >
                <ArchivesToastIcon className="h-5 w-5" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <>
                  <p
                    className={`text-[11px] font-semibold uppercase tracking-wider ${
                      archivesToastShell.variant === 'error'
                        ? 'text-red-200/80'
                        : archivesToastShell.variant === 'deleted'
                          ? 'text-rose-200/80'
                          : archivesToastShell.variant === 'progress'
                            ? 'text-amber-200/80'
                            : archivesToastShell.variant === 'cta' ||
                                archivesToastShell.variant === 'success'
                              ? 'text-blue-200/92'
                              : 'text-white/50'
                    }`}
                  >
                    {archivesToastShell.variant === 'error'
                      ? p('toast_error')
                      : archivesToastShell.variant === 'progress'
                        ? p('toast_patience')
                        : archivesToastShell.variant === 'cta' ||
                            archivesToastShell.variant === 'success'
                          ? p('toast_success')
                          : archivesToastShell.variant === 'deleted'
                            ? p('toast_archives')
                            : p('toast_notification')}
                  </p>
                  <p
                    className="mt-1.5 text-[15px] font-semibold leading-snug text-white"
                    dangerouslySetInnerHTML={{ __html: toast.message }}
                  />
                </>
                {toast.showActions && toast.documentId && (
                  <div className={NOTIFICATION_TOAST_ACTIONS_FOOTER_CLASS}>
                    <button
                      type="button"
                      onClick={async () => {
                        const doc = documents.find((d) => d.id === toast.documentId);
                        if (doc) {
                          await handleDownloadPDF(doc);
                        }
                        setToast((prev) => ({ ...prev, visible: false }));
                      }}
                      className={NOTIFICATION_TOAST_ACTION_BUTTON_CLASS}
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      {p('toast_download_pdf')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setToast((prev) => ({ ...prev, visible: false }));
                        navigate(documentCreationBasePath);
                      }}
                      className={NOTIFICATION_TOAST_ACTION_BUTTON_CLASS}
                    >
                      <FileText className="h-4 w-4" />
                      {p('toast_create_another')}
                    </button>
                  </div>
                )}
              </div>
              <button
                type="button"
                aria-label={p('dismiss_toast')}
                onClick={() => setToast((prev) => ({ ...prev, visible: false }))}
                className="-m-1 shrink-0 rounded-lg p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden Document Preview for PDF generation */}
      {documentToPrint && (
        <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
          <TemplateWrapper 
            documentType={documentToPrint.type}
            formData={documentToPrint.document}
            clientData={documentToPrint.client}
            isRealTimePreview={true}
            onSave={() => {}} // Dummy function
            onBack={() => {}} // Dummy function
          />
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4 list-toolbar-mobile-layout">
        <div className="flex-1 min-w-0">
          {isSectionArchives ? (
            <>
              <QawaemPageTitleWithFilters
                titleSuffix={
                  isSaudiBrand() ? (
                    <QawaemTableCardsViewToggle value={viewMode} onChange={setViewMode} />
                  ) : null
                }
                filters={
                  <ArchivesToolbarFiltersRow
                    {...archivesToolbarProps}
                    variant="titleRow"
                    hideViewToggle={isSaudiBrand()}
                    searchPlaceholder={p('search_document')}
                  />
                }
              >
                <h1 className="text-3xl font-bold text-forest-900 flex items-center gap-3">
                  <FileText className="page-title-icon page-title-icon--bronze h-8 w-8" />
                  {t(sectionConfig.titleKey)}
                </h1>
              </QawaemPageTitleWithFilters>
              <p className="page-section-desc">{t(sectionConfig.descriptionKey)}</p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <FileText className="h-8 w-8 text-blue-400" />
                {isInventaireArchives
                  ? p('inventaire_title')
                  : isLogistiqueArchives
                    ? p('logistique_title')
                    : p('title')}
              </h1>
              <p className="text-white/60 mt-2">
                {isInventaireArchives
                  ? p('inventaire_description')
                  : isLogistiqueArchives
                    ? p('logistique_description')
                    : p('description')}
              </p>
            </>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-3 archives-section-header-actions">
          {isSectionArchives ? (
            <>
              {sectionConfig.createActions.map((action) => (
                <button
                  key={`${action.type}-${action.party || 'default'}`}
                  type="button"
                  onClick={() => navigate(buildSectionCreateDocumentUrl(sectionConfig, action))}
                  className="archives-section-create-btn flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition-all duration-300 hover:from-blue-600 hover:to-blue-800"
                  title={t(action.labelKey)}
                >
                  <FilePlus className="h-4 w-4 shrink-0" />
                  <span>{t(action.labelKey)}</span>
                </button>
              ))}
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className={`archives-advanced-options-btn ${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
              >
                <FilterIcon className="h-5 w-5" />
                <span className="hidden sm:inline">{p('advanced_options')}</span>
                {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => navigate(documentCreationBasePath)}
                className="flex items-center space-x-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 px-4 py-3 font-semibold text-white shadow-lg shadow-blue-500/30 transition-all duration-300 hover:from-blue-600 hover:to-blue-800"
                title={t('sidebar.create_document')}
              >
                <FilePlus className="h-5 w-5 shrink-0" />
                <span className="hidden sm:inline">
                  {t('sidebar.create_document')}
                </span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Documents Total */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="relative group"
        >
          {/* Glow effect */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm"></div>
          
          <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{p('documents_total')}</p>
                <p className="text-2xl font-bold text-white force-white mt-2">{stats.total}</p>
              </div>
              <div className="relative">
                {/* Icon glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg"></div>
                <div className="relative p-3 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600">
                  <FolderOpenIcon className="h-6 w-6 text-white force-white" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Factures */}
        {!isSectionArchives && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative group"
        >
          {/* Glow effect */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm"></div>
          
          <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{p('invoices')}</p>
                <p className="text-2xl font-bold text-white force-white mt-2">{stats.invoices}</p>
              </div>
              <div className="relative">
                {/* Icon glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg"></div>
                <div className="relative p-3 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600">
                  <FileText className="h-6 w-6 text-white force-white" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        )}

        {/* Devis */}
        {!isSectionArchives && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="relative group"
        >
          {/* Glow effect */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500 to-amber-600 rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm"></div>
          
          <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{p('quotes')}</p>
                <p className="text-2xl font-bold text-white force-white mt-2">{stats.quotes}</p>
              </div>
              <div className="relative">
                {/* Icon glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-amber-600 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg"></div>
                <div className="relative p-3 rounded-2xl bg-gradient-to-r from-amber-500 to-amber-600">
                  <FileText className="h-6 w-6 text-white force-white" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        )}

        {/* Montant Total */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="relative group"
        >
          {/* Glow effect */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-green-600 rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm"></div>
          
          <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{p('total_amount')}</p>
                <p className="text-2xl font-bold text-white force-white mt-2">
                  {formatBrandMoney(stats.totalAmount)}
                </p>
              </div>
              <div className="relative">
                {/* Icon glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg"></div>
                <div className="relative p-3 rounded-2xl bg-gradient-to-r from-green-500 to-green-600">
                  <BanknoteIcon className="h-6 w-6 text-white force-white" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Ce Mois-ci — section archives only (keeps KPI row at max 4 elsewhere) */}
        {isSectionArchives && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="relative group"
        >
          {/* Glow effect */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-purple-600 rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm"></div>
          
          <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/60 text-sm font-medium">{p('this_month')}</p>
                <p className="text-2xl font-bold text-white force-white mt-2">{stats.thisMonth}</p>
              </div>
              <div className="relative">
                {/* Icon glow */}
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg"></div>
                <div className="relative p-3 rounded-2xl bg-gradient-to-r from-purple-500 to-purple-600">
                  <CalendarIcon className="h-6 w-6 text-white force-white" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
        )}
      </div>

      {/* Main View Mode Tabs */}
      {!isSectionArchives && (
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setMainViewMode(tab.id);
                setSelectedClient(null);
                setSelectedDocumentType(null);
                setSelectedOperation(null);
                setCurrentPage(1);
              }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                mainViewMode === tab.id
                  ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                  : 'text-white/80 hover:bg-white/10 hover:text-white'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>
      )}
      
      {/* Document Type Tabs (Only show for "all_documents" view) */}
      {!isSectionArchives && mainViewMode === 'all_documents' && showAdvancedOptions && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20"
        >
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {documentFilterTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setCurrentPage(1);
                }}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
              </button>
            ))}
          </div>
        </motion.div>
      )}


      {/* "Par Client" View */}
      {mainViewMode === 'par_client' && !selectedClient && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
          <div className="facturation-list-controls-strip px-6 py-4 border-b border-white/10">
            <ArchivesToolbarFiltersRow {...archivesToolbarProps} searchPlaceholder={t('partners.search_client')} />
          </div>

          {/* Card View */}
          {viewMode === 'cards' && (
          <div className="p-6 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pickerClientsFiltered.map(client => {
              // Calculate client metrics (respect Statut + Période)
              const clientDocs = aggregateFilteredDocs.filter(doc => doc.client === client.name);
              const totalDocs = clientDocs.length;
              const totalAmount = clientDocs.reduce((sum, doc) => {
                const amount = resolveArchiveDocumentAmount(doc);
                return sum + amount;
              }, 0);
              const lastDoc = [...clientDocs].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
              
              return (
                <motion.div
                  key={client.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white/5 rounded-xl p-4 border border-white/10 cursor-pointer hover:bg-white/10 transition-all h-full min-w-0"
                  onClick={() => setSelectedClient(client.name)}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <span className="text-white font-bold">{client.name?.charAt(0) || 'C'}</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-white">{client.name}</h4>
                      <p className="text-sm text-white/60">
                        {client.type === 'company' ? p('company') : p('individual')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-white/60">{p('documents')}</p>
                      <p className="text-xl font-semibold text-white">{totalDocs}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-white/60">{p('total_amount')}</p>
                      <p className="text-xl font-semibold text-white">{formatBrandMoney(totalAmount)}</p>
                    </div>
                  </div>
                  
                  {lastDoc && (
                    <div className="mt-4 p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-white/60">{p('last_document')}</p>
                      <p className="text-sm font-medium text-white">{lastDoc.number} - {formatUserDate(lastDoc.date) || '\u2014'}</p>
                    </div>
                  )}
                </motion.div>
              );
            })}
            
            {clients.length === 0 && (
              <div className="col-span-3 text-center py-8">
                <p className="text-white/60">{p('no_clients')}</p>
              </div>
            )}
            {clients.length > 0 && pickerClientsFiltered.length === 0 && (
              <div className="col-span-3 text-center py-8">
                <p className="text-white/60">{p('try_different')}</p>
              </div>
            )}
          </div>
          </div>
          )}
          
          {/* Table View */}
          {viewMode === 'table' && (
            <div className="overflow-x-auto table-horizontal-scroll">
              <table className="w-full employees-table data-table border-collapse">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="px-6 py-4 text-left text-white font-semibold">{t('partners.name')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{t('partners.type')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('documents')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('total_amount')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('last_document')}</th>
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('document_actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 employees-table-body">
                  {pickerClientsFiltered.map(client => {
                    // Calculate client metrics
                    const clientDocs = aggregateFilteredDocs.filter(doc => doc.client === client.name);
                    const totalDocs = clientDocs.length;
                    const totalAmount = clientDocs.reduce((sum, doc) => {
                      const amount = resolveArchiveDocumentAmount(doc);
                      return sum + amount;
                    }, 0);
                    const lastDoc = [...clientDocs].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                    
                    return (
                      <tr 
                        key={client.id}
                        className="hover:bg-white/5 transition-colors cursor-pointer"
                        onClick={() => setSelectedClient(client.name)}
                      >
                        <td className="px-4 py-3 text-white font-medium">{client.name}</td>
                        <td className="px-4 py-3 text-white/80">
                          {client.type === 'company' ? p('company') : p('individual')}
                        </td>
                        <td className="px-4 py-3 text-white font-medium">{totalDocs}</td>
                        <td className="px-4 py-3 text-white font-medium">{formatBrandMoney(totalAmount)}</td>
                        <td className="px-4 py-3 text-white/80">
                          {lastDoc ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs px-2 py-1 bg-white/10 rounded">
                                {translateDocumentType(lastDoc.type)}
                              </span>
                              <span>{lastDoc.number}</span>
                            </div>
                          ) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedClient(client.name);
                              }}
                              type="button"
                              className={`${archivesFacturiersLikeActionBtn.view} action-button-tooltip`}
                              data-tooltip={t('common.view_details')}
                              aria-label={t('common.view_details')}
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  
                  {clients.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-white/60">
                        {p('no_clients')}
                      </td>
                    </tr>
                  )}
                  {clients.length > 0 && pickerClientsFiltered.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-white/60">
                        {p('try_different')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* "Par Operation" View */}
      {mainViewMode === 'par_operation' && !selectedOperation && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
          <div className="facturation-list-controls-strip px-6 py-4 border-b border-white/10">
            <ArchivesToolbarFiltersRow {...archivesToolbarProps} searchPlaceholder={p('search_document')} />
          </div>

          {/* Card View */}
          {viewMode === 'cards' && (
          <div className="p-6 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Find document flows - first find documents with operationId */}
            {(() => {
              // Group documents by their operation ID
              const operationGroups = {};
              
              // Find documents with operationId and group them
              aggregateFilteredDocs.forEach(doc => {
                if (doc.operationId) {
                  if (!operationGroups[doc.operationId]) {
                    operationGroups[doc.operationId] = {
                      id: doc.operationId,
                      name: doc.operationName || doc.client,
                      client: doc.operationClient || doc.client,
                      docs: [],
                      firstDate: new Date(doc.date).getTime() || Date.now(),
                      totalAmount: 0,
                      flowTypes: []
                    };
                  }
                  
                  // Add document to group
                  operationGroups[doc.operationId].docs.push(doc);
                  
                  // Update total amount
                  const amount = resolveArchiveDocumentAmount(doc);
                  operationGroups[doc.operationId].totalAmount += amount;
                  
                  // Track document types in the flow
                  if (!operationGroups[doc.operationId].flowTypes.includes(doc.type)) {
                    operationGroups[doc.operationId].flowTypes.push(doc.type);
                  }
                }
              });
              
              // Also look for linked documents based on conversion chains
              aggregateFilteredDocs.forEach(doc => {
                if (doc.sourceDocumentId) {
                  const sourceDoc = documents.find(d => d.id === doc.sourceDocumentId);
                  if (sourceDoc && !sourceDoc.operationId) {
                    // Create a synthetic operation for these linked documents
                    const syntheticOpId = `SYN-${sourceDoc.id}`;
                    
                    if (!operationGroups[syntheticOpId]) {
                      operationGroups[syntheticOpId] = {
                        id: syntheticOpId,
                        name: doc.client,
                        client: doc.client,
                        docs: [],
                        firstDate: new Date(sourceDoc.date).getTime() || Date.now(),
                        totalAmount: 0,
                        flowTypes: [],
                        synthetic: true
                      };
                    }
                    
                    // Add both source and derived document
                    if (!operationGroups[syntheticOpId].docs.some(d => d.id === sourceDoc.id)) {
                      operationGroups[syntheticOpId].docs.push(sourceDoc);
                      const srcAmount = resolveArchiveDocumentAmount(sourceDoc);
                      operationGroups[syntheticOpId].totalAmount += srcAmount;
                      
                      if (!operationGroups[syntheticOpId].flowTypes.includes(sourceDoc.type)) {
                        operationGroups[syntheticOpId].flowTypes.push(sourceDoc.type);
                      }
                    }
                    
                    if (!operationGroups[syntheticOpId].docs.some(d => d.id === doc.id)) {
                      operationGroups[syntheticOpId].docs.push(doc);
                      const docAmount = resolveArchiveDocumentAmount(doc);
                      operationGroups[syntheticOpId].totalAmount += docAmount;
                      
                      if (!operationGroups[syntheticOpId].flowTypes.includes(doc.type)) {
                        operationGroups[syntheticOpId].flowTypes.push(doc.type);
                      }
                    }
                  }
                }
              });
              
              // For the remaining documents, group by client and date as before
              const ungroupedDocs = aggregateFilteredDocs.filter(doc => 
                !doc.operationId && 
                !operationGroups[`SYN-${doc.id}`] &&
                !Object.values(operationGroups).some(group => 
                  group.synthetic && group.docs.some(d => d.id === doc.id)
                )
              );
              
              // Create monthly operations for ungrouped documents
              const uniqueClients = [...new Set(ungroupedDocs.map(doc => doc.client))];
              uniqueClients.forEach(client => {
                const clientDocs = ungroupedDocs.filter(doc => doc.client === client);
                
                // Group by month/year
                const dateGroups = {};
                clientDocs.forEach(doc => {
                  if (!doc.date) return;
                  
                  const date = new Date(doc.date);
                  if (isNaN(date)) return;
                  
                  const monthYear = `${date.getMonth()}-${date.getFullYear()}`;
                  const groupId = `MONTH-${client.replace(/\s+/g, '-')}-${monthYear}`;
                  
                  if (!dateGroups[groupId]) {
                    dateGroups[groupId] = {
                      id: groupId,
                      name: `${client} - ${formatUserDate(date, { month: 'long', year: 'numeric' })}`,
                      client,
                      docs: [],
                      firstDate: date.getTime(),
                      totalAmount: 0,
                      flowTypes: [],
                      monthly: true
                    };
                  }
                  
                  // Add document to group
                  dateGroups[groupId].docs.push(doc);
                  
                  // Update amount
                  const amount = resolveArchiveDocumentAmount(doc);
                  dateGroups[groupId].totalAmount += amount;
                  
                  // Track document types
                  if (!dateGroups[groupId].flowTypes.includes(doc.type)) {
                    dateGroups[groupId].flowTypes.push(doc.type);
                  }
                });
                
                // Add monthly groups to operations
                Object.values(dateGroups).forEach(group => {
                  operationGroups[group.id] = group;
                });
              });
              
              // Convert to array and sort by date
              let opsPickerList = Object.values(operationGroups);
              opsPickerList.sort((a, b) => b.firstDate - a.firstDate);
              const searchQOp = searchTerm.trim().toLowerCase();
              if (searchQOp) {
                opsPickerList = opsPickerList.filter((operation) => {
                  if (operation.name?.toLowerCase().includes(searchQOp)) return true;
                  if (String(operation.client || '').toLowerCase().includes(searchQOp)) return true;
                  return operation.flowTypes.some((ft) =>
                    translateDocumentType(ft).toLowerCase().includes(searchQOp)
                  );
                });
              }

              return opsPickerList.map((operation) => {
                // Create a title based on the flow type
                let operationTitle;
                if (operation.synthetic || operation.docs.some(d => d.operationId)) {
                  // This is a proper business flow
                  operationTitle = p('business_operation');
                } else {
                  // This is a monthly grouping
                  operationTitle = p('monthly_documents');
                }
                
                // Get document flow path
                const hasFlow = operation.docs.length > 1 && 
                                operation.flowTypes.length > 1 &&
                                (operation.synthetic || operation.docs.some(d => d.operationId));
                
                let flowPath = null;
                if (hasFlow) {
                  // Try to reconstruct the document flow path
                  // Sort documents by operation step if available, otherwise by date
                  const sortedDocs = [...operation.docs].sort((a, b) => {
                    if (a.operationStep && b.operationStep) {
                      return a.operationStep - b.operationStep;
                    }
                    // Fallback to date sorting
                    return new Date(a.date) - new Date(b.date);
                  });
                  
                  flowPath = sortedDocs.map(doc => doc.type);
                }
                
                return (
                  <motion.div
                    key={operation.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`bg-white/5 rounded-xl p-4 border ${hasFlow ? 'border-blue-500/30' : 'border-white/10'} cursor-pointer hover:bg-white/10 transition-all h-full min-w-0`}
                    onClick={() => setSelectedOperation(operation.id)}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        hasFlow 
                          ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                          : 'bg-gradient-to-br from-purple-500 to-purple-600'
                      }`}>
                        {hasFlow ? <ClipboardList className="h-5 w-5 text-white" /> : <CalendarIcon className="h-5 w-5 text-white" />}
                      </div>
                      <div>
                        <h4 className="font-medium text-white">{operationTitle}</h4>
                        <p className="text-sm text-white/60">
                          {operation.name}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div className="p-3 rounded-lg bg-white/5">
                        <p className="text-xs text-white/60">{p('documents')}</p>
                        <p className="text-xl font-semibold text-white">{operation.docs.length}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-white/5">
                        <p className="text-xs text-white/60">{p('total_amount')}</p>
                        <p className="text-xl font-semibold text-white">{formatBrandMoney(operation.totalAmount)}</p>
                      </div>
                    </div>
                    
                    {/* Document flow visualization */}
                    {hasFlow && flowPath && (
                      <div className="mt-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                        <p className="text-xs text-white/70 mb-2">{p('document_flow')}</p>
                        <div className="flex items-center overflow-x-auto">
                          {flowPath.map((type, index) => (
                            <React.Fragment key={index}>
                              <div className="px-2 py-1 text-xs rounded bg-white/10 text-white whitespace-nowrap">
                                {translateDocumentType(type)}
                              </div>
                              {index < flowPath.length - 1 && (
                                <div className="mx-1 text-white/40">{p('document_flow_separator')}</div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {!hasFlow && (
                      <div className="mt-4">
                        <div className="flex flex-wrap gap-1">
                          {operation.flowTypes.slice(0, 3).map(type => (
                            <div 
                              key={type} 
                              className="px-2 py-0.5 text-xs rounded-full bg-white/10 text-white/80"
                            >
                              {translateDocumentType(type)}
                            </div>
                          ))}
                          {operation.flowTypes.length > 3 && (
                            <div className="px-2 py-0.5 text-xs rounded-full bg-white/10 text-white/80">
                              +{operation.flowTypes.length - 3}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              });
            })()}
          </div>
          </div>
          )}
          {/* Table View */}
          {viewMode === 'table' && (
            <div className="overflow-x-auto table-horizontal-scroll">
              <table className="w-full employees-table data-table border-collapse">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('operation_name')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('client')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('documents')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('total_amount')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('document_types')}</th>
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('document_actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 employees-table-body">
                  {/* Reuse the operation generation logic from cards view */}
                  {(() => {
                    // Group documents by their operation ID
                    const operationGroups = {};
                    
                    // Find documents with operationId and group them
                    aggregateFilteredDocs.forEach(doc => {
                      if (doc.operationId) {
                        if (!operationGroups[doc.operationId]) {
                          operationGroups[doc.operationId] = {
                            id: doc.operationId,
                            name: doc.operationName || doc.client,
                            client: doc.operationClient || doc.client,
                            docs: [],
                            firstDate: new Date(doc.date).getTime() || Date.now(),
                            totalAmount: 0,
                            flowTypes: []
                          };
                        }
                        
                        // Add document to group
                        operationGroups[doc.operationId].docs.push(doc);
                        
                        // Update total amount
                        const amount = resolveArchiveDocumentAmount(doc);
                        operationGroups[doc.operationId].totalAmount += amount;
                        
                        // Track document types in the flow
                        if (!operationGroups[doc.operationId].flowTypes.includes(doc.type)) {
                          operationGroups[doc.operationId].flowTypes.push(doc.type);
                        }
                      }
                    });
                    
                    // Also look for linked documents based on conversion chains
                    aggregateFilteredDocs.forEach(doc => {
                      if (doc.sourceDocumentId) {
                        const sourceDoc = documents.find(d => d.id === doc.sourceDocumentId);
                        if (sourceDoc && !sourceDoc.operationId) {
                          // Create a synthetic operation for these linked documents
                          const syntheticOpId = `SYN-${sourceDoc.id}`;
                          
                          if (!operationGroups[syntheticOpId]) {
                            operationGroups[syntheticOpId] = {
                              id: syntheticOpId,
                              name: doc.client,
                              client: doc.client,
                              docs: [],
                              firstDate: new Date(sourceDoc.date).getTime() || Date.now(),
                              totalAmount: 0,
                              flowTypes: [],
                              synthetic: true
                            };
                          }
                          
                          // Add both source and derived document
                          if (!operationGroups[syntheticOpId].docs.some(d => d.id === sourceDoc.id)) {
                            operationGroups[syntheticOpId].docs.push(sourceDoc);
                            const srcAmount = resolveArchiveDocumentAmount(sourceDoc);
                            operationGroups[syntheticOpId].totalAmount += srcAmount;
                            
                            if (!operationGroups[syntheticOpId].flowTypes.includes(sourceDoc.type)) {
                              operationGroups[syntheticOpId].flowTypes.push(sourceDoc.type);
                            }
                          }
                          
                          if (!operationGroups[syntheticOpId].docs.some(d => d.id === doc.id)) {
                            operationGroups[syntheticOpId].docs.push(doc);
                            const docAmount = resolveArchiveDocumentAmount(doc);
                            operationGroups[syntheticOpId].totalAmount += docAmount;
                            
                            if (!operationGroups[syntheticOpId].flowTypes.includes(doc.type)) {
                              operationGroups[syntheticOpId].flowTypes.push(doc.type);
                            }
                          }
                        }
                      }
                    });
                    
                    // For remaining documents, group by client and date
                    const ungroupedDocs = aggregateFilteredDocs.filter(doc => 
                      !doc.operationId && 
                      !operationGroups[`SYN-${doc.id}`] &&
                      !Object.values(operationGroups).some(group => 
                        group.synthetic && group.docs.some(d => d.id === doc.id)
                      )
                    );
                    
                    // Create monthly operations for ungrouped documents
                    const uniqueClients = [...new Set(ungroupedDocs.map(doc => doc.client))];
                    uniqueClients.forEach(client => {
                      const clientDocs = ungroupedDocs.filter(doc => doc.client === client);
                      
                      // Group by month/year
                      const dateGroups = {};
                      clientDocs.forEach(doc => {
                        if (!doc.date) return;
                        
                        const date = new Date(doc.date);
                        if (isNaN(date)) return;
                        
                        const monthYear = `${date.getMonth()}-${date.getFullYear()}`;
                        const groupId = `MONTH-${client.replace(/\s+/g, '-')}-${monthYear}`;
                        
                        if (!dateGroups[groupId]) {
                          dateGroups[groupId] = {
                            id: groupId,
                            name: `${client} - ${formatUserDate(date, { month: 'long', year: 'numeric' })}`,
                            client,
                            docs: [],
                            firstDate: date.getTime(),
                            totalAmount: 0,
                            flowTypes: [],
                            monthly: true
                          };
                        }
                        
                        // Add document to group
                        dateGroups[groupId].docs.push(doc);
                        
                        // Update amount
                        const amount = resolveArchiveDocumentAmount(doc);
                        dateGroups[groupId].totalAmount += amount;
                        
                        // Track document types
                        if (!dateGroups[groupId].flowTypes.includes(doc.type)) {
                          dateGroups[groupId].flowTypes.push(doc.type);
                        }
                      });
                      
                      // Add monthly groups to operations
                      Object.values(dateGroups).forEach(group => {
                        operationGroups[group.id] = group;
                      });
                    });
                    
                    // Convert to array and sort by date
                    let opsPickerList = Object.values(operationGroups);
                    opsPickerList.sort((a, b) => b.firstDate - a.firstDate);
                    const searchQOp = searchTerm.trim().toLowerCase();
                    if (searchQOp) {
                      opsPickerList = opsPickerList.filter((operation) => {
                        if (operation.name?.toLowerCase().includes(searchQOp)) return true;
                        if (String(operation.client || '').toLowerCase().includes(searchQOp)) return true;
                        return operation.flowTypes.some((ft) =>
                          translateDocumentType(ft).toLowerCase().includes(searchQOp)
                        );
                      });
                    }

                    return opsPickerList.map((operation) => {
                      // Create a title based on the flow type
                      let operationTitle;
                      if (operation.synthetic || operation.docs.some(d => d.operationId)) {
                        // This is a proper business flow
                        operationTitle = p('business_operation');
                      } else {
                        // This is a monthly grouping
                        operationTitle = p('monthly_documents');
                      }
                      
                      // Check for document flow
                      const hasFlow = operation.docs.length > 1 && 
                                      operation.flowTypes.length > 1 &&
                                      (operation.synthetic || operation.docs.some(d => d.operationId));
                      
                      return (
                        <tr 
                          key={operation.id}
                          className={`hover:bg-white/5 transition-colors cursor-pointer ${hasFlow ? 'bg-blue-500/5' : ''}`}
                          onClick={() => setSelectedOperation(operation.id)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                hasFlow 
                                  ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                                  : 'bg-gradient-to-br from-purple-500 to-purple-600'
                              }`}>
                                {hasFlow ? <ClipboardList className="h-4 w-4 text-white" /> : <CalendarIcon className="h-4 w-4 text-white" />}
                              </div>
                              <div>
                                <p className="font-medium text-white">{operation.name}</p>
                                <p className="text-xs text-white/60">{operationTitle}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-white/80">{operation.client}</td>
                          <td className="px-4 py-3 text-white font-medium">{operation.docs.length}</td>
                          <td className="px-4 py-3 text-white font-medium">{formatBrandMoney(operation.totalAmount)}</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {operation.flowTypes.slice(0, 3).map(type => (
                                <span key={type} className="px-2 py-0.5 text-xs rounded-full bg-white/10 text-white/80">
                                  {translateDocumentType(type)}
                                </span>
                              ))}
                              {operation.flowTypes.length > 3 && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-white/10 text-white/80">
                                  +{operation.flowTypes.length - 3}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedOperation(operation.id);
                                }}
                                type="button"
                                className={`${archivesFacturiersLikeActionBtn.view} action-button-tooltip`}
                                data-tooltip={t('common.view_details')}
                                aria-label={t('common.view_details')}
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* "Par Type de Document" View */}
      {mainViewMode === 'par_type' && !selectedDocumentType && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
          <div className="facturation-list-controls-strip px-6 py-4 border-b border-white/10">
            <ArchivesToolbarFiltersRow {...archivesToolbarProps} searchPlaceholder={p('search_document')} />
          </div>

          {/* Card View */}
          {viewMode === 'cards' && (
          <div className="p-6 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {documentTypePickerRows.map(([key, type]) => {
              const typeDocs = aggregateFilteredDocs.filter(doc => doc.type === key);
              const totalDocs = typeDocs.length;
              const totalAmount = typeDocs.reduce((sum, doc) => {
                const amount = resolveArchiveDocumentAmount(doc);
                return sum + amount;
              }, 0);
              
              return (
                <motion.div
                  key={key}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white/5 rounded-xl p-4 border border-white/10 cursor-pointer hover:bg-white/10 transition-all h-full min-w-0"
                  onClick={() => setSelectedDocumentType(key)}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg" style={{backgroundColor: type.color + '20', color: type.color}}>
                      <FileText className="h-6 w-6" />
                    </div>
                    <h4 className="font-medium text-white">{translateDocumentType(key)}</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-white/60">{p('documents')}</p>
                      <p className="text-xl font-semibold text-white">{totalDocs}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-white/5">
                      <p className="text-xs text-white/60">{p('total_amount')}</p>
                      <p className="text-xl font-semibold text-white">{formatBrandMoney(totalAmount)}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
            {documentTypePickerRows.length === 0 && (
              <div className="col-span-4 text-center py-8 text-white/60">{p('try_different')}</div>
            )}
          </div>
          </div>
          )}
          
          {/* Table View */}
          {viewMode === 'table' && (
            <div className="overflow-x-auto table-horizontal-scroll">
              <table className="w-full employees-table data-table border-collapse">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('document_type')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('documents')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('total_amount')}</th>
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('document_actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 employees-table-body">
                  {documentTypePickerRows.map(([key, type]) => {
                    const typeDocs = aggregateFilteredDocs.filter(doc => doc.type === key);
                    const totalDocs = typeDocs.length;
                    const totalAmount = typeDocs.reduce((sum, doc) => {
                      const amount = resolveArchiveDocumentAmount(doc);
                      return sum + amount;
                    }, 0);
                    
                    return (
                      <tr 
                        key={key}
                        className="hover:bg-white/5 transition-colors cursor-pointer"
                        onClick={() => setSelectedDocumentType(key)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 rounded" style={{backgroundColor: type.color + '20', color: type.color}}>
                              <FileText className="h-4 w-4" />
                            </div>
                            <div className="font-medium text-white">{translateDocumentType(key)}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-white font-medium">{totalDocs}</td>
                        <td className="px-4 py-3 text-white font-medium">{formatBrandMoney(totalAmount)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedDocumentType(key);
                              }}
                              type="button"
                              className={`${archivesFacturiersLikeActionBtn.view} action-button-tooltip`}
                              data-tooltip={t('common.view_details')}
                              aria-label={t('common.view_details')}
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {documentTypePickerRows.length === 0 && (
                    <tr>
                      <td colSpan={4} className="text-center py-8 text-white/60">{p('try_different')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {/* Documents Table */}
      {(mainViewMode === 'all_documents' || selectedClient || selectedDocumentType || selectedOperation) && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
          {/* Header with Filters + Bulk Actions + Export */}
          {!selectedClient && !selectedDocumentType && !selectedOperation && mainViewMode === 'all_documents' && !isSectionArchives && (
            <div className="facturation-list-controls-strip p-6 border-b border-white/10">
              {/* Filters Row — Vue générale (shared with aggregate tabs) */}
              <ArchivesToolbarFiltersRow {...archivesToolbarProps} searchPlaceholder={p('search_document')} />
            </div>
          )}
          
          {/* Header for selected client view */}
          {mainViewMode === 'par_client' && selectedClient && (
            <div className="p-6 border-b border-white/10 bg-white/5">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => setSelectedClient(null)}
                    className="p-2 bg-white/10 rounded-lg text-white/70 hover:bg-white/15 hover:text-white transition-all"
                    aria-label={p('aria_back')}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-6 w-6 text-blue-400" />
                    <h3 className="text-2xl font-bold text-white">
                      {selectedClient}
                    </h3>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg">
                  <FileText className="h-4 w-4 text-white/60" />
                  <span className="text-white font-semibold">{filteredDocuments.length}</span>
                  <span className="text-white/60">{p('documents')}</span>
                </div>
              </div>
              
              {/* Linked Documents Feature */}
              {filteredDocuments.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">{p('linked_documents')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {Array.from(new Set(filteredDocuments.map(doc => doc.type))).map(docType => {
                      const typeInfo = documentTypes[docType] || documentTypes.default;
                      const IconComponent = typeInfo.icon;
                      return (
                        <div 
                          key={docType}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg bg-white/10 text-white/90 cursor-pointer hover:bg-white/20 transition-all border border-white/20"
                        >
                          {IconComponent && <IconComponent className="h-3.5 w-3.5" style={{ color: typeInfo.color }} />}
                          <span>{translateDocumentType(docType)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Header for selected document type view */}
          {mainViewMode === 'par_type' && selectedDocumentType && (
            <div className="p-6 border-b border-white/10 bg-white/5">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => setSelectedDocumentType(null)}
                    className="p-2 bg-white/10 rounded-lg text-white/70 hover:bg-white/15 hover:text-white transition-all"
                    aria-label={p('aria_back')}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const typeInfo = documentTypes[selectedDocumentType] || documentTypes.default;
                      const IconComponent = typeInfo.icon;
                      return IconComponent && <IconComponent className="h-6 w-6" style={{ color: typeInfo.color }} />;
                    })()}
                    <h3 className="text-2xl font-bold text-white">
                      {translateDocumentType(selectedDocumentType)}
                    </h3>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg">
                  <FileText className="h-4 w-4 text-white/60" />
                  <span className="text-white font-semibold">{filteredDocuments.length}</span>
                  <span className="text-white/60">{p('documents')}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Header for selected operation view */}
          {mainViewMode === 'par_operation' && selectedOperation && (
            <div className="p-6 border-b border-white/10 bg-white/5">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-3">
                  <button 
                    onClick={() => setSelectedOperation(null)}
                    className="p-2 bg-white/10 rounded-lg text-white/70 hover:bg-white/15 hover:text-white transition-all"
                    aria-label={p('aria_back')}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="flex items-center gap-2">
                    <RefreshCw className="h-6 w-6 text-purple-400" />
                    <h3 className="text-2xl font-bold text-white">
                      {p('operation_documents')}
                    </h3>
                  </div>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg">
                  <FileText className="h-4 w-4 text-white/60" />
                  <span className="text-white font-semibold">{filteredDocuments.length}</span>
                  <span className="text-white/60">{p('documents')}</span>
                </div>
              </div>
              
              {/* Document types in this operation */}
              {filteredDocuments.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">{p('linked_documents')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {Array.from(new Set(filteredDocuments.map(doc => doc.type))).map(docType => {
                      const typeInfo = documentTypes[docType] || documentTypes.default;
                      const IconComponent = typeInfo.icon;
                      return (
                        <div 
                          key={docType}
                          className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg bg-white/10 text-white/90 cursor-pointer hover:bg-white/20 transition-all border border-white/20"
                        >
                          {IconComponent && <IconComponent className="h-3.5 w-3.5" style={{ color: typeInfo.color }} />}
                          <span>{translateDocumentType(docType)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {currentDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <FileText className="h-16 w-16 text-white/30 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">{p('no_documents')}</h3>
              <p className="text-white/60 mb-6">{p('try_different')}</p>
            </div>
          ) : (
          <>
            {viewMode === 'table' && (
            <div className="overflow-x-auto table-horizontal-scroll">
              <table className="w-full employees-table data-table">
                <thead className="bg-white/5 border-b border-white/10">
                  <tr>
                    <QawaemTableSelectAllHeader
                      allRowsSelected={allRowsSelected}
                      someRowsSelected={someRowsSelected}
                      onChange={handleSelectAll}
                      selectAllTitle={t('common.select')}
                      deselectAllTitle={t('common.select')}
                    />
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('document_number')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('document_type')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('document_title')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('document_client')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('document_date')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('document_amount')}</th>
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('document_status')}</th>
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('document_actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 employees-table-body">
                  {currentDocuments.map((document) => {
                    const displayTypeKey = resolvePaymentDocumentDisplayType(document);
                    const docType = getArchiveDocTypePresentation(document, documentTypes);
                    const statusMeta = statusConfig[document.status] || {
                      label: document.status || '\u2014',
                      color: '#64748b',
                    };

                    const amountDisplay = formatArchiveListAmount(document, formatBrandMoney);

                    const clientLabel = resolveArchiveListClientLabel(document, findClientData);

                    const rowInteractiveSel =
                      '.status-container, .document-actions, [role="checkbox"], a, [role="menuitem"]';

                    return (
                      <tr
                        key={document.id}
                        onClick={(e) => {
                          if (e.target.closest(rowInteractiveSel)) return;
                          handleView(document);
                        }}
                        className={`group hover:bg-white/[0.07] transition-colors cursor-pointer select-none ${
                          selectedDocuments.includes(document.id) ? 'bg-white/10' : ''
                        }`}
                      >
                        <QawaemTableSelectRowCell
                          checked={selectedDocuments.includes(document.id)}
                          onChange={() => handleSelectDocument(document.id)}
                          dataLabel={t('common.select')}
                        />
                        <td className="px-6 py-4 align-middle" data-label={p('document_number')}><span className="text-white font-semibold">{document.number}</span></td>
                        <td className="px-6 py-4 align-middle" data-label={p('document_type')}>
                          <div className="flex items-center gap-3">
                            <span className="p-2 rounded-xl shrink-0 border border-white/10" style={{backgroundColor: docType.color + '22', color: docType.color}}>
                              {React.createElement(docType.icon, { className: "h-5 w-5" })}
                            </span>
                            <span className="text-white font-medium">{resolveArchiveDocumentTypeLabel(document, translateDocumentType)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-white font-medium align-middle max-w-[220px] truncate" title={getDocumentDisplayTitle(document) || ''} data-label={p('document_title')}>{getDocumentDisplayTitle(document) || '\u2014'}</td>
                        <td className="px-6 py-4 align-middle" data-label={p('document_client')}>
                          <div className="flex items-center gap-2 min-w-0">
                            <UserIcon className="h-4 w-4 text-console-muted shrink-0" />
                            <span className={`truncate ${clientLabel === '\u2014' ? 'text-console-muted italic' : 'text-console'}`}>{clientLabel}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-white/80 align-middle whitespace-nowrap" data-label={p('document_date')}>
                          {document.date ? formatUserDate(document.date) || '\u2014' : '\u2014'}
                        </td>
                        <td className="px-6 py-4 text-white font-semibold align-middle whitespace-nowrap" data-label={p('document_amount')}>{amountDisplay}</td>
                        <td className="px-6 py-4 align-middle" data-label={p('document_status')}>
                          <ArchivesClickableStatusBadge
                            document={document}
                            isMenuOpen={activeStatusMenu === document.id}
                            onToggleMenu={handleToggleStatusMenu}
                            statusConfig={statusConfig}
                            resolveArchiveDocTypeKeyForStatuses={resolveArchiveDocTypeKeyForStatuses}
                            getStatusLabelForDocumentType={getStatusLabelForDocumentType}
                            clickChangeStatusTitle={p('click_change_status')}
                          />
                        </td>
                        <td className="px-6 py-4 align-middle" data-label={p('document_actions')}>
                          <div className="document-actions flex flex-wrap items-center justify-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleView(document)}
                              className={`${archivesFacturiersLikeActionBtn.view} action-button-tooltip`}
                              data-tooltip={p('view_document')}
                              aria-label={p('view_document')}
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  `/document-creation?editId=${document.id}&type=${encodeURIComponent(displayTypeKey)}${documentCreationEditFromQuery}`
                                )
                              }
                              className={`${archivesFacturiersLikeActionBtn.edit} action-button-tooltip`}
                              data-tooltip={p('edit_document')}
                              aria-label={p('edit_document')}
                            >
                              <EditIcon className="h-4 w-4" />
                            </button>

                            <button
                              type="button"
                              onClick={() => handleGeneratePDF(document)}
                              className={`${archivesFacturiersLikeActionBtn.download} action-button-tooltip`}
                              disabled={isGeneratingPDF}
                              data-tooltip={p('download_pdf')}
                              aria-label={p('download_pdf')}
                            >
                              <ArrowDownTrayIcon className="h-4 w-4" />
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeleteDocument(document)}
                              className={`${archivesFacturiersLikeActionBtn.delete} action-button-tooltip`}
                              data-tooltip={t('common.delete')}
                              aria-label={t('common.delete')}
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-white/5 border-t border-white/10">
                    <td colSpan={9} className="px-6 py-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-white/70 text-sm">
                        <span>
                          <span className="text-white font-semibold">{filteredDocuments.length}</span>{' '}
                          {filteredDocuments.length === 1 ? p('document_unit') : p('documents')} ({p('filtered_total')})
                        </span>
                        <span>
                          {p('total_amount')}:{' '}
                          <span className="text-white font-semibold">
                            {formatBrandMoney(filteredTotalAmount)}
                          </span>
                        </span>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            )}

            {viewMode === 'cards' && (
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                {currentDocuments.map((document, index) => {
                  const displayTypeKey = resolvePaymentDocumentDisplayType(document);
                  const docType = getArchiveDocTypePresentation(document, documentTypes);
                  const statusMeta = statusConfig[document.status] || {
                    label: document.status || '\u2014',
                    color: '#64748b',
                  };

                  const amountDisplay = formatArchiveListAmount(document, formatBrandMoney);

                  const clientLabel = resolveArchiveListClientLabel(document, findClientData);

                  return (
                    <motion.div 
                      key={document.id} 
                      initial={{ opacity: 0, y: 20 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      transition={{ delay: index * 0.05 }}
                      className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all cursor-pointer h-full min-w-0"
                      onClick={() => handleView(document)}
                    >
                          <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-4">
                            <div className="flex items-center gap-3">
                              <span className="p-3 rounded-xl shrink-0 border border-white/10" style={{backgroundColor: docType.color + '22', color: docType.color}}>
                                {React.createElement(docType.icon, { className: "h-6 w-6" })}
                              </span>
                              <div>
                                <h3 className="text-xl font-bold text-white">{document.number}</h3>
                                <p className="text-white/60 text-sm">{resolveArchiveDocumentTypeLabel(document, translateDocumentType)}</p>
                              </div>
                            </div>
                            <div onClick={(e) => e.stopPropagation()}>
                              <ArchivesClickableStatusBadge
                                document={document}
                                isMenuOpen={activeStatusMenu === document.id}
                                onToggleMenu={handleToggleStatusMenu}
                                statusConfig={statusConfig}
                                resolveArchiveDocTypeKeyForStatuses={resolveArchiveDocTypeKeyForStatuses}
                                getStatusLabelForDocumentType={getStatusLabelForDocumentType}
                                clickChangeStatusTitle={p('click_change_status')}
                              />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            <div>
                              <h5 className="font-medium text-white mb-2">{p('card_info')}</h5>
                              <div className="space-y-1 text-sm text-white/70">
                                <div><span className="font-medium">{p('card_title_label')}:</span> {getDocumentDisplayTitle(document) || '\u2014'}</div>
                                <div className="flex items-center gap-2">
                                  <UserIcon className="h-3 w-3 text-white/60" />
                                  <span className="font-medium">{p('card_client_label')}:</span> <span className={clientLabel === '\u2014' ? 'italic text-white/45' : ''}>{clientLabel}</span>
                                </div>
                                <div><span className="font-medium">{p('card_date_label')}:</span> {document.date ? formatUserDate(document.date) || '\u2014' : '\u2014'}</div>
                              </div>
                            </div>
                            
                            <div>
                              <h5 className="font-medium text-white mb-2">{p('card_amount')}</h5>
                              <div className="space-y-1">
                                <div className="text-2xl font-bold text-white">{amountDisplay}</div>
                                {document.items && document.items.length > 0 && (
                                  <div className="text-xs text-white/50">
                                    {p('card_items', { count: document.items.length })}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <div>
                              <h5 className="font-medium text-white mb-2">{p('card_actions')}</h5>
                              <div className="flex flex-wrap items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <QawaemErpRowActions
                                  compact
                                  size={14}
                                  onView={() => handleView(document)}
                                  onEdit={() =>
                                    navigate(
                                      `/document-creation?editId=${document.id}&type=${encodeURIComponent(displayTypeKey)}${documentCreationEditFromQuery}`
                                    )
                                  }
                                  onDelete={() => handleDeleteDocument(document)}
                                  viewTitle={p('view_document')}
                                  editTitle={p('edit_document')}
                                  deleteTitle={t('common.delete')}
                                />
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleGeneratePDF(document);
                                  }}
                                  disabled={isGeneratingPDF}
                                  className={`${archivesFacturiersLikeActionBtn.download} action-button-tooltip`}
                                  data-tooltip={p('download_pdf')}
                                  aria-label={p('download_pdf')}
                                >
                                  <ArrowDownTrayIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            <FacturationListPaginationBar
              totalItems={totalDocuments}
              rawPage={currentPage}
              onPageChange={setCurrentPage}
              pageSize={documentsPerPage}
            />
          </>
        )}
      </div>
      )}

      {/* Document Preview Modal */}
      {showPreviewModal && previewDocument && documentToPrint ? (
        <QawaemErpViewModal
          open
          onClose={() => {
            setShowPreviewModal(false);
            setDocumentToPrint(null);
          }}
          title={`${p('preview_title')}: ${getPreviewDocumentTitle(previewDocument)}`}
          icon={FileText}
          maxWidth="1400px"
          testId="archives-preview-modal"
        >
              <div className="bg-white rounded-xl shadow-2xl">
                <TemplateWrapper 
                  documentType={documentToPrint.type}
                  formData={documentToPrint.document}
                  clientData={documentToPrint.client}
                  isRealTimePreview={true}
                  partnerType={
                    isSupplierCreditNoteDocument({
                      ...documentToPrint.document,
                      type: documentToPrint.type,
                    }) ||
                    documentToPrint.document.isSupplier ||
                    documentToPrint.document.is_supplier
                      ? 'supplier'
                      : 'client'
                  }
                  onSave={() => {}}
                  onBack={() => setShowPreviewModal(false)}
                />
              </div>
              
              <div className="flex items-center justify-end gap-3 pt-6 mt-6 border-t border-white/10">
                <button 
                  type="button"
                  onClick={() => {
                    setShowPreviewModal(false);
                    setDocumentToPrint(null);
                  }}
                  className="px-4 py-2.5 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors font-medium"
                >
                  {p('close_preview')}
                </button>
                
                <button
                  type="button"
                  onClick={() => handleGeneratePDF(previewDocument)}
                  disabled={isGeneratingPDF}
                  className="px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-700 text-white rounded-xl hover:from-red-600 hover:to-red-800 transition-all duration-200 font-medium flex items-center gap-2 shadow-lg shadow-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  <span>{p('download_document')}</span>
                </button>
              </div>
        </QawaemErpViewModal>
      ) : null}

      <ArchivesStatusDropdownOverlay
        document={activeStatusDocument}
        position={statusDropdownPosition}
        orderedStatuses={activeStatusOrderedStatuses}
        statusConfig={statusConfig}
        resolveArchiveDocTypeKeyForStatuses={resolveArchiveDocTypeKeyForStatuses}
        getStatusLabelForDocumentType={getStatusLabelForDocumentType}
        onStatusChange={handleStatusChange}
        changeStatusTitle={p('change_status')}
      />
    </div>
  );
};

export default ArchivesFacturation;
